import React, { useState, useEffect, useRef, useMemo } from "react";
import { useUpdateCenter, UpdateChip, UpdateBanner } from "./UpdateCenter.jsx";
import { useAccount, captureReferralFromUrl, PLANS, STATUSES } from "./account/useAccount.js";
import { supabase } from "./lib/supabase.js";
/* All pure money/date maths lives in ./lib/money.js so it can be unit-tested
   without React (see lib/money.test.js). Nothing there imports back from here. */
import {
  autoMinPayment, withAutoMin, normalizeDebt,
  monthName, todayISO, daysBetween, monthKeyOf, shiftMonthKey,
  nextDayOfMonth, nextPaydayDate, uid, FREQ_FACTOR, monthlyIncomeOf, recMonthly,
  STATEMENT_LEAD_DAYS, derivedStatementDay, statementDayOf, closeOnOrBefore,
  isPaymentEntry, balanceBeforeEntry, FREQS,
  STRATEGIES, otherStrategy, byStrategy,
  processInterest, processRecurring, simulate, minOnlyProjection, derivedStartingDebt,
  moneyMap, project, monthCashFlow, upcomingBills, upcomingDebtPayments,
  homePlan, allocate, addMonths,
  essentialMonthly, emergencyTarget, nextStep, budgetTotal, totalOwed,
  whatIf, EMPTY_SCENARIO,
} from "./lib/money.js";
import { parseCsv, detectColumns, detectDateOrder, toExpenses, merchantKey } from "./lib/csv.js";
import { pushSupported, subscribeToPush, unsubscribeFromPush } from "./lib/push.js";

/* ─── Aurio v3 · by Zoldan Labs ────────────────────────────
   Debt-first personal finance. Manual-first, works anywhere.
   New in v3: i18n (EN-UK default, EN-US, PT-BR) + Invest tab
   (retirement/passive-income projection, debt-free boost,
   educational market guides for NZ · AU · US · BR).
──────────────────────────────────────────────────────────────── */

const T = {
  midnight: "#0E1118",
  panel: "#161B26",
  panelHi: "#1D2432",
  line: "#2A3242",
  white: "#F2ECDC",
  dim: "#9AA3B2",
  faint: "#5F6878",
  gold: "#C9A227",
  goldSoft: "rgba(201,162,39,0.14)",
  green: "#5E8C6E",
  greenHi: "#7FB48F",
  greenSoft: "rgba(94,140,110,0.14)",
  garnet: "#B0424E",
  garnetSoft: "rgba(176,66,78,0.14)",
};

const FONT_CSS = `
/* Fonts are self-hosted and loaded once via index.html → /fonts/fonts.css. */
* { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
body { margin: 0; }
input, select, button, textarea { font-family: 'Geist', system-ui, sans-serif; }
input[type=range] { -webkit-appearance: none; appearance: none; height: 4px; border-radius: 2px; background: ${T.line}; outline: none; width: 100%; }
input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 22px; height: 22px; border-radius: 50%; background: ${T.gold}; border: 3px solid ${T.midnight}; box-shadow: 0 0 0 1px ${T.gold}; cursor: pointer; }
::placeholder { color: ${T.faint}; }
@media (prefers-reduced-motion: reduce) { * { transition: none !important; animation: none !important; } }
`;

/* ═══ i18n ═══════════════════════════════════════════════════
   Base language is EN-UK (strings as written). PT and US dicts
   map English text → localised text; missing keys fall back. */

const LANGS = [
  { id: "en-GB", label: "🇬🇧 EN-UK" },
  { id: "en-US", label: "🇺🇸 EN-US" },
  { id: "pt-BR", label: "🇧🇷 PT-BR" },
  { id: "es", label: "🇪🇸 ES" },
];

const US = {
  "Fuel & transport": "Gas & transport",
};

const PT = {
  // nav
  "Home": "Início", "Debts": "Dívidas", "Spend": "Gastos", "Budget": "Orçamento", "Invest": "Investir",
  // categories
  "Groceries": "Mercado", "Eating out": "Restaurantes", "Fuel & transport": "Transporte e combustível",
  "Rent": "Aluguel", "Utilities & phone": "Contas e telefone", "Subscriptions": "Assinaturas",
  "Insurance": "Seguros", "Family overseas": "Família no exterior", "Fitness & supps": "Academia e supl.",
  "Personal": "Pessoal", "Trip fund": "Fundo de viagem", "Other": "Outros",
  // home
  "You are debt-free on": "Você fica livre de dívidas em",
  "Today 🎉": "Hoje 🎉",
  "{n} months sooner": "{n} meses antes", "{n} month sooner": "{n} mês antes",
  "interest saved": "de juros economizados",
  "Extra payment / month": "Pagamento extra / mês",
  "Drag to see your date move": "Arraste e veja sua data mudar",
  "Total debt today": "Dívida total hoje",
  "Paid off": "Quitado",
  "{p}% of your {a} starting debt destroyed": "{p}% da sua dívida inicial de {a} destruída",
  "Now": "Agora",
  "Strategy": "Estratégia", "Switch": "Trocar",
  "Saves {a} vs {s}": "Economiza {a} vs {s}",
  "Same cost as the alternative": "Mesmo custo que a alternativa",
  "avalanche": "avalanche", "snowball": "bola de neve",
  "Spent this month": "Gasto neste mês",
  "of {a} budget · {n} expenses": "de {a} de orçamento · {n} gastos",
  "Left after plan": "Sobra após o plano",
  "income − budget − minimums − extra": "renda − orçamento − mínimos − extra",
  "💡 You have {a} unassigned. Sending it to debt moves your debt-free date to {d}.": "💡 Você tem {a} não alocado. Mandando para a dívida, sua data livre de dívidas passa para {d}.",
  "Send it to debt": "Mandar para a dívida",
  // debts
  "Strategy comparison": "Comparação de estratégias",
  "Avalanche": "Avalanche", "Snowball": "Bola de neve",
  "Highest APR first": "Maior juro primeiro", "Smallest balance first": "Menor saldo primeiro",
  "total interest": "juros totais",
  "ATTACK THIS": "ATAQUE ESTA",
  "min": "mín", "used": "usado",
  "Credit card": "Cartão de crédito", "Personal loan": "Empréstimo pessoal", "Car loan": "Financiamento de carro", "Debt": "Dívida",
  "💡 Rate check: you're paying {apr}%. {tip} At ~{good}% this balance costs about {save}/yr less in interest. Compare on MoneyHub or Canstar — offered rates depend on your credit file.":
    "💡 Verificação de taxa: você paga {apr}%. {tip} A ~{good}%, este saldo custaria cerca de {save}/ano a menos em juros. Compare no MoneyHub ou Canstar — as taxas dependem do seu histórico de crédito.",
  "Low-rate NZ cards sit around 12.9–13.9%, and 0% balance-transfer offers exist.": "Cartões de taxa baixa na NZ ficam em 12,9–13,9%, e existem ofertas de transferência a 0%.",
  "Consolidation loans run ~11–13% for good credit; challenger lenders from ~8%.": "Empréstimos de consolidação ficam em ~11–13% com bom crédito; financeiras a partir de ~8%.",
  "NZ car loans start around 7.25% for decent credit profiles.": "Financiamentos de carro na NZ partem de ~7,25% com bom perfil de crédito.",
  "Log payment": "Registrar pagamento", "Edit": "Editar", "Delete": "Excluir",
  "Delete {n}?": "Excluir {n}?", "Debt deleted": "Dívida excluída",
  "+ Add a debt": "+ Adicionar dívida",
  "Payment to {n}": "Pagamento para {n}",
  "Amount": "Valor", "Full": "Total",
  "{a} payment logged 🔥": "Pagamento de {a} registrado 🔥",
  "Edit debt": "Editar dívida", "New debt": "Nova dívida", "Type": "Tipo",
  "Name": "Nome", "Current balance": "Saldo atual", "APR %": "Juros % a.a.",
  "Minimum payment / month": "Pagamento mínimo / mês", "Credit limit (blank for loans)": "Limite do cartão (vazio p/ empréstimos)",
  "Save debt": "Salvar dívida", "Debt updated": "Dívida atualizada", "Debt added": "Dívida adicionada",
  // spend
  "Log an expense": "Registrar um gasto",
  "Note (optional)": "Observação (opcional)", "Add expense": "Adicionar gasto",
  "This month": "Este mês",
  "No expenses yet this month. Log your first one above.": "Nenhum gasto neste mês ainda. Registre o primeiro acima.",
  "Or import a CSV from your bank →": "Ou importe um CSV do seu banco →",
  "Expense logged": "Gasto registrado",
  "Logged — ⚠️ {c} is over budget": "Registrado — ⚠️ {c} estourou o orçamento",
  // budget
  "Income (after tax)": "Renda (líquida)",
  "weekly": "semanal", "fortnightly": "quinzenal", "monthly": "mensal",
  "quarterly": "trimestral", "annual": "anual", "once": "uma vez", "one-off": "avulso",
  // tight-month forecast warning (v0.37)
  "⚠️ {m} looks short by {a}.": "⚠️ {m} parece ficar {a} curto.",
  "{names} land that month.": "{names} caem nesse mês.",
  " and ": " e ",
  "Setting aside {a}/month between now and then covers it.": "Separar {a}/mês daqui até lá resolve.",
  "Plan for it": "Planejar isso",
  "short by": "faltam",
  "short": "em falta",
  "Big bills ahead": "Contas grandes à frente",
  /* what-if scenarios (Grow) */
  "Plan updated.": "Plano atualizado.",
  /* Grow sub-tabs */
  "Next up": "A seguir", "Savings": "Poupança", "Deposit": "Entrada", "Retire": "Aposentadoria",
  "Grow sections": "Seções de crescimento",
  "Saving for a property deposit?": "Juntando a entrada de um imóvel?",
  "Set up my deposit plan": "Criar meu plano de entrada",
  "Go to your buffer": "Ir para sua reserva",
  "Where your money goes": "Para onde vai seu dinheiro",
  "See the projection": "Ver a projeção",
  "Your buffer is solid and there's no high-interest debt in the way — \"Where your spare money goes\" is safe to follow in order.": "Sua reserva está sólida e não há dívida cara no caminho — pode seguir \"Para onde vai seu dinheiro sobrando\" na ordem.",
  "Buffer, debt, and goals are all covered. From here, every extra dollar compounds.": "Reserva, dívidas e metas estão cobertas. Daqui em diante, cada valor extra rende juros compostos.",
  "Early bird: first 100 get 3 months half price": "Early bird: os primeiros 100 pagam metade por 3 meses",
  "Plan ahead for big bills, test what-if scenarios, sync across devices, unlimited history, CSV export and bill reminders. Cancel anytime.": "Planeje contas grandes, teste cenários \"e se\", sincronize entre dispositivos, histórico ilimitado, exportação CSV e lembretes de contas. Cancele quando quiser.",
  /* what-if scenarios (Grow) */
  "What if…": "E se…",
  "Reset": "Limpar",
  "Try a change and see what it does to your debt-free date. Nothing is saved until you apply it.": "Teste uma mudança e veja o efeito na sua data de quitação. Nada é salvo até você aplicar.",
  "Extra at debt": "Extra na dívida",
  "on top of {a}/mo": "além de {a}/mês",
  "One-off lump sum": "Valor único",
  "a refund or bonus, paid today": "uma restituição ou bônus, pago hoje",
  "Income change": "Mudança na renda",
  "a raise, or lost hours": "um aumento, ou horas perdidas",
  "Spending change": "Mudança nos gastos",
  "use a minus to cut back": "use um menos para cortar",
  "Move a lever to see what changes.": "Mexa em um controle para ver o que muda.",
  "Debt-free": "Livre de dívidas",
  "{n} months later": "{n} meses depois",
  "{n} month later": "{n} mês depois",
  "Same date": "Mesma data",
  "{a} less interest": "{a} a menos de juros",
  "{a} more interest": "{a} a mais de juros",
  "✓ Your cash flow covers this every month for the next year.": "✓ Seu fluxo de caixa cobre isso todos os meses no próximo ano.",
  "⚠️ Short in {m} of the next {n} months (up from {b}), by as much as {a}.": "⚠️ No vermelho em {m} dos próximos {n} meses (antes eram {b}), em até {a}.",
  "⚠️ Short in {m} of the next {n} months, by as much as {a} — the same as your plan today.": "⚠️ No vermelho em {m} dos próximos {n} meses, em até {a} — igual ao seu plano de hoje.",
  "Make this the plan": "Tornar este o plano",

  "/mo": "/mês",
  "Set aside": "Separar",
  "Put that much away each month and the bill is already paid for when it lands.": "Guarde esse valor todo mês e a conta já estará paga quando chegar.",
  "≈ {a} per month — all plan maths use this equivalent.": "≈ {a} por mês — todos os cálculos do plano usam este equivalente.",
  "Income (monthly)": "Renda (mensal)", "Living budget": "Orçamento de vida",
  "Debt minimums": "Mínimos das dívidas", "Extra to debt": "Extra para dívidas", "Unassigned": "Não alocado",
  "⚠️ Your plan spends {a} more than you earn. Trim a category or lower the extra payment.": "⚠️ Seu plano gasta {a} a mais do que você ganha. Reduza uma categoria ou o pagamento extra.",
  "Category budgets": "Orçamentos por categoria",
  "Viewing per {c}. Budgets are stored monthly — the input still edits the monthly amount.": "Visualizando por {c}. Orçamentos são mensais — o campo edita o valor mensal.",
  "week": "semana", "fortnight": "quinzena",
  "No recurring items yet. Add the bills that live here (e.g. {ex}) and they'll log themselves.": "Nenhum item recorrente ainda. Adicione as contas desta categoria (ex.: {ex}) e elas se registram sozinhas.",
  "power, gas, water, phone": "luz, gás, água, telefone",
  "health, car, income protection": "saúde, carro, proteção de renda",
  "Netflix, Spotify, iCloud": "Netflix, Spotify, iCloud",
  "monthly transfer to family": "transferência mensal para a família",
  "regular payments": "pagamentos regulares",
  "next": "próx.",
  "Recurring commits {r}/mo of this {b} budget": "Recorrentes comprometem {r}/mês deste orçamento de {b}",
  " — budget is below your fixed bills": " — o orçamento está abaixo das contas fixas",
  "+ Add recurring item": "+ Adicionar item recorrente",
  "Recurring": "Recorrente",
  "e.g. Netflix, Power, Transfer to parents": "ex.: Netflix, Luz, Transferência para os pais",
  "Repeats": "Repete", "Next due date": "Próximo vencimento",
  "Save recurring expense": "Salvar gasto recorrente",
  "Recurring expense added — it logs itself from its next due date": "Gasto recorrente adicionado — ele se registra a partir do próximo vencimento",
  "{n} recurring expenses auto-logged": "{n} gastos recorrentes registrados automaticamente",
  "{n} recurring expense auto-logged": "{n} gasto recorrente registrado automaticamente",
  // invest
  "Projected passive income at {age}": "Renda passiva projetada aos {age}",
  "per month": "por mês",
  "Goal: {a}/mo": "Meta: {a}/mês",
  "surplus": "acima da meta", "short — see boosters below": "abaixo da meta — veja os impulsos abaixo",
  "Your retirement inputs": "Seus dados de aposentadoria",
  "Age now": "Idade atual", "Retire at": "Aposentar aos",
  "Target passive income / month": "Renda passiva desejada / mês",
  "Already invested": "Já investido", "Investing / month now": "Investindo / mês hoje",
  "Assumptions (editable — the future is not predictable)": "Premissas (editáveis — o futuro não é previsível)",
  "Expected return {r}% /yr": "Retorno esperado {r}% /ano",
  "Long-run diversified share markets have averaged ~7–10% nominal before fees and tax; past performance does not predict the future.": "Mercados de ações diversificados renderam em média ~7–10% nominal no longo prazo, antes de taxas e impostos; rentabilidade passada não garante o futuro.",
  "Withdrawal / dividend rate {r}%": "Taxa de retirada / dividendos {r}%",
  "The classic '4% rule' sizes the pot you need; dividend portfolios often yield 3–6%.": "A clássica 'regra dos 4%' dimensiona o patrimônio necessário; carteiras de dividendos costumam render 3–6%.",
  "You need a pot of about": "Você precisa de um patrimônio de cerca de",
  "to draw {a}/mo at {r}%": "para retirar {a}/mês a {r}%",
  "The debt-free boost": "O impulso pós-dívidas",
  "From {d}, your {a}/mo of debt payments becomes investing power. That single move changes your projection from {p1} to {p2} at age {age}.": "A partir de {d}, seus {a}/mês de pagamentos de dívida viram poder de investimento. Só essa mudança leva sua projeção de {p1} para {p2} aos {age} anos.",
  "Current plan": "Plano atual", "With debt-free boost": "Com o impulso pós-dívidas",
  "Goal reached at age {age} 🎉": "Meta atingida aos {age} anos 🎉",
  "Goal not reached by {age} — raise contributions, retire later, or adjust the target.": "Meta não atingida até os {age} — aumente aportes, adie a aposentadoria ou ajuste a meta.",
  "Where people invest for income": "Onde as pessoas investem para renda",
  "Educational only — not financial advice, and not a recommendation of any product. Returns are not guaranteed and past performance does not predict future results. Consider licensed advice for personal decisions.": "Conteúdo educativo — não é aconselhamento financeiro nem recomendação de produto. Retornos não são garantidos e rentabilidade passada não garante resultados futuros. Considere orientação licenciada para decisões pessoais.",
  "Loading your money…": "Carregando suas finanças…",
  "Language": "Idioma",
  // onboarding
  "Welcome to Aurio": "Bem-vindo ao Aurio",
  "Answer a few quick questions and your Home, Debts, Budget and Invest tabs will be built around your real numbers. It takes about two minutes.": "Responda algumas perguntas rápidas e as abas Início, Dívidas, Orçamento e Investir serão montadas com seus números reais. Leva uns dois minutos.",
  "Set up my plan": "Montar meu plano",
  "Skip for now — use example data": "Pular por enquanto — usar dados de exemplo",
  "You can redo any of this later — every number stays editable inside the app.": "Você pode refazer tudo depois — cada número continua editável dentro do app.",
  "Step {n} of {m}": "Etapa {n} de {m}",
  "Back": "Voltar", "Next": "Avançar",
  "What do you take home?": "Quanto você recebe líquido?",
  "Your pay after tax. Pick how often you're paid — we'll do the monthly maths.": "Sua renda após impostos. Escolha a frequência do pagamento — nós fazemos a conta mensal.",
  "What do you owe?": "O que você deve?",
  "Add each card or loan — estimates are fine. This powers your payoff plan.": "Adicione cada cartão ou empréstimo — estimativas servem. Isso alimenta seu plano de quitação.",
  "Add this debt": "Adicionar esta dívida",
  "No debts? Lucky you — just tap Next.": "Sem dívidas? Sorte sua — é só tocar em Avançar.",
  "What does a month cost you?": "Quanto custa o seu mês?",
  "Rough numbers are perfect. You can fine-tune every category later in Budget.": "Números aproximados são perfeitos. Você ajusta cada categoria depois no Orçamento.",
  "Where are you headed?": "Para onde você está indo?",
  "Used by the Invest tab to project your passive income.": "Usado pela aba Investir para projetar sua renda passiva.",
  "Here's your starting plan": "Aqui está seu plano inicial",
  "Open my plan": "Abrir meu plano",
  "month": "mês",
  "How often do you think about these costs?": "Com que frequência você pensa nesses custos?",
  "Amounts below are per {c} — we'll convert everything to monthly for your plan.": "Os valores abaixo são por {c} — vamos converter tudo para mensal no seu plano.",
  "APR is the yearly interest rate you're charged on this debt — you'll find it on your card or loan statement.": "APR são os juros anuais cobrados nesta dívida (taxa % ao ano) — está na fatura do cartão ou no contrato do empréstimo.",
  // account / My Info
  "My Info": "Meu Perfil",
  "Signed in as": "Conectado como",
  "Email": "E-mail",
  "Subscription plan": "Plano de assinatura",
  "Status": "Status",
  "Trial ends": "Teste termina em",
  "Full name": "Nome completo",
  "Save": "Salvar", "Saved": "Salvo",
  "Sign in": "Entrar", "Sign out": "Sair",
  "Sign in to see your plan and keep your profile with Zoldan Labs. Your money data stays on this device.": "Entre para ver seu plano e manter seu perfil na Zoldan Labs. Seus dados financeiros ficam neste dispositivo.",
  "We'll email you a sign-in code — no password needed.": "Enviaremos um código de acesso por e-mail — sem senha.",
  "Send code": "Enviar código",
  "Code sent — check your email.": "Código enviado — confira seu e-mail.",
  "No code in the email? Tapping its sign-in link works too — this app signs in automatically.": "Sem código no e-mail? Tocar no link de acesso também funciona — o app entra automaticamente.",
  "Enter the code from your email": "Digite o código do seu e-mail",
  "Verify & sign in": "Verificar e entrar",
  "Couldn't send the code. Check the email and try again.": "Não foi possível enviar o código. Verifique o e-mail e tente de novo.",
  "That code didn't work. Try again.": "O código não funcionou. Tente de novo.",
  "Signed in ✓": "Conectado ✓",
  "Signed out": "Desconectado",
  "Account features aren't set up in this build.": "Os recursos de conta não estão configurados nesta versão.",
  "Your trial has ended — contact Zoldan Labs about full access.": "Seu período de teste terminou — fale com a Zoldan Labs sobre o acesso completo.",
  "Free": "Gratuito", "Trial": "Teste", "Tester": "Testador", "Paid": "Pago", "Lifetime": "Vitalício",
  "Active": "Ativo", "Expired": "Expirado", "Cancelled": "Cancelado",
  // upcoming bills & reminders
  "Upcoming payments": "Próximos pagamentos",
  "Payday": "Dia do pagamento",
  "Nothing due in the next 30 days.": "Nada vence nos próximos 30 dias.",
  "today": "hoje", "tomorrow": "amanhã", "in {d} days": "em {d} dias",
  "💰 Set aside {a} for the {n} payments due before your payday on {d}.": "💰 Separe {a} para os {n} pagamentos que vencem antes do seu pagamento em {d}.",
  "🔔 Remind me when the app opens up to {n} days before each payment": "🔔 Me avise ao abrir o app até {n} dias antes de cada pagamento",
  "Bill reminders on 🔔": "Lembretes de contas ativados 🔔",
  "Reminders on 🔔 — they'll show when you open Aurio. Sign in to get them with the app closed.": "Lembretes ativados 🔔 — eles aparecem quando você abre o Aurio. Entre na sua conta para recebê-los com o app fechado.",
  "Bill reminders off": "Lembretes de contas desativados",
  "Notifications aren't supported in this browser.": "Este navegador não suporta notificações.",
  "Notifications are blocked — allow them in your browser settings.": "Notificações bloqueadas — permita nas configurações do navegador.",
  "{n} is due today — {a}": "{n} vence hoje — {a}",
  "{n} is due in {d} days — {a}": "{n} vence em {d} dias — {a}",
  "{c} payments due in the next {d} days — {a} total": "{c} pagamentos vencem nos próximos {d} dias — {a} no total",
  "minimum": "mínimo",
  "paid this cycle": "pago neste ciclo",
  "Minimum already paid this cycle": "Mínimo já pago neste ciclo",
  "Log this on the Debts tab": "Registre na aba Dívidas",
  // spend history
  "Previous month": "Mês anterior", "Next month": "Próximo mês",
  "Last 6 months": "Últimos 6 meses",
  "Where it went": "Para onde foi",
  // chart clarity pass (v0.25)
  "out this month (spend + debt payments)": "saíram este mês (gastos + pagamentos de dívidas)",
  // card charges in the budget (v0.36)
  "{a} charge logged to {c}": "Cobrança de {a} registrada em {c}",
  "What was it for?": "Para que foi?",
  "Not a budget expense": "Não é um gasto do orçamento",
  "Only the card balance moves — nothing reaches your budget. Right for a balance transfer, cash advance or fee.": "Só o saldo do cartão muda — nada entra no seu orçamento. Ideal para transferência de saldo, saque ou anuidade.",
  "This lands in your budget straight away. It counts as money out later, when you pay the card.": "Isso entra no seu orçamento na hora. Conta como dinheiro que saiu depois, quando você pagar o cartão.",
  "left your account this month (cash spend + debt payments)": "saíram da sua conta este mês (gastos em dinheiro + pagamentos de dívidas)",
  "{a} of this month's spending went on a card — that counts here when you pay the card, not when you swipe.": "{a} dos gastos deste mês foram no cartão — isso conta aqui quando você pagar a fatura, não quando você passa o cartão.",
  "Charged to {n} — undo it from that debt's activity.": "Cobrado em {n} — desfaça pela atividade dessa dívida.",
  "your card": "seu cartão",
  "card": "cartão",
  "<1%": "<1%",
  "{p}% of your income": "{p}% da sua renda",
  "You've spent more than came in this month.": "Você gastou mais do que entrou este mês.",
  "{a} out of {b} income — {p}% used.": "{a} de {b} de renda — {p}% usado.",
  "{n} smaller category": "{n} categoria menor",
  "{n} smaller categories": "{n} categorias menores",
  "show all": "ver todas",
  "show less": "ver menos",
  "Spending by category: {list}": "Gastos por categoria: {list}",
  "Monthly spend for the last 6 months: {list}": "Gastos mensais dos últimos 6 meses: {list}",
  "Debt falling from {a} to zero by {d}.": "Dívida caindo de {a} até zero em {d}.",
  "Debt balance over time — it never reaches zero on this plan.": "Saldo da dívida ao longo do tempo — neste plano ele nunca chega a zero.",
  "{p}% of your {a} limit used": "{p}% do seu limite de {a} usado",
  "{c}: {s} spent of {b} budget": "{c}: {s} gastos de {b} do orçamento",
  "{c}: {s} spent, no budget set": "{c}: {s} gastos, sem orçamento definido",
  "Next 12 months, saveable versus committed. About {a} saveable per month on average.": "Próximos 12 meses, poupável versus comprometido. Cerca de {a} poupáveis por mês, em média.",
  "Projected balance from age {a} to {b}: {x} with the debt-free boost, {y} on the current plan.": "Saldo projetado dos {a} aos {b} anos: {x} com o impulso pós-dívidas, {y} no plano atual.",
  "over budget by {a}": "estourou o orçamento em {a}",
  "No expenses recorded in {m}.": "Nenhum gasto registrado em {m}.",
  // backup & cloud
  "Restored from your cloud backup ☁️": "Restaurado do seu backup na nuvem ☁️",
  "Checking your cloud backup…": "Verificando seu backup na nuvem…",
  "Couldn't reach your cloud backup": "Não foi possível acessar seu backup na nuvem",
  "You're signed in, so there may be data saved to your account. Aurio won't start a fresh setup until it can check — that's what stops a new setup from replacing your real data.": "Você está conectado, então pode haver dados salvos na sua conta. O Aurio não vai iniciar uma nova configuração até conseguir verificar — é isso que impede que uma configuração nova substitua seus dados reais.",
  "Try again": "Tentar de novo",
  "☁️ Cloud backup is on — your data saves to your account automatically and follows you to any device you sign in on.": "☁️ Backup na nuvem ativo — seus dados são salvos na sua conta automaticamente e acompanham você em qualquer dispositivo em que entrar.",
  "Backup & restore": "Backup e restauração",
  "Download a backup file of everything in the app — you can restore it here on any device, any time.": "Baixe um arquivo de backup com tudo do app — você pode restaurá-lo aqui em qualquer dispositivo, a qualquer momento.",
  "Download backup": "Baixar backup",
  "Restore backup": "Restaurar backup",
  "Backup downloaded": "Backup baixado",
  "Replace the data on this device with this backup?": "Substituir os dados deste dispositivo por este backup?",
  "Backup restored ✓": "Backup restaurado ✓",
  "That file doesn't look like a Aurio backup.": "Esse arquivo não parece um backup do Aurio.",
  // csv export
  "expense": "gasto", "debt payment": "pagamento de dívida",
  "Export CSV": "Exportar CSV",
  "Export all as CSV": "Exportar tudo em CSV",
  "CSV exported": "CSV exportado",
  // reset / danger zone
  "Danger zone": "Zona de perigo",
  "Erase all data": "Apagar todos os dados",
  "⚠️ This permanently erases all debts, expenses, budgets and settings from this device and your cloud backup. This cannot be undone.": "⚠️ Isso apaga permanentemente todas as dívidas, gastos, orçamentos e configurações deste dispositivo e do seu backup na nuvem. Não é possível desfazer.",
  "Download backup, then erase": "Baixar backup e depois apagar",
  "Erase without backup": "Apagar sem backup",
  "Cancel": "Cancelar",
  "Privacy": "Privacidade",
  // milestones & coach hint
  "You're debt-free.": "Você está livre de dívidas.",
  "Every debt, gone. This is what you built.": "Cada dívida, quitada. Foi você que construiu isso.",
  "A debt, gone.": "Uma dívida, quitada.",
  "{n} is fully paid off.": "{n} está totalmente quitada.",
  "The home stretch.": "A reta final.",
  "75% destroyed — the end is in sight.": "75% destruído — o fim está à vista.",
  "Halfway there.": "Você chegou à metade.",
  "Half of your starting debt is gone.": "Metade da sua dívida inicial já foi.",
  "A quarter down.": "Um quarto do caminho.",
  "You've destroyed 25% of your starting debt.": "Você destruiu 25% da sua dívida inicial.",
  "Keep going": "Continuar",
  "👇 Try it — drag to watch your debt-free date jump.": "👇 Experimente — arraste e veja sua data livre de dívidas saltar.",
  // sectioned csv export
  "Aurio financial export": "Exportação financeira Aurio",
  "all data": "todos os dados",
  "Income": "Renda", "Budgets": "Orçamentos", "Recurring bills": "Contas recorrentes",
  "Transactions": "Transações", "Summary": "Resumo",
  "frequency": "frequência", "amount": "valor", "monthly equivalent": "equivalente mensal",
  "balance": "saldo", "min payment / month": "pagamento mínimo / mês", "credit limit": "limite de crédito",
  "due day": "dia de vencimento", "statement day": "dia de fechamento", "interest posted through": "juros lançados até",
  "payoff at minimum only": "quitação só com o mínimo", "interest at minimum only": "juros só com o mínimo",
  "category": "categoria", "monthly budget": "orçamento mensal", "spent this month": "gasto neste mês",
  "next due": "próximo vencimento", "date": "data", "description": "descrição",
  "total debt": "dívida total", "monthly income": "renda mensal", "total monthly budget": "orçamento mensal total",
  "debt-free date (current plan)": "data livre de dívidas (plano atual)",
  "projected interest (current plan)": "juros projetados (plano atual)",
  "projected interest (minimums only)": "juros projetados (só mínimos)",
  "never at this minimum": "nunca com este mínimo",
  "Couldn't export — try again.": "Não foi possível exportar — tente de novo.",
  // debt true cost
  "{n} year": "{n} ano", "{n} years": "{n} anos", "{n} month": "{n} mês", "{n} months": "{n} meses",
  "At minimum only: paid off in {t} · {i} interest · {tot} total repaid": "Só com o mínimo: quitada em {t} · {i} de juros · {tot} pagos no total",
  "⚠️ The minimum never pays this off — the balance grows every month.": "⚠️ O mínimo nunca quita esta dívida — o saldo cresce todo mês.",
  "⚠️ At minimums only this debt never ends — a minimum doesn't even cover its interest. See Debts.": "⚠️ Só com os mínimos esta dívida nunca acaba — um mínimo nem cobre os juros. Veja Dívidas.",
  "⚠️ Your current payments never clear this debt — raise the minimums or the extra payment.": "⚠️ Seus pagamentos atuais nunca quitam esta dívida — aumente os mínimos ou o pagamento extra.",
  "True cost on your current plan: {a} ({i} interest)": "Custo real no seu plano atual: {a} ({i} de juros)",
  "At minimums only it would be {a} — your extra payment saves {s}.": "Só com os mínimos seria {a} — seu pagamento extra economiza {s}.",
  // recurring edit
  "Recurring expense updated": "Gasto recorrente atualizado",
  "Save changes": "Salvar alterações",
  // reserved budget segment
  "{a} reserved for upcoming bills": "{a} reservado para contas a vencer",
  // currency
  "Currency": "Moeda",
  "Terms": "Termos",
  // premium / stripe
  "Welcome to Premium 🎉": "Bem-vindo ao Premium 🎉",
  "Your trial has ended. Upgrade to keep Premium.": "Seu teste terminou. Assine para manter o Premium.",
  // CSV import (v0.31)
  "Import from CSV": "Importar de CSV",
  "Import CSV": "Importar CSV",
  "Export a CSV from your bank or spreadsheet and pick it here. It's read on your device — nothing is uploaded, and no bank login is ever needed.": "Exporte um CSV do seu banco ou planilha e selecione aqui. Ele é lido no seu aparelho — nada é enviado, e nunca é preciso login bancário.",
  "Choose a CSV file": "Escolher um arquivo CSV",
  "Reading…": "Lendo…",
  "That file doesn't look like a CSV Aurio can read.": "Esse arquivo não parece um CSV que o Aurio consiga ler.",
  "Couldn't find a date and an amount column in that file. Check it's a transaction export, not a summary.": "Não encontrei uma coluna de data e uma de valor nesse arquivo. Confira se é um extrato de transações, não um resumo.",
  "{n} to import": "{n} para importar",
  "{n} already in Aurio": "{n} já estão no Aurio",
  "{n} money-in rows skipped": "{n} linhas de entrada ignoradas",
  "{n} couldn't be read": "{n} não puderam ser lidas",
  "Reading dates as {order}.": "Lendo as datas como {order}.",
  "day/month (3/4 = 3 April)": "dia/mês (3/4 = 3 de abril)",
  "month/day (3/4 = 4 March)": "mês/dia (3/4 = 4 de março)",
  "First row: {d}": "Primeira linha: {d}",
  "Not right — swap day and month": "Não está certo — trocar dia e mês",
  "Everything in this file is already in Aurio — nothing to add.": "Tudo neste arquivo já está no Aurio — nada a adicionar.",
  "No spending rows found in this file.": "Nenhuma linha de gasto encontrada neste arquivo.",
  "(no description)": "(sem descrição)",
  "Category": "Categoria",
  "Showing the first {n}. All {total} will be imported.": "Mostrando os primeiros {n}. Todos os {total} serão importados.",
  "Import {n} expenses": "Importar {n} gastos",
  "You can undo this straight afterwards if it doesn't look right.": "Você pode desfazer logo em seguida se não parecer certo.",
  "{n} expenses imported": "{n} gastos importados",
  "Imported {n} expenses": "{n} gastos importados",
  "Remove the {n} expenses from that import?": "Remover os {n} gastos dessa importação?",
  "Import undone": "Importação desfeita",
  "Keep": "Manter",
  // emergency fund + next-dollar guidance (v0.30)
  "🛟 Emergency fund": "🛟 Fundo de emergência",
  "Emergency fund": "Fundo de emergência",
  "3 months of essentials — rent, groceries, utilities, transport, insurance, your recurring bills, and every debt minimum. Sized from your own numbers.": "3 meses de despesas essenciais — aluguel, mercado, contas, transporte, seguro, suas contas recorrentes e todos os pagamentos mínimos de dívidas. Calculado com seus próprios números.",
  "Saved so far": "Guardado até agora",
  "🌱 Starter buffer": "🌱 Reserva inicial",
  "High-interest debt": "Dívida de juros altos",
  "🛟 Full emergency fund": "🛟 Fundo de emergência completo",
  "🎯 Goals & deposit": "🎯 Metas e entrada",
  "Build a starter buffer first": "Monte uma reserva inicial primeiro",
  "{a} covers one month of essentials — rent, groceries, utilities, transport, insurance, your bills, and every debt minimum. A small buffer first means the next surprise bill doesn't have to become a new debt.": "{a} cobre um mês de despesas essenciais — aluguel, mercado, contas, transporte, seguro e todos os pagamentos mínimos de dívidas. Uma pequena reserva primeiro significa que a próxima conta inesperada não precisa virar uma dívida nova.",
  "Clear {n} before investing": "Quite {n} antes de investir",
  "It costs {apr}%/yr. Your investing assumption is {r}%/yr — paying it off is a guaranteed {apr}% return, and you won't reliably beat that in the market.": "Custa {apr}% ao ano. Sua expectativa de investimento é {r}% ao ano — quitar essa dívida é um retorno garantido de {apr}%, e dificilmente você vai superar isso no mercado com consistência.",
  "Go to Debts": "Ir para Dívidas",
  "Grow your emergency fund": "Aumente seu fundo de emergência",
  "Your starter buffer is in place. The fully-funded target is {a} — three months of essentials.": "Sua reserva inicial está pronta. A meta completa é {a} — três meses de despesas essenciais.",
  "Fund your goals and deposit": "Financie suas metas e a entrada",
  "Your buffer is solid and there's no high-interest debt in the way — \"Where your spare money goes\" below is safe to follow in order.": "Sua reserva está sólida e não há dívida de juros altos no caminho — \"Para onde vai seu dinheiro que sobra\" abaixo pode ser seguido na ordem.",
  "Put spare money into investing": "Coloque o dinheiro que sobra para investir",
  "Buffer, debt, and goals are all covered. From here, every extra dollar compounds — see the projection below.": "Reserva, dívidas e metas estão cobertas. A partir daqui, cada real extra rende juros compostos — veja a projeção abaixo.",
  "Do this next": "Faça isso a seguir",
  "Then:": "Depois:",
  "Suggested order based on your own numbers — not financial advice.": "Ordem sugerida com base nos seus próprios números — não é consultoria financeira.",
  // allocation waterfall + inflation (v0.29)
  "Where your spare money goes": "Para onde vai seu dinheiro que sobra",
  "One at a time, in this order — each starts when the one above it is funded. Reorder to change what gets your money first.": "Um de cada vez, nesta ordem — cada um começa quando o de cima é concluído. Reordene para mudar o que recebe seu dinheiro primeiro.",
  "🏠 House deposit": "🏠 Entrada da casa",
  "📈 Investing": "📈 Investindo",
  "gets what's left each month": "recebe o que sobra a cada mês",
  "nothing spare yet": "ainda não sobra nada",
  "from {d}": "a partir de {d}",
  "no date yet": "ainda sem data",
  "funded": "concluído",
  "ready {d}": "pronto em {d}",
  "getting your money now": "recebendo seu dinheiro agora",
  "Move up": "Mover para cima",
  "Move down": "Mover para baixo",
  "✓ Funded": "✓ Concluído",
  "No date yet — nothing spare each month": "Ainda sem data — nada sobra por mês",
  "⚠️ On this plan you get there {d}, {n} after your target. Move it up the list, or lower the target.": "⚠️ Neste plano você chega lá em {d}, {n} depois da sua meta. Suba na lista ou reduza o valor.",
  "On track for {d}": "No caminho para {d}",
  "Starts {d}, once {n} ahead of it are funded — reorder in \"Where your spare money goes\" to change that.": "Começa em {d}, quando os {n} à frente estiverem concluídos — reordene em \"Para onde vai seu dinheiro que sobra\" para mudar isso.",
  "now": "agora",
  "Saving {a}/mo · {n} of funding": "Guardando {a}/mês · {n} de aportes",
  "≈ {a}/mo in today's money, at {r}% inflation": "≈ {a}/mês em valores de hoje, com inflação de {r}%",
  "Inflation {r}% /yr": "Inflação {r}% ao ano",
  "Used to show what your future income is worth in today's money. Set it to 0 to see the raw numbers instead.": "Usado para mostrar quanto sua renda futura vale em dinheiro de hoje. Coloque 0 para ver os números brutos.",
  "🎉 You're debt-free — {a}/mo no longer goes to debt.": "🎉 Você está livre de dívidas — {a}/mês não vai mais para dívidas.",
  "Redirecting {pct}% is {b}/mo into investing.": "Redirecionar {pct}% são {b}/mês para investimentos.",
  "Other markets ({n})": "Outros mercados ({n})",
  "Hide other markets": "Ocultar outros mercados",
  // first-home planner (v0.28)
  "Saving for a first home?": "Está economizando para a primeira casa?",
  "Track your deposit alongside your debt payoff and see how the two dates compare — a debt clearing after your deposit is ready is usually what actually decides your move-in date.": "Acompanhe sua entrada junto com a quitação das dívidas e veja como as duas datas se comparam — uma dívida que quita depois da entrada pronta costuma ser o que realmente decide quando você se muda.",
  "Set up my first-home plan": "Configurar meu plano da primeira casa",
  "New Zealand": "Nova Zelândia",
  "Australia": "Austrália",
  "KiwiSaver (usable)": "KiwiSaver (utilizável)",
  "Super (usable, FHSS)": "Super (utilizável, FHSS)",
  "Other savings": "Outras economias",
  "Deposit ready": "Entrada pronta",
  "Ready now 🎉": "Pronto agora 🎉",
  "No date yet — nothing saveable each month": "Ainda sem data — nada poupável por mês",
  "The \"You can save\" amount above is $0 — free up money in your budget or income to project a date here.": "O valor \"Você pode poupar\" acima está em $0 — libere dinheiro no seu orçamento ou renda para projetar uma data aqui.",
  "{a} of {b} saved toward your deposit": "{a} de {b} guardados para a entrada",
  "{a} of {b} saved toward your deposit — {p}%": "{a} de {b} guardados para a entrada — {p}%",
  "{a} of {b}": "{a} de {b}",
  "⚠️ Your debt clears {d} — after your deposit is ready. Lenders weigh your debts when sizing a loan, so on this plan the debt date is the one moving your move-in date.": "⚠️ Sua dívida quita em {d} — depois que a entrada estiver pronta. Os bancos consideram suas dívidas ao calcular um empréstimo, então neste plano a data da dívida é a que decide quando você se muda.",
  "Your deposit is ready {d}, before you're debt-free {b} — clearing the debt first is likely what decides when a lender says yes.": "Sua entrada fica pronta em {d}, antes de você quitar as dívidas em {b} — quitar as dívidas primeiro é provavelmente o que decide quando um banco vai aprovar.",
  "First-home schemes only — leave this at 0 for an investment property.": "Apenas para programas de primeira casa — deixe em 0 para um imóvel de investimento.",
  "Target price": "Preço alvo",
  "Deposit needed": "Entrada necessária",
  "Cash saved": "Dinheiro guardado",
  "Still to find": "Ainda falta",
  "Saving {a}/mo · {n}": "Guardando {a}/mês · {n}",
  "See {c} first-home help": "Ver ajuda da primeira casa para {c}",
  "Hide this section": "Ocultar esta seção",
  "Your KiwiSaver balance {a} → {b} usable toward the deposit.": "Seu saldo do KiwiSaver {a} → {b} utilizável para a entrada.",
  "Your balance {a} → {b} usable under the $50,000 lifetime cap.": "Seu saldo {a} → {b} utilizável dentro do limite vitalício de $50.000.",
  "At a 5% deposit your target is {a} — you already have enough.": "Com uma entrada de 5% seu alvo é {a} — você já tem o suficiente.",
  "At a 5% deposit your target is {a} — {b} still to find.": "Com uma entrada de 5% seu alvo é {a} — ainda falta {b}.",
  "{c} first-home help": "Ajuda da primeira casa para {c}",
  "General information, not financial advice, and not a recommendation of any product. Scheme rules change — verify current eligibility and figures with the relevant government agency or lender before relying on them. Checked {v}.": "Informação geral, não é consultoria financeira nem recomendação de produto. As regras dos programas mudam — confirme a elegibilidade e os valores atuais com o órgão do governo ou banco responsável antes de se basear neles. Verificado em {v}.",
  // referral loop (v0.27)
  "Give a month, get a month": "Dê um mês, ganhe um mês",
  "Your friend gets a free month of Premium straight away. You get one too, once they're still using Aurio a week later.": "Seu amigo ganha um mês grátis de Premium na hora. Você ganha o seu quando ele ainda estiver usando o Aurio uma semana depois.",
  "Copy link": "Copiar link",
  "Invite a friend": "Convidar um amigo",
  "Invite link copied": "Link de convite copiado",
  "Couldn't copy — long-press the code instead.": "Não foi possível copiar — mantenha o código pressionado.",
  "I'm using Aurio to clear my debt — here's a free month of Premium: {link}": "Estou usando o Aurio para quitar minhas dívidas — aqui está um mês grátis de Premium: {link}",
  "{n} free month earned": "{n} mês grátis conquistado",
  "{n} free months earned": "{n} meses grátis conquistados",
  "{n} friend still to qualify": "{n} amigo ainda para qualificar",
  "{n} friends still to qualify": "{n} amigos ainda para qualificar",
  "Includes {n} days of referral credit.": "Inclui {n} dias de crédito por indicação.",
  "A free month of Premium is yours 🎉": "Um mês grátis de Premium é seu 🎉",
  "A friend stuck with Aurio — a free month is yours 🎉": "Um amigo continuou no Aurio — um mês grátis é seu 🎉",
  "{n} friends stuck with Aurio — {n} free months are yours 🎉": "{n} amigos continuaram no Aurio — {n} meses grátis são seus 🎉",
  // self-serve trial (v0.26)
  "Try Premium free for 14 days": "Experimente o Premium grátis por 14 dias",
  "Sync across your devices, unlimited history, CSV export and bill reminders. No card needed — it simply ends on its own.": "Sincronização entre seus dispositivos, histórico ilimitado, exportação CSV e lembretes de contas. Sem cartão — o teste termina sozinho.",
  "Start my free trial": "Começar meu teste grátis",
  "Start my free 14-day trial": "Começar meu teste grátis de 14 dias",
  "Create a free account to start": "Criar uma conta grátis para começar",
  "Starting…": "Começando…",
  "Your 14-day Premium trial is live 🎉": "Seu teste Premium de 14 dias está ativo 🎉",
  "Couldn't start the trial — try again.": "Não foi possível iniciar o teste — tente novamente.",
  "Dismiss": "Dispensar",
  "See plans": "Ver planos",
  "✓ Premium is active — cloud sync, unlimited history, CSV export, reminders and full projections are unlocked.": "✓ Premium ativo — sincronização na nuvem, histórico ilimitado, exportação CSV, lembretes e projeções completas liberados.",
  "Manage subscription": "Gerenciar assinatura",
  "Couldn't open billing. Try again.": "Não foi possível abrir a cobrança. Tente de novo.",
  "Unlock Premium": "Desbloquear o Premium",
  "Cloud sync across devices, unlimited history, CSV export, bill reminders and full retirement projections.": "Sincronização entre dispositivos, histórico ilimitado, exportação CSV, lembretes de contas e projeções completas de aposentadoria.",
  "Cloud sync across devices, unlimited history, CSV export, bill reminders and full retirement projections. Cancel anytime.": "Sincronização entre dispositivos, histórico ilimitado, exportação CSV, lembretes de contas e projeções completas de aposentadoria. Cancele quando quiser.",
  "Couldn't start checkout. Try again.": "Não foi possível iniciar o pagamento. Tente de novo.",
  "Opening secure checkout…": "Abrindo o pagamento seguro…",
  "Secure payment by Stripe. Your card details never touch Aurio.": "Pagamento seguro pela Stripe. Os dados do seu cartão nunca passam pelo Aurio.",
  "Final price is confirmed at checkout for your region.": "O preço final é confirmado no checkout para a sua região.",
  "Yearly": "Anual", "Monthly": "Mensal",
  "Best value — save 40%": "Melhor valor — economize 40%",
  "Pay once, yours forever": "Pague uma vez, seu para sempre",
  "/year": "/ano", "/month": "/mês", " once": " uma vez",
  // payment history (v0.18)
  "Payment undone": "Pagamento desfeito",
  "↓ Paid {a} this month": "↓ {a} pagos este mês",
  "Activity ({n})": "Atividade ({n})",
  "Hide history": "Ocultar histórico",
  "{a} paid": "{a} pagos",
  "{a} charged": "{a} cobrados",
  "{a} interest": "{a} de juros",
  "across {n} entries": "em {n} registros",
  "Payment": "Pagamento",
  "Undo this payment? The balance will be restored.": "Desfazer este pagamento? O saldo será restaurado.",
  "Undo": "Desfazer",
  // log charge + monthly interest (v0.23)
  "+ Log charge": "+ Registrar cobrança",
  "Charge to {n}": "Cobrança em {n}",
  "This adds to the balance — for when the card gets used again.": "Isso soma ao saldo — para quando o cartão for usado de novo.",
  "Log charge": "Registrar cobrança",
  "{a} charge logged": "Cobrança de {a} registrada",
  "Charge": "Cobrança",
  "Interest": "Juros",
  "Interest posted on {n} debts": "Juros lançados em {n} dívidas",
  "Interest posted on {n} debt": "Juros lançados em {n} dívida",
  // realistic invest boost (v0.18)
  "From {d}, {a}/mo frees up from debt payments. Realistically, some of that goes to other bills or lifestyle — not all of it becomes investing power.": "A partir de {d}, {a}/mês fica livre dos pagamentos de dívida. Na prática, parte disso vai para outras contas ou estilo de vida — nem tudo vira poder de investimento.",
  "How much will you realistically redirect to investing?": "Quanto você realisticamente vai redirecionar para investir?",
  "Defaults to 60% — a buffer for the bills that don't disappear just because the debt did.": "Padrão de 60% — uma margem para as contas que não desaparecem só porque a dívida desapareceu.",
  "Redirecting {pct}% ({b}/mo) changes your projection from {p1} to {p2} at age {age}.": "Redirecionar {pct}% ({b}/mês) muda sua projeção de {p1} para {p2} aos {age} anos.",
  // spend cash-flow + donut (v0.18)
  "Cash flow": "Fluxo de caixa",
  "Income": "Renda",
  "Out (spend + debt payments)": "Saídas (gastos + pagamentos de dívida)",
  "Left": "Sobra",
  "Income shown is your current setting — it isn't tracked per past month.": "A renda mostrada é sua configuração atual — não é registrada por mês passado.",
  // custom categories + auto minimum (v0.19)
  "Auto · 3%": "Auto · 3%",
  "Use auto 3%": "Usar auto 3%",
  "Card issuers usually set the minimum at 1–3% of your balance. We default to 3% and keep it updated as your balance falls — edit it to match your statement and it stays fixed.": "As administradoras costumam definir o mínimo entre 1–3% do saldo. Usamos 3% por padrão e mantemos atualizado conforme o saldo cai — edite para bater com sua fatura e ele fica fixo.",
  "Category added": "Categoria adicionada",
  "Category deleted": "Categoria excluída",
  "Delete {n}? Past expenses stay, shown under Other.": "Excluir {n}? Os gastos anteriores continuam, exibidos em Outros.",
  "+ Add category": "+ Adicionar categoria",
  "New category": "Nova categoria",
  "e.g. Pets, Kids school": "ex.: Pets, Escola dos filhos",
  "Deleting a category later won't erase its expenses — they move to Other.": "Excluir uma categoria depois não apaga seus gastos — eles vão para Outros.",
  "Save category": "Salvar categoria",
  "Edit category": "Editar categoria",
  "Category updated": "Categoria atualizada",
  "Reset to default": "Restaurar padrão",
  // never-converges guard + auto-min toggle fix (v0.20.1)
  "Never": "Nunca",
  "Payments don't cover the interest": "Os pagamentos não cobrem os juros",
  "Not on this plan": "Não neste plano",
  "⚠️ Neither strategy clears this debt yet": "⚠️ Nenhuma estratégia quita esta dívida ainda",
  "⚠️ Your current payments never clear this debt, so there's no freed-up amount to project yet — fix the plan in Debts first.": "⚠️ Seus pagamentos atuais nunca quitam esta dívida, então ainda não há valor liberado para projetar — ajuste o plano em Dívidas primeiro.",
  "Tap to enter your own amount": "Toque para inserir seu próprio valor",
  "That's {apr}% per year — common for revolving credit in some countries. Double-check it's not the monthly rate.": "Isso é {apr}% ao ano — comum em crédito rotativo em alguns países. Confira se não é a taxa mensal.",
  // bill paid-ahead marker (v0.21)
  "Mark as paid ahead": "Marcar como pago antecipadamente",
  "Mark as not paid ahead": "Desmarcar pago antecipado",
  "paid ahead": "pago antecipado",
  // sign-in at entry (v0.34)
  "I already have an account — sign in": "Já tenho uma conta — entrar",
  "Signing in first restores your cloud backup instead of starting over.": "Entrar primeiro restaura seu backup na nuvem em vez de começar do zero.",
  "Cloud backup": "Backup na nuvem",
  "Checking your account…": "Verificando sua conta…",
  "Nothing backed up to your account yet.": "Nada foi salvo na sua conta ainda.",
  "Last backup: {d}": "Último backup: {d}",
  "that's {n} days ago": "isso foi há {n} dias",
  "The server didn't keep that save. Nothing was lost here — keep a file backup and report this.": "O servidor não guardou esse salvamento. Nada foi perdido aqui — faça um backup em arquivo e relate isso.",
  "Back up now": "Fazer backup agora",
  "Backing up…": "Fazendo backup…",
  "Backed up to your account ✓": "Backup salvo na sua conta ✓",
  "Still checking your account — try again in a moment.": "Ainda verificando sua conta — tente de novo em instantes.",
  "Couldn't back up. Check your connection and try again.": "Não foi possível fazer o backup. Verifique sua conexão e tente de novo.",
  "Aurio backs up on its own a moment after every change — this is here for when you want to be certain.": "O Aurio faz backup sozinho logo após cada alteração — isto está aqui para quando você quiser ter certeza.",
  "Which one should Aurio keep?": "Qual o Aurio deve manter?",
  "You're signed in and there's a backup on your account, but this device also has data you entered before signing in. They can't be merged — pick one. Nothing is saved over until you choose.": "Você está conectado e há um backup na sua conta, mas este dispositivo também tem dados que você inseriu antes de entrar. Eles não podem ser mesclados — escolha um. Nada é sobrescrito até você escolher.",
  "Your cloud backup": "Seu backup na nuvem",
  "What's on this device": "O que está neste dispositivo",
  "Saved {d} · {n} debts · {a}": "Salvo em {d} · {n} dívidas · {a}",
  "{n} debts · {a}": "{n} dívidas · {a}",
  "Restore my backup": "Restaurar meu backup",
  "Keep this device's data": "Manter os dados deste dispositivo",
  "Either way the other copy is kept in your account's version history, so this is recoverable.": "De qualquer forma, a outra cópia fica no histórico de versões da sua conta, então isso é recuperável.",
  // grouped repeat occurrences (v0.33)
  "×{n}": "×{n}",
  "Show each date": "Ver cada data",
  "Hide dates": "Ocultar datas",
  // statement cycles, due dates & dated entries (v0.24)
  "Payment due on (day of month)": "Vencimento (dia do mês)",
  "e.g. 15": "ex.: 15",
  "The day your payment is due each month (1–28). Aurio uses it to work out your real statement cycle, so interest is charged on what you actually carried — and to remind you before it's due.": "O dia em que seu pagamento vence a cada mês (1–28). O Aurio usa isso para calcular seu ciclo de fatura real, então os juros incidem sobre o que você realmente carregou — e para te avisar antes do vencimento.",
  "Advanced: set the statement closing day": "Avançado: definir o dia de fechamento da fatura",
  "Statement closes on (day of month)": "Fatura fecha em (dia do mês)",
  "The day your billing period ends — interest is worked out on the average balance you carried across that period. We assume it's {n} days before your due date; change it to match your statement.": "O dia em que seu período de faturamento termina — os juros são calculados sobre o saldo médio que você carregou nesse período. Assumimos {n} dias antes do vencimento; ajuste conforme sua fatura.",
  "Date paid": "Data do pagamento",
  "When the payment actually left your account — the date decides how much interest this cycle costs. You can only date back to the start of the current statement cycle.": "Quando o pagamento realmente saiu da sua conta — a data determina quanto de juros este ciclo custa. Você só pode retroceder até o início do ciclo de fatura atual.",
  "Date charged": "Data da compra",
  "Payment due today": "Pagamento vence hoje",
  "Payment due tomorrow": "Pagamento vence amanhã",
  "Payment due in {d} days": "Pagamento vence em {d} dias",
  "✓ Minimum paid this cycle": "✓ Mínimo pago neste ciclo",
  // debt end date + save/grow tab (v0.22)
  "Ends on (optional)": "Termina em (opcional)",
  "For a fixed-term loan (e.g. a car payment) — the Save tab's money map uses this to show you when that payment frees up. Leave blank for revolving credit like cards.": "Para um empréstimo com prazo fixo (ex.: financiamento de carro) — o mapa financeiro da aba Poupar usa isso para mostrar quando esse pagamento se libera. Deixe em branco para crédito rotativo como cartões.",
  "ends {d}": "termina em {d}",
  "Grow": "Crescer",
  "You can save": "Você pode poupar",
  "mo": "mês",
  "Savings rate {r}%": "Taxa de poupança {r}%",
  "This year's money map": "Mapa financeiro deste ano",
  "saveable": "poupável",
  "committed": "comprometido",
  "≈ {a} you could save this year.": "≈ {a} que você poderia poupar este ano.",
  "→ From {d}: +{a}/mo ({r})": "→ A partir de {d}: +{a}/mês ({r})",
  "{n} ends": "{n} termina",
  "you're debt-free": "você está livre de dívidas",
  "Savings goals": "Metas de poupança",
  "by {d}": "até {d}",
  "+ Add money": "+ Adicionar dinheiro",
  "+ New goal": "+ Nova meta",
  "💰 You have {a} saveable this month.": "💰 Você tem {a} poupável este mês.",
  "Add it to {n} →": "Adicionar a {n} →",
  "Add to {n}": "Adicionar a {n}",
  "Add money": "Adicionar dinheiro",
  "Edit goal": "Editar meta",
  "New goal": "Nova meta",
  "e.g. Emergency fund, House deposit": "ex.: Fundo de emergência, Entrada da casa",
  "Target amount": "Valor da meta",
  "Target date (optional)": "Data da meta (opcional)",
  "Save goal": "Salvar meta",
  "Goal updated": "Meta atualizada",
  "Goal added": "Meta adicionada",
  "Delete {n}? This can't be undone.": "Excluir {n}? Isso não pode ser desfeito.",
  "Goal deleted": "Meta excluída",
  "{a} added to {n} 🎉": "{a} adicionado a {n} 🎉",
};

const ES = {
  // nav
  "Home": "Inicio", "Debts": "Deudas", "Spend": "Gastos", "Budget": "Presupuesto", "Invest": "Invertir",
  // categories
  "Groceries": "Supermercado", "Eating out": "Restaurantes", "Fuel & transport": "Transporte y combustible",
  "Rent": "Alquiler", "Utilities & phone": "Servicios y teléfono", "Subscriptions": "Suscripciones",
  "Insurance": "Seguros", "Family overseas": "Familia en el exterior", "Fitness & supps": "Gimnasio y supl.",
  "Personal": "Personal", "Trip fund": "Fondo de viaje", "Other": "Otros",
  // home
  "You are debt-free on": "Estarás libre de deudas en",
  "Today 🎉": "Hoy 🎉",
  "{n} months sooner": "{n} meses antes", "{n} month sooner": "{n} mes antes",
  "interest saved": "de intereses ahorrados",
  "Extra payment / month": "Pago extra / mes",
  "Drag to see your date move": "Arrastra y mira cómo cambia tu fecha",
  "Total debt today": "Deuda total hoy",
  "Paid off": "Pagado",
  "{p}% of your {a} starting debt destroyed": "{p}% de tu deuda inicial de {a} destruida",
  "Now": "Ahora",
  "Strategy": "Estrategia", "Switch": "Cambiar",
  "Saves {a} vs {s}": "Ahorra {a} vs {s}",
  "Same cost as the alternative": "Mismo costo que la alternativa",
  "avalanche": "avalancha", "snowball": "bola de nieve",
  "Spent this month": "Gastado este mes",
  "of {a} budget · {n} expenses": "de {a} de presupuesto · {n} gastos",
  "Left after plan": "Sobra tras el plan",
  "income − budget − minimums − extra": "ingreso − presupuesto − mínimos − extra",
  "💡 You have {a} unassigned. Sending it to debt moves your debt-free date to {d}.": "💡 Tienes {a} sin asignar. Si lo mandas a la deuda, tu fecha libre de deudas pasa a {d}.",
  "Send it to debt": "Mandarlo a la deuda",
  // debts
  "Strategy comparison": "Comparación de estrategias",
  "Avalanche": "Avalancha", "Snowball": "Bola de nieve",
  "Highest APR first": "Mayor interés primero", "Smallest balance first": "Menor saldo primero",
  "total interest": "intereses totales",
  "ATTACK THIS": "ATACA ESTA",
  "min": "mín", "used": "usado",
  "Credit card": "Tarjeta de crédito", "Personal loan": "Préstamo personal", "Car loan": "Crédito de auto", "Debt": "Deuda",
  "💡 Rate check: you're paying {apr}%. {tip} At ~{good}% this balance costs about {save}/yr less in interest. Compare on MoneyHub or Canstar — offered rates depend on your credit file.":
    "💡 Revisión de tasa: pagas {apr}%. {tip} A ~{good}%, este saldo costaría cerca de {save}/año menos en intereses. Compara en MoneyHub o Canstar — las tasas dependen de tu historial crediticio.",
  "Low-rate NZ cards sit around 12.9–13.9%, and 0% balance-transfer offers exist.": "Las tarjetas de tasa baja en NZ rondan el 12,9–13,9%, y existen ofertas de transferencia al 0%.",
  "Consolidation loans run ~11–13% for good credit; challenger lenders from ~8%.": "Los préstamos de consolidación van del ~11–13% con buen crédito; financieras desde ~8%.",
  "NZ car loans start around 7.25% for decent credit profiles.": "Los créditos de auto en NZ parten de ~7,25% con buen perfil crediticio.",
  "Log payment": "Registrar pago", "Edit": "Editar", "Delete": "Eliminar",
  "Delete {n}?": "¿Eliminar {n}?", "Debt deleted": "Deuda eliminada",
  "+ Add a debt": "+ Agregar deuda",
  "Payment to {n}": "Pago a {n}",
  "Amount": "Monto", "Full": "Total",
  "{a} payment logged 🔥": "Pago de {a} registrado 🔥",
  "Edit debt": "Editar deuda", "New debt": "Nueva deuda", "Type": "Tipo",
  "Name": "Nombre", "Current balance": "Saldo actual", "APR %": "Interés % anual",
  "Minimum payment / month": "Pago mínimo / mes", "Credit limit (blank for loans)": "Límite de la tarjeta (vacío p/ préstamos)",
  "Save debt": "Guardar deuda", "Debt updated": "Deuda actualizada", "Debt added": "Deuda agregada",
  // spend
  "Log an expense": "Registrar un gasto",
  "Note (optional)": "Nota (opcional)", "Add expense": "Agregar gasto",
  "This month": "Este mes",
  "No expenses yet this month. Log your first one above.": "Aún no hay gastos este mes. Registra el primero arriba.",
  "Or import a CSV from your bank →": "O importa un CSV de tu banco →",
  "Expense logged": "Gasto registrado",
  "Logged — ⚠️ {c} is over budget": "Registrado — ⚠️ {c} superó el presupuesto",
  // budget
  "Income (after tax)": "Ingreso (neto)",
  "weekly": "semanal", "fortnightly": "quincenal", "monthly": "mensual",
  "quarterly": "trimestral", "annual": "anual", "once": "una vez", "one-off": "puntual",
  // tight-month forecast warning (v0.37)
  "⚠️ {m} looks short by {a}.": "⚠️ {m} parece ficar corto por {a}.",
  "{names} land that month.": "{names} caen en ese mes.",
  " and ": " y ",
  "Setting aside {a}/month between now and then covers it.": "Apartar {a}/mes desde ahora hasta entonces lo cubre.",
  "Plan for it": "Planificarlo",
  "short by": "faltan",
  "short": "en falta",
  "Big bills ahead": "Cuentas grandes por venir",
  "Early bird: first 100 get 3 months half price": "Early bird: los primeros 100 pagan la mitad por 3 meses",
  "Plan ahead for big bills, test what-if scenarios, sync across devices, unlimited history, CSV export and bill reminders. Cancel anytime.": "Planifica las cuentas grandes, prueba escenarios \"qué pasa si\", sincroniza entre dispositivos, historial ilimitado, exportación CSV y recordatorios de cuentas. Cancela cuando quieras.",
  /* what-if scenarios (Grow) */
  "Plan updated.": "Plan actualizado.",
  /* Grow sub-tabs */
  "Next up": "Siguiente", "Savings": "Ahorros", "Deposit": "Entrada", "Retire": "Jubilación",
  "Grow sections": "Secciones de crecimiento",
  "Saving for a property deposit?": "¿Ahorrando la entrada de una propiedad?",
  "Set up my deposit plan": "Crear mi plan de entrada",
  "Go to your buffer": "Ir a tu colchón",
  "Where your money goes": "A dónde va tu dinero",
  "See the projection": "Ver la proyección",
  "Your buffer is solid and there's no high-interest debt in the way — \"Where your spare money goes\" is safe to follow in order.": "Tu colchón está sólido y no hay deuda cara en el camino — puedes seguir \"A dónde va tu dinero sobrante\" en orden.",
  "Buffer, debt, and goals are all covered. From here, every extra dollar compounds.": "Colchón, deudas y metas están cubiertos. De aquí en adelante, cada peso extra se capitaliza.",
  "What if…": "¿Y si…?",
  "Reset": "Limpiar",
  "Try a change and see what it does to your debt-free date. Nothing is saved until you apply it.": "Prueba un cambio y mira qué le hace a tu fecha sin deudas. No se guarda nada hasta que lo apliques.",
  "Extra at debt": "Extra a la deuda",
  "on top of {a}/mo": "además de {a}/mes",
  "One-off lump sum": "Suma única",
  "a refund or bonus, paid today": "una devolución o bono, pagado hoy",
  "Income change": "Cambio de ingresos",
  "a raise, or lost hours": "un aumento, u horas perdidas",
  "Spending change": "Cambio de gastos",
  "use a minus to cut back": "usa un menos para recortar",
  "Move a lever to see what changes.": "Mueve un control para ver qué cambia.",
  "Debt-free": "Sin deudas",
  "{n} months later": "{n} meses después",
  "{n} month later": "{n} mes después",
  "Same date": "Misma fecha",
  "{a} less interest": "{a} menos de intereses",
  "{a} more interest": "{a} más de intereses",
  "✓ Your cash flow covers this every month for the next year.": "✓ Tu flujo de caja lo cubre todos los meses del próximo año.",
  "⚠️ Short in {m} of the next {n} months (up from {b}), by as much as {a}.": "⚠️ Cortos en {m} de los próximos {n} meses (antes {b}), hasta por {a}.",
  "⚠️ Short in {m} of the next {n} months, by as much as {a} — the same as your plan today.": "⚠️ Cortos en {m} de los próximos {n} meses, hasta por {a} — igual que tu plan de hoy.",
  "Make this the plan": "Hacer de esto el plan",

  "/mo": "/mes",
  "Set aside": "Apartar",
  "Put that much away each month and the bill is already paid for when it lands.": "Aparta esa cantidad cada mes y la cuenta ya estará pagada cuando llegue.",
  "≈ {a} per month — all plan maths use this equivalent.": "≈ {a} por mes — todos los cálculos del plan usan este equivalente.",
  "Income (monthly)": "Ingreso (mensual)", "Living budget": "Presupuesto de vida",
  "Debt minimums": "Mínimos de deudas", "Extra to debt": "Extra a deudas", "Unassigned": "Sin asignar",
  "⚠️ Your plan spends {a} more than you earn. Trim a category or lower the extra payment.": "⚠️ Tu plan gasta {a} más de lo que ganas. Recorta una categoría o baja el pago extra.",
  "Category budgets": "Presupuestos por categoría",
  "Viewing per {c}. Budgets are stored monthly — the input still edits the monthly amount.": "Viendo por {c}. Los presupuestos se guardan mensualmente — el campo edita el monto mensual.",
  "week": "semana", "fortnight": "quincena",
  "No recurring items yet. Add the bills that live here (e.g. {ex}) and they'll log themselves.": "Aún no hay ítems recurrentes. Agrega las cuentas de esta categoría (ej.: {ex}) y se registrarán solas.",
  "power, gas, water, phone": "luz, gas, agua, teléfono",
  "health, car, income protection": "salud, auto, protección de ingresos",
  "Netflix, Spotify, iCloud": "Netflix, Spotify, iCloud",
  "monthly transfer to family": "transferencia mensual a la familia",
  "regular payments": "pagos regulares",
  "next": "próx.",
  "Recurring commits {r}/mo of this {b} budget": "Los recurrentes comprometen {r}/mes de este presupuesto de {b}",
  " — budget is below your fixed bills": " — el presupuesto está por debajo de tus cuentas fijas",
  "+ Add recurring item": "+ Agregar ítem recurrente",
  "Recurring": "Recurrente",
  "e.g. Netflix, Power, Transfer to parents": "ej.: Netflix, Luz, Transferencia a los padres",
  "Repeats": "Se repite", "Next due date": "Próximo vencimiento",
  "Save recurring expense": "Guardar gasto recurrente",
  "Recurring expense added — it logs itself from its next due date": "Gasto recurrente agregado — se registra solo desde su próximo vencimiento",
  "{n} recurring expenses auto-logged": "{n} gastos recurrentes registrados automáticamente",
  "{n} recurring expense auto-logged": "{n} gasto recurrente registrado automáticamente",
  // invest
  "Projected passive income at {age}": "Ingreso pasivo proyectado a los {age}",
  "per month": "por mes",
  "Goal: {a}/mo": "Meta: {a}/mes",
  "surplus": "sobre la meta", "short — see boosters below": "bajo la meta — mira los impulsos abajo",
  "Your retirement inputs": "Tus datos de retiro",
  "Age now": "Edad actual", "Retire at": "Retirarte a los",
  "Target passive income / month": "Ingreso pasivo deseado / mes",
  "Already invested": "Ya invertido", "Investing / month now": "Invirtiendo / mes hoy",
  "Assumptions (editable — the future is not predictable)": "Supuestos (editables — el futuro no es predecible)",
  "Expected return {r}% /yr": "Retorno esperado {r}% /año",
  "Long-run diversified share markets have averaged ~7–10% nominal before fees and tax; past performance does not predict the future.": "Los mercados accionarios diversificados han promediado ~7–10% nominal a largo plazo, antes de comisiones e impuestos; el rendimiento pasado no garantiza el futuro.",
  "Withdrawal / dividend rate {r}%": "Tasa de retiro / dividendos {r}%",
  "The classic '4% rule' sizes the pot you need; dividend portfolios often yield 3–6%.": "La clásica 'regla del 4%' dimensiona el patrimonio que necesitas; las carteras de dividendos suelen rendir 3–6%.",
  "You need a pot of about": "Necesitas un patrimonio de aproximadamente",
  "to draw {a}/mo at {r}%": "para retirar {a}/mes al {r}%",
  "The debt-free boost": "El impulso sin deudas",
  "From {d}, your {a}/mo of debt payments becomes investing power. That single move changes your projection from {p1} to {p2} at age {age}.": "Desde {d}, tus {a}/mes de pagos de deuda se convierten en poder de inversión. Solo ese cambio lleva tu proyección de {p1} a {p2} a los {age} años.",
  "Current plan": "Plan actual", "With debt-free boost": "Con el impulso sin deudas",
  "Goal reached at age {age} 🎉": "Meta alcanzada a los {age} años 🎉",
  "Goal not reached by {age} — raise contributions, retire later, or adjust the target.": "Meta no alcanzada a los {age} — sube los aportes, retírate más tarde o ajusta la meta.",
  "Where people invest for income": "Dónde invierte la gente para generar ingresos",
  "Educational only — not financial advice, and not a recommendation of any product. Returns are not guaranteed and past performance does not predict future results. Consider licensed advice for personal decisions.": "Contenido educativo — no es asesoría financiera ni recomendación de ningún producto. Los retornos no están garantizados y el rendimiento pasado no predice resultados futuros. Considera asesoría licenciada para decisiones personales.",
  "Loading your money…": "Cargando tus finanzas…",
  "Language": "Idioma",
  // onboarding
  "Welcome to Aurio": "Bienvenido a Aurio",
  "Answer a few quick questions and your Home, Debts, Budget and Invest tabs will be built around your real numbers. It takes about two minutes.": "Responde unas preguntas rápidas y las pestañas Inicio, Deudas, Presupuesto e Invertir se armarán con tus números reales. Toma unos dos minutos.",
  "Set up my plan": "Armar mi plan",
  "Skip for now — use example data": "Omitir por ahora — usar datos de ejemplo",
  "You can redo any of this later — every number stays editable inside the app.": "Puedes rehacer todo esto después — cada número sigue editable dentro de la app.",
  "Step {n} of {m}": "Paso {n} de {m}",
  "Back": "Atrás", "Next": "Siguiente",
  "What do you take home?": "¿Cuánto recibes neto?",
  "Your pay after tax. Pick how often you're paid — we'll do the monthly maths.": "Tu pago después de impuestos. Elige con qué frecuencia te pagan — nosotros hacemos la cuenta mensual.",
  "What do you owe?": "¿Qué debes?",
  "Add each card or loan — estimates are fine. This powers your payoff plan.": "Agrega cada tarjeta o préstamo — los estimados sirven. Esto alimenta tu plan de pago.",
  "Add this debt": "Agregar esta deuda",
  "No debts? Lucky you — just tap Next.": "¿Sin deudas? Qué suerte — solo toca Siguiente.",
  "What does a month cost you?": "¿Cuánto te cuesta un mes?",
  "Rough numbers are perfect. You can fine-tune every category later in Budget.": "Los números aproximados son perfectos. Puedes afinar cada categoría después en Presupuesto.",
  "Where are you headed?": "¿Hacia dónde vas?",
  "Used by the Invest tab to project your passive income.": "Lo usa la pestaña Invertir para proyectar tu ingreso pasivo.",
  "Here's your starting plan": "Aquí está tu plan inicial",
  "Open my plan": "Abrir mi plan",
  "month": "mes",
  "How often do you think about these costs?": "¿Con qué frecuencia piensas en estos costos?",
  "Amounts below are per {c} — we'll convert everything to monthly for your plan.": "Los montos de abajo son por {c} — convertiremos todo a mensual para tu plan.",
  "APR is the yearly interest rate you're charged on this debt — you'll find it on your card or loan statement.": "El APR es la tasa de interés anual que te cobran por esta deuda — la encuentras en el estado de cuenta de tu tarjeta o préstamo.",
  // account / My Info
  "My Info": "Mi Perfil",
  "Signed in as": "Conectado como",
  "Email": "Correo",
  "Subscription plan": "Plan de suscripción",
  "Status": "Estado",
  "Trial ends": "La prueba termina el",
  "Full name": "Nombre completo",
  "Save": "Guardar", "Saved": "Guardado",
  "Sign in": "Iniciar sesión", "Sign out": "Cerrar sesión",
  "Sign in to see your plan and keep your profile with Zoldan Labs. Your money data stays on this device.": "Inicia sesión para ver tu plan y mantener tu perfil con Zoldan Labs. Tus datos financieros se quedan en este dispositivo.",
  "We'll email you a sign-in code — no password needed.": "Te enviaremos un código de acceso por correo — sin contraseña.",
  "Send code": "Enviar código",
  "Code sent — check your email.": "Código enviado — revisa tu correo.",
  "No code in the email? Tapping its sign-in link works too — this app signs in automatically.": "¿No hay código en el correo? Tocar su enlace de acceso también funciona — la app inicia sesión automáticamente.",
  "Enter the code from your email": "Ingresa el código de tu correo",
  "Verify & sign in": "Verificar e iniciar sesión",
  "Couldn't send the code. Check the email and try again.": "No se pudo enviar el código. Revisa el correo e intenta de nuevo.",
  "That code didn't work. Try again.": "Ese código no funcionó. Intenta de nuevo.",
  "Signed in ✓": "Sesión iniciada ✓",
  "Signed out": "Sesión cerrada",
  "Account features aren't set up in this build.": "Las funciones de cuenta no están configuradas en esta versión.",
  "Your trial has ended — contact Zoldan Labs about full access.": "Tu prueba terminó — contacta a Zoldan Labs por el acceso completo.",
  "Free": "Gratis", "Trial": "Prueba", "Tester": "Tester", "Paid": "Pago", "Lifetime": "Vitalicio",
  "Active": "Activo", "Expired": "Expirado", "Cancelled": "Cancelado",
  // upcoming bills & reminders
  "Upcoming payments": "Próximos pagos",
  "Payday": "Día de pago",
  "Nothing due in the next 30 days.": "Nada vence en los próximos 30 días.",
  "today": "hoy", "tomorrow": "mañana", "in {d} days": "en {d} días",
  "💰 Set aside {a} for the {n} payments due before your payday on {d}.": "💰 Separa {a} para los {n} pagos que vencen antes de tu día de pago el {d}.",
  "🔔 Remind me when the app opens up to {n} days before each payment": "🔔 Avísame al abrir la app hasta {n} días antes de cada pago",
  "Bill reminders on 🔔": "Recordatorios de cuentas activados 🔔",
  "Reminders on 🔔 — they'll show when you open Aurio. Sign in to get them with the app closed.": "Recordatorios activados 🔔 — aparecen cuando abres Aurio. Inicia sesión para recibirlos con la app cerrada.",
  "Bill reminders off": "Recordatorios de cuentas desactivados",
  "Notifications aren't supported in this browser.": "Este navegador no soporta notificaciones.",
  "Notifications are blocked — allow them in your browser settings.": "Notificaciones bloqueadas — permítelas en la configuración del navegador.",
  "{n} is due today — {a}": "{n} vence hoy — {a}",
  "{n} is due in {d} days — {a}": "{n} vence en {d} días — {a}",
  "{c} payments due in the next {d} days — {a} total": "{c} pagos vencen en los próximos {d} días — {a} en total",
  "minimum": "mínimo",
  "paid this cycle": "pagado en este ciclo",
  "Minimum already paid this cycle": "Mínimo ya pagado en este ciclo",
  "Log this on the Debts tab": "Regístralo en la pestaña Deudas",
  // spend history
  "Previous month": "Mes anterior", "Next month": "Mes siguiente",
  "Last 6 months": "Últimos 6 meses",
  "Where it went": "A dónde se fue",
  // chart clarity pass (v0.25)
  "out this month (spend + debt payments)": "salieron este mes (gastos + pagos de deudas)",
  // card charges in the budget (v0.36)
  "{a} charge logged to {c}": "Cargo de {a} registrado en {c}",
  "What was it for?": "¿Para qué fue?",
  "Not a budget expense": "No es un gasto del presupuesto",
  "Only the card balance moves — nothing reaches your budget. Right for a balance transfer, cash advance or fee.": "Solo cambia el saldo de la tarjeta — nada entra en tu presupuesto. Ideal para transferencia de saldo, adelanto de efectivo o comisión.",
  "This lands in your budget straight away. It counts as money out later, when you pay the card.": "Esto entra en tu presupuesto de inmediato. Cuenta como dinero que salió después, cuando pagues la tarjeta.",
  "left your account this month (cash spend + debt payments)": "salieron de tu cuenta este mes (gastos en efectivo + pagos de deudas)",
  "{a} of this month's spending went on a card — that counts here when you pay the card, not when you swipe.": "{a} de los gastos de este mes fueron con tarjeta — eso cuenta aquí cuando pagas la tarjeta, no cuando la usas.",
  "Charged to {n} — undo it from that debt's activity.": "Cargado en {n} — deshazlo desde la actividad de esa deuda.",
  "your card": "tu tarjeta",
  "card": "tarjeta",
  "<1%": "<1%",
  "{p}% of your income": "{p}% de tus ingresos",
  "You've spent more than came in this month.": "Gastaste más de lo que entró este mes.",
  "{a} out of {b} income — {p}% used.": "{a} de {b} de ingresos — {p}% usado.",
  "{n} smaller category": "{n} categoría menor",
  "{n} smaller categories": "{n} categorías menores",
  "show all": "ver todas",
  "show less": "ver menos",
  "Spending by category: {list}": "Gastos por categoría: {list}",
  "Monthly spend for the last 6 months: {list}": "Gasto mensual de los últimos 6 meses: {list}",
  "Debt falling from {a} to zero by {d}.": "Deuda bajando de {a} a cero en {d}.",
  "Debt balance over time — it never reaches zero on this plan.": "Saldo de la deuda a lo largo del tiempo — con este plan nunca llega a cero.",
  "{p}% of your {a} limit used": "{p}% de tu límite de {a} usado",
  "{c}: {s} spent of {b} budget": "{c}: {s} gastados de {b} del presupuesto",
  "{c}: {s} spent, no budget set": "{c}: {s} gastados, sin presupuesto definido",
  "Next 12 months, saveable versus committed. About {a} saveable per month on average.": "Próximos 12 meses, ahorrable versus comprometido. Cerca de {a} ahorrables por mes, en promedio.",
  "Projected balance from age {a} to {b}: {x} with the debt-free boost, {y} on the current plan.": "Saldo proyectado de los {a} a los {b} años: {x} con el impulso sin deudas, {y} con el plan actual.",
  "over budget by {a}": "superó el presupuesto por {a}",
  "No expenses recorded in {m}.": "Sin gastos registrados en {m}.",
  // backup & cloud
  "Restored from your cloud backup ☁️": "Restaurado desde tu respaldo en la nube ☁️",
  "Checking your cloud backup…": "Verificando tu respaldo en la nube…",
  "Couldn't reach your cloud backup": "No se pudo acceder a tu respaldo en la nube",
  "You're signed in, so there may be data saved to your account. Aurio won't start a fresh setup until it can check — that's what stops a new setup from replacing your real data.": "Has iniciado sesión, así que puede haber datos guardados en tu cuenta. Aurio no iniciará una configuración nueva hasta poder verificarlo — eso es lo que evita que una configuración nueva reemplace tus datos reales.",
  "Try again": "Intentar de nuevo",
  "☁️ Cloud backup is on — your data saves to your account automatically and follows you to any device you sign in on.": "☁️ Respaldo en la nube activo — tus datos se guardan en tu cuenta automáticamente y te siguen a cualquier dispositivo donde inicies sesión.",
  "Backup & restore": "Respaldo y restauración",
  "Download a backup file of everything in the app — you can restore it here on any device, any time.": "Descarga un archivo de respaldo con todo lo de la app — puedes restaurarlo aquí en cualquier dispositivo, cuando quieras.",
  "Download backup": "Descargar respaldo",
  "Restore backup": "Restaurar respaldo",
  "Backup downloaded": "Respaldo descargado",
  "Replace the data on this device with this backup?": "¿Reemplazar los datos de este dispositivo con este respaldo?",
  "Backup restored ✓": "Respaldo restaurado ✓",
  "That file doesn't look like a Aurio backup.": "Ese archivo no parece un respaldo de Aurio.",
  // csv export
  "expense": "gasto", "debt payment": "pago de deuda",
  "Export CSV": "Exportar CSV",
  "Export all as CSV": "Exportar todo en CSV",
  "CSV exported": "CSV exportado",
  // reset / danger zone
  "Danger zone": "Zona de peligro",
  "Erase all data": "Borrar todos los datos",
  "⚠️ This permanently erases all debts, expenses, budgets and settings from this device and your cloud backup. This cannot be undone.": "⚠️ Esto borra permanentemente todas las deudas, gastos, presupuestos y configuraciones de este dispositivo y de tu respaldo en la nube. No se puede deshacer.",
  "Download backup, then erase": "Descargar respaldo y luego borrar",
  "Erase without backup": "Borrar sin respaldo",
  "Cancel": "Cancelar",
  "Privacy": "Privacidad",
  // milestones & coach hint
  "You're debt-free.": "Estás libre de deudas.",
  "Every debt, gone. This is what you built.": "Cada deuda, saldada. Esto lo construiste tú.",
  "A debt, gone.": "Una deuda, saldada.",
  "{n} is fully paid off.": "{n} está totalmente pagada.",
  "The home stretch.": "La recta final.",
  "75% destroyed — the end is in sight.": "75% destruido — el final está a la vista.",
  "Halfway there.": "Vas por la mitad.",
  "Half of your starting debt is gone.": "La mitad de tu deuda inicial ya no está.",
  "A quarter down.": "Un cuarto del camino.",
  "You've destroyed 25% of your starting debt.": "Has destruido el 25% de tu deuda inicial.",
  "Keep going": "Seguir",
  "👇 Try it — drag to watch your debt-free date jump.": "👇 Pruébalo — arrastra y mira saltar tu fecha libre de deudas.",
  // sectioned csv export
  "Aurio financial export": "Exportación financiera Aurio",
  "all data": "todos los datos",
  "Income": "Ingreso", "Budgets": "Presupuestos", "Recurring bills": "Cuentas recurrentes",
  "Transactions": "Transacciones", "Summary": "Resumen",
  "frequency": "frecuencia", "amount": "monto", "monthly equivalent": "equivalente mensual",
  "balance": "saldo", "min payment / month": "pago mínimo / mes", "credit limit": "límite de crédito",
  "due day": "día de vencimiento", "statement day": "día de cierre", "interest posted through": "intereses aplicados hasta",
  "payoff at minimum only": "pago solo con el mínimo", "interest at minimum only": "intereses solo con el mínimo",
  "category": "categoría", "monthly budget": "presupuesto mensual", "spent this month": "gastado este mes",
  "next due": "próximo vencimiento", "date": "fecha", "description": "descripción",
  "total debt": "deuda total", "monthly income": "ingreso mensual", "total monthly budget": "presupuesto mensual total",
  "debt-free date (current plan)": "fecha libre de deudas (plan actual)",
  "projected interest (current plan)": "intereses proyectados (plan actual)",
  "projected interest (minimums only)": "intereses proyectados (solo mínimos)",
  "never at this minimum": "nunca con este mínimo",
  "Couldn't export — try again.": "No se pudo exportar — intenta de nuevo.",
  // debt true cost
  "{n} year": "{n} año", "{n} years": "{n} años", "{n} month": "{n} mes", "{n} months": "{n} meses",
  "At minimum only: paid off in {t} · {i} interest · {tot} total repaid": "Solo con el mínimo: pagada en {t} · {i} de intereses · {tot} pagados en total",
  "⚠️ The minimum never pays this off — the balance grows every month.": "⚠️ El mínimo nunca salda esta deuda — el saldo crece cada mes.",
  "⚠️ At minimums only this debt never ends — a minimum doesn't even cover its interest. See Debts.": "⚠️ Solo con los mínimos esta deuda nunca termina — un mínimo ni siquiera cubre sus intereses. Mira Deudas.",
  "⚠️ Your current payments never clear this debt — raise the minimums or the extra payment.": "⚠️ Tus pagos actuales nunca saldan esta deuda — sube los mínimos o el pago extra.",
  "True cost on your current plan: {a} ({i} interest)": "Costo real con tu plan actual: {a} ({i} de intereses)",
  "At minimums only it would be {a} — your extra payment saves {s}.": "Solo con los mínimos sería {a} — tu pago extra ahorra {s}.",
  // recurring edit
  "Recurring expense updated": "Gasto recurrente actualizado",
  "Save changes": "Guardar cambios",
  // reserved budget segment
  "{a} reserved for upcoming bills": "{a} reservado para cuentas por vencer",
  // currency
  "Currency": "Moneda",
  "Terms": "Términos",
  // premium / stripe
  "Welcome to Premium 🎉": "Bienvenido a Premium 🎉",
  "Your trial has ended. Upgrade to keep Premium.": "Tu prueba terminó. Suscríbete para mantener Premium.",
  // CSV import (v0.31)
  "Import from CSV": "Importar desde CSV",
  "Import CSV": "Importar CSV",
  "Export a CSV from your bank or spreadsheet and pick it here. It's read on your device — nothing is uploaded, and no bank login is ever needed.": "Exporta un CSV de tu banco o planilla y selecciónalo aquí. Se lee en tu dispositivo — no se sube nada, y nunca hace falta un acceso bancario.",
  "Choose a CSV file": "Elegir un archivo CSV",
  "Reading…": "Leyendo…",
  "That file doesn't look like a CSV Aurio can read.": "Ese archivo no parece un CSV que Aurio pueda leer.",
  "Couldn't find a date and an amount column in that file. Check it's a transaction export, not a summary.": "No encontré una columna de fecha y una de monto en ese archivo. Verifica que sea un extracto de transacciones, no un resumen.",
  "{n} to import": "{n} para importar",
  "{n} already in Aurio": "{n} ya están en Aurio",
  "{n} money-in rows skipped": "{n} filas de ingreso omitidas",
  "{n} couldn't be read": "{n} no se pudieron leer",
  "Reading dates as {order}.": "Leyendo las fechas como {order}.",
  "day/month (3/4 = 3 April)": "día/mes (3/4 = 3 de abril)",
  "month/day (3/4 = 4 March)": "mes/día (3/4 = 4 de marzo)",
  "First row: {d}": "Primera fila: {d}",
  "Not right — swap day and month": "No está bien — intercambiar día y mes",
  "Everything in this file is already in Aurio — nothing to add.": "Todo en este archivo ya está en Aurio — nada que agregar.",
  "No spending rows found in this file.": "No se encontraron filas de gasto en este archivo.",
  "(no description)": "(sin descripción)",
  "Category": "Categoría",
  "Showing the first {n}. All {total} will be imported.": "Mostrando los primeros {n}. Se importarán los {total}.",
  "Import {n} expenses": "Importar {n} gastos",
  "You can undo this straight afterwards if it doesn't look right.": "Puedes deshacerlo enseguida si no se ve bien.",
  "{n} expenses imported": "{n} gastos importados",
  "Imported {n} expenses": "{n} gastos importados",
  "Remove the {n} expenses from that import?": "¿Eliminar los {n} gastos de esa importación?",
  "Import undone": "Importación deshecha",
  "Keep": "Mantener",
  // emergency fund + next-dollar guidance (v0.30)
  "🛟 Emergency fund": "🛟 Fondo de emergencia",
  "Emergency fund": "Fondo de emergencia",
  "3 months of essentials — rent, groceries, utilities, transport, insurance, your recurring bills, and every debt minimum. Sized from your own numbers.": "3 meses de gastos esenciales — alquiler, supermercado, servicios, transporte, seguro, tus cuentas recurrentes y todos los pagos mínimos de deudas. Calculado con tus propios números.",
  "Saved so far": "Ahorrado hasta ahora",
  "🌱 Starter buffer": "🌱 Colchón inicial",
  "High-interest debt": "Deuda de interés alto",
  "🛟 Full emergency fund": "🛟 Fondo de emergencia completo",
  "🎯 Goals & deposit": "🎯 Metas y pie",
  "Build a starter buffer first": "Arma un colchón inicial primero",
  "{a} covers one month of essentials — rent, groceries, utilities, transport, insurance, your bills, and every debt minimum. A small buffer first means the next surprise bill doesn't have to become a new debt.": "{a} cubre un mes de gastos esenciales — alquiler, supermercado, servicios, transporte, seguro y todos los pagos mínimos de deudas. Un colchón pequeño primero significa que la próxima cuenta inesperada no tiene que convertirse en una deuda nueva.",
  "Clear {n} before investing": "Salda {n} antes de invertir",
  "It costs {apr}%/yr. Your investing assumption is {r}%/yr — paying it off is a guaranteed {apr}% return, and you won't reliably beat that in the market.": "Cuesta {apr}% anual. Tu expectativa de inversión es {r}% anual — saldar esta deuda es un retorno garantizado del {apr}%, y difícilmente lo superes en el mercado de forma consistente.",
  "Go to Debts": "Ir a Deudas",
  "Grow your emergency fund": "Haz crecer tu fondo de emergencia",
  "Your starter buffer is in place. The fully-funded target is {a} — three months of essentials.": "Tu colchón inicial ya está listo. La meta completa es {a} — tres meses de gastos esenciales.",
  "Fund your goals and deposit": "Financia tus metas y el pie",
  "Your buffer is solid and there's no high-interest debt in the way — \"Where your spare money goes\" below is safe to follow in order.": "Tu colchón está sólido y no hay deuda de interés alto en el camino — \"A dónde va tu dinero disponible\" abajo se puede seguir en orden.",
  "Put spare money into investing": "Pon el dinero disponible a invertir",
  "Buffer, debt, and goals are all covered. From here, every extra dollar compounds — see the projection below.": "Colchón, deudas y metas están cubiertos. Desde aquí, cada dólar extra genera interés compuesto — mira la proyección abajo.",
  "Do this next": "Haz esto a continuación",
  "Then:": "Después:",
  "Suggested order based on your own numbers — not financial advice.": "Orden sugerido según tus propios números — no es asesoría financiera.",
  // allocation waterfall + inflation (v0.29)
  "Where your spare money goes": "A dónde va tu dinero disponible",
  "One at a time, in this order — each starts when the one above it is funded. Reorder to change what gets your money first.": "Uno a la vez, en este orden — cada uno empieza cuando el de arriba se completa. Reordena para cambiar qué recibe tu dinero primero.",
  "🏠 House deposit": "🏠 Pie de la casa",
  "📈 Investing": "📈 Invirtiendo",
  "gets what's left each month": "recibe lo que sobra cada mes",
  "nothing spare yet": "aún no sobra nada",
  "from {d}": "desde {d}",
  "no date yet": "aún sin fecha",
  "funded": "completado",
  "ready {d}": "listo en {d}",
  "getting your money now": "recibiendo tu dinero ahora",
  "Move up": "Subir",
  "Move down": "Bajar",
  "✓ Funded": "✓ Completado",
  "No date yet — nothing spare each month": "Aún sin fecha — no sobra nada al mes",
  "⚠️ On this plan you get there {d}, {n} after your target. Move it up the list, or lower the target.": "⚠️ Con este plan llegas en {d}, {n} después de tu meta. Súbelo en la lista o baja el objetivo.",
  "On track for {d}": "En camino para {d}",
  "Starts {d}, once {n} ahead of it are funded — reorder in \"Where your spare money goes\" to change that.": "Empieza en {d}, cuando los {n} que van antes estén completados — reordena en \"A dónde va tu dinero disponible\" para cambiarlo.",
  "now": "ahora",
  "Saving {a}/mo · {n} of funding": "Ahorrando {a}/mes · {n} de aportes",
  "≈ {a}/mo in today's money, at {r}% inflation": "≈ {a}/mes en dinero de hoy, con {r}% de inflación",
  "Inflation {r}% /yr": "Inflación {r}% anual",
  "Used to show what your future income is worth in today's money. Set it to 0 to see the raw numbers instead.": "Se usa para mostrar cuánto vale tu ingreso futuro en dinero de hoy. Ponlo en 0 para ver las cifras sin ajustar.",
  "🎉 You're debt-free — {a}/mo no longer goes to debt.": "🎉 Estás libre de deudas — {a}/mes ya no va a deudas.",
  "Redirecting {pct}% is {b}/mo into investing.": "Redirigir {pct}% son {b}/mes hacia inversión.",
  "Other markets ({n})": "Otros mercados ({n})",
  "Hide other markets": "Ocultar otros mercados",
  // first-home planner (v0.28)
  "Saving for a first home?": "¿Ahorrando para tu primera casa?",
  "Track your deposit alongside your debt payoff and see how the two dates compare — a debt clearing after your deposit is ready is usually what actually decides your move-in date.": "Sigue tu pie junto con el pago de tus deudas y compara ambas fechas — una deuda que se salda después de tener el pie listo suele ser lo que realmente decide cuándo te mudas.",
  "Set up my first-home plan": "Configurar mi plan de primera vivienda",
  "New Zealand": "Nueva Zelanda",
  "Australia": "Australia",
  "KiwiSaver (usable)": "KiwiSaver (utilizable)",
  "Super (usable, FHSS)": "Super (utilizable, FHSS)",
  "Other savings": "Otros ahorros",
  "Deposit ready": "Pie listo",
  "Ready now 🎉": "Listo ahora 🎉",
  "No date yet — nothing saveable each month": "Aún sin fecha — nada ahorrable al mes",
  "The \"You can save\" amount above is $0 — free up money in your budget or income to project a date here.": "El monto \"Puedes ahorrar\" de arriba está en $0 — libera dinero en tu presupuesto o ingresos para proyectar una fecha aquí.",
  "{a} of {b} saved toward your deposit": "{a} de {b} ahorrados para tu pie",
  "{a} of {b} saved toward your deposit — {p}%": "{a} de {b} ahorrados para tu pie — {p}%",
  "{a} of {b}": "{a} de {b}",
  "⚠️ Your debt clears {d} — after your deposit is ready. Lenders weigh your debts when sizing a loan, so on this plan the debt date is the one moving your move-in date.": "⚠️ Tu deuda se salda en {d} — después de que tu pie esté listo. Los bancos consideran tus deudas al calcular un préstamo, así que en este plan la fecha de la deuda es la que decide cuándo te mudas.",
  "Your deposit is ready {d}, before you're debt-free {b} — clearing the debt first is likely what decides when a lender says yes.": "Tu pie está listo el {d}, antes de que quedes libre de deudas el {b} — saldar la deuda primero probablemente sea lo que decida cuándo un banco te apruebe.",
  "First-home schemes only — leave this at 0 for an investment property.": "Solo para programas de primera vivienda — déjalo en 0 si es una propiedad de inversión.",
  "Target price": "Precio objetivo",
  "Deposit needed": "Pie necesario",
  "Cash saved": "Efectivo ahorrado",
  "Still to find": "Aún falta",
  "Saving {a}/mo · {n}": "Ahorrando {a}/mes · {n}",
  "See {c} first-home help": "Ver ayuda de primera vivienda para {c}",
  "Hide this section": "Ocultar esta sección",
  "Your KiwiSaver balance {a} → {b} usable toward the deposit.": "Tu saldo de KiwiSaver {a} → {b} utilizable para el pie.",
  "Your balance {a} → {b} usable under the $50,000 lifetime cap.": "Tu saldo {a} → {b} utilizable dentro del tope vitalicio de $50.000.",
  "At a 5% deposit your target is {a} — you already have enough.": "Con un pie del 5% tu objetivo es {a} — ya tienes suficiente.",
  "At a 5% deposit your target is {a} — {b} still to find.": "Con un pie del 5% tu objetivo es {a} — aún falta {b}.",
  "{c} first-home help": "Ayuda de primera vivienda para {c}",
  "General information, not financial advice, and not a recommendation of any product. Scheme rules change — verify current eligibility and figures with the relevant government agency or lender before relying on them. Checked {v}.": "Información general, no es asesoría financiera ni recomendación de ningún producto. Las reglas de los programas cambian — verifica la elegibilidad y las cifras actuales con el organismo del gobierno o prestamista correspondiente antes de basarte en ellas. Verificado en {v}.",
  // referral loop (v0.27)
  "Give a month, get a month": "Da un mes, gana un mes",
  "Your friend gets a free month of Premium straight away. You get one too, once they're still using Aurio a week later.": "Tu amigo recibe un mes gratis de Premium al instante. Tú recibes el tuyo cuando siga usando Aurio una semana después.",
  "Copy link": "Copiar enlace",
  "Invite a friend": "Invitar a un amigo",
  "Invite link copied": "Enlace de invitación copiado",
  "Couldn't copy — long-press the code instead.": "No se pudo copiar — mantén pulsado el código.",
  "I'm using Aurio to clear my debt — here's a free month of Premium: {link}": "Estoy usando Aurio para saldar mis deudas — aquí tienes un mes gratis de Premium: {link}",
  "{n} free month earned": "{n} mes gratis conseguido",
  "{n} free months earned": "{n} meses gratis conseguidos",
  "{n} friend still to qualify": "{n} amigo aún por calificar",
  "{n} friends still to qualify": "{n} amigos aún por calificar",
  "Includes {n} days of referral credit.": "Incluye {n} días de crédito por referidos.",
  "A free month of Premium is yours 🎉": "Un mes gratis de Premium es tuyo 🎉",
  "A friend stuck with Aurio — a free month is yours 🎉": "Un amigo siguió con Aurio — un mes gratis es tuyo 🎉",
  "{n} friends stuck with Aurio — {n} free months are yours 🎉": "{n} amigos siguieron con Aurio — {n} meses gratis son tuyos 🎉",
  // self-serve trial (v0.26)
  "Try Premium free for 14 days": "Prueba Premium gratis por 14 días",
  "Sync across your devices, unlimited history, CSV export and bill reminders. No card needed — it simply ends on its own.": "Sincronización entre tus dispositivos, historial ilimitado, exportación CSV y recordatorios de cuentas. Sin tarjeta — la prueba termina sola.",
  "Start my free trial": "Comenzar mi prueba gratis",
  "Start my free 14-day trial": "Comenzar mi prueba gratis de 14 días",
  "Create a free account to start": "Crear una cuenta gratis para comenzar",
  "Starting…": "Comenzando…",
  "Your 14-day Premium trial is live 🎉": "Tu prueba Premium de 14 días está activa 🎉",
  "Couldn't start the trial — try again.": "No se pudo iniciar la prueba — inténtalo de nuevo.",
  "Dismiss": "Descartar",
  "See plans": "Ver planes",
  "✓ Premium is active — cloud sync, unlimited history, CSV export, reminders and full projections are unlocked.": "✓ Premium activo — sincronización en la nube, historial ilimitado, exportación CSV, recordatorios y proyecciones completas desbloqueados.",
  "Manage subscription": "Gestionar suscripción",
  "Couldn't open billing. Try again.": "No se pudo abrir la facturación. Intenta de nuevo.",
  "Unlock Premium": "Desbloquear Premium",
  "Cloud sync across devices, unlimited history, CSV export, bill reminders and full retirement projections.": "Sincronización entre dispositivos, historial ilimitado, exportación CSV, recordatorios de cuentas y proyecciones completas de retiro.",
  "Cloud sync across devices, unlimited history, CSV export, bill reminders and full retirement projections. Cancel anytime.": "Sincronización entre dispositivos, historial ilimitado, exportación CSV, recordatorios de cuentas y proyecciones completas de retiro. Cancela cuando quieras.",
  "Couldn't start checkout. Try again.": "No se pudo iniciar el pago. Intenta de nuevo.",
  "Opening secure checkout…": "Abriendo el pago seguro…",
  "Secure payment by Stripe. Your card details never touch Aurio.": "Pago seguro con Stripe. Los datos de tu tarjeta nunca pasan por Aurio.",
  "Final price is confirmed at checkout for your region.": "El precio final se confirma al pagar según tu región.",
  "Yearly": "Anual", "Monthly": "Mensual",
  "Best value — save 40%": "Mejor valor — ahorra 40%",
  "Pay once, yours forever": "Paga una vez, tuyo para siempre",
  "/year": "/año", "/month": "/mes", " once": " una vez",
  // payment history (v0.18)
  "Payment undone": "Pago deshecho",
  "↓ Paid {a} this month": "↓ {a} pagados este mes",
  "Activity ({n})": "Actividad ({n})",
  "Hide history": "Ocultar historial",
  "{a} paid": "{a} pagados",
  "{a} charged": "{a} cobrados",
  "{a} interest": "{a} de interés",
  "across {n} entries": "en {n} registros",
  "Payment": "Pago",
  "Undo this payment? The balance will be restored.": "¿Deshacer este pago? El saldo se restaurará.",
  "Undo": "Deshacer",
  // log charge + monthly interest (v0.23)
  "+ Log charge": "+ Registrar cargo",
  "Charge to {n}": "Cargo a {n}",
  "This adds to the balance — for when the card gets used again.": "Esto suma al saldo — para cuando la tarjeta se use de nuevo.",
  "Log charge": "Registrar cargo",
  "{a} charge logged": "Cargo de {a} registrado",
  "Charge": "Cargo",
  "Interest": "Interés",
  "Interest posted on {n} debts": "Interés registrado en {n} deudas",
  "Interest posted on {n} debt": "Interés registrado en {n} deuda",
  // realistic invest boost (v0.18)
  "From {d}, {a}/mo frees up from debt payments. Realistically, some of that goes to other bills or lifestyle — not all of it becomes investing power.": "A partir de {d}, {a}/mes queda libre de los pagos de deuda. En la práctica, parte de eso va a otras cuentas o estilo de vida — no todo se convierte en poder de inversión.",
  "How much will you realistically redirect to investing?": "¿Cuánto vas a redirigir realmente a invertir?",
  "Defaults to 60% — a buffer for the bills that don't disappear just because the debt did.": "Por defecto 60% — un margen para las cuentas que no desaparecen solo porque la deuda desapareció.",
  "Redirecting {pct}% ({b}/mo) changes your projection from {p1} to {p2} at age {age}.": "Redirigir el {pct}% ({b}/mes) cambia tu proyección de {p1} a {p2} a los {age} años.",
  // spend cash-flow + donut (v0.18)
  "Cash flow": "Flujo de caja",
  "Income": "Ingresos",
  "Out (spend + debt payments)": "Salidas (gastos + pagos de deuda)",
  "Left": "Sobrante",
  "Income shown is your current setting — it isn't tracked per past month.": "El ingreso mostrado es tu configuración actual — no se registra por mes pasado.",
  // custom categories + auto minimum (v0.19)
  "Auto · 3%": "Auto · 3%",
  "Use auto 3%": "Usar auto 3%",
  "Card issuers usually set the minimum at 1–3% of your balance. We default to 3% and keep it updated as your balance falls — edit it to match your statement and it stays fixed.": "Los emisores suelen fijar el mínimo entre 1–3% del saldo. Usamos 3% por defecto y lo mantenemos actualizado a medida que el saldo baja — edítalo para que coincida con tu estado de cuenta y quedará fijo.",
  "Category added": "Categoría añadida",
  "Category deleted": "Categoría eliminada",
  "Delete {n}? Past expenses stay, shown under Other.": "¿Eliminar {n}? Los gastos anteriores se mantienen, mostrados en Otros.",
  "+ Add category": "+ Añadir categoría",
  "New category": "Nueva categoría",
  "e.g. Pets, Kids school": "ej.: Mascotas, Colegio de los niños",
  "Deleting a category later won't erase its expenses — they move to Other.": "Eliminar una categoría después no borra sus gastos — pasan a Otros.",
  "Save category": "Guardar categoría",
  "Edit category": "Editar categoría",
  "Category updated": "Categoría actualizada",
  "Reset to default": "Restablecer por defecto",
  // never-converges guard + auto-min toggle fix (v0.20.1)
  "Never": "Nunca",
  "Payments don't cover the interest": "Los pagos no cubren los intereses",
  "Not on this plan": "No con este plan",
  "⚠️ Neither strategy clears this debt yet": "⚠️ Ninguna estrategia salda esta deuda todavía",
  "⚠️ Your current payments never clear this debt, so there's no freed-up amount to project yet — fix the plan in Debts first.": "⚠️ Tus pagos actuales nunca saldan esta deuda, así que todavía no hay un monto liberado para proyectar — ajusta el plan en Deudas primero.",
  "Tap to enter your own amount": "Toca para ingresar tu propio monto",
  "That's {apr}% per year — common for revolving credit in some countries. Double-check it's not the monthly rate.": "Eso es {apr}% anual — común en crédito rotativo en algunos países. Verifica que no sea la tasa mensual.",
  // bill paid-ahead marker (v0.21)
  "Mark as paid ahead": "Marcar como pagado por adelantado",
  "Mark as not paid ahead": "Desmarcar pago adelantado",
  "paid ahead": "pagado adelantado",
  // sign-in at entry (v0.34)
  "I already have an account — sign in": "Ya tengo una cuenta — iniciar sesión",
  "Signing in first restores your cloud backup instead of starting over.": "Iniciar sesión primero restaura tu copia en la nube en vez de empezar de cero.",
  "Cloud backup": "Copia en la nube",
  "Checking your account…": "Revisando tu cuenta…",
  "Nothing backed up to your account yet.": "Todavía no hay nada guardado en tu cuenta.",
  "Last backup: {d}": "Última copia: {d}",
  "that's {n} days ago": "eso fue hace {n} días",
  "The server didn't keep that save. Nothing was lost here — keep a file backup and report this.": "El servidor no conservó ese guardado. Aquí no se perdió nada — haz una copia en archivo y repórtalo.",
  "Back up now": "Hacer copia ahora",
  "Backing up…": "Haciendo copia…",
  "Backed up to your account ✓": "Copia guardada en tu cuenta ✓",
  "Still checking your account — try again in a moment.": "Aún revisando tu cuenta — inténtalo de nuevo en un momento.",
  "Couldn't back up. Check your connection and try again.": "No se pudo hacer la copia. Revisa tu conexión e inténtalo de nuevo.",
  "Aurio backs up on its own a moment after every change — this is here for when you want to be certain.": "Aurio hace una copia por su cuenta poco después de cada cambio — esto está aquí para cuando quieras estar seguro.",
  "Which one should Aurio keep?": "¿Cuál debe conservar Aurio?",
  "You're signed in and there's a backup on your account, but this device also has data you entered before signing in. They can't be merged — pick one. Nothing is saved over until you choose.": "Has iniciado sesión y hay una copia en tu cuenta, pero este dispositivo también tiene datos que ingresaste antes de entrar. No se pueden combinar — elige una. Nada se sobrescribe hasta que elijas.",
  "Your cloud backup": "Tu copia en la nube",
  "What's on this device": "Lo que hay en este dispositivo",
  "Saved {d} · {n} debts · {a}": "Guardado el {d} · {n} deudas · {a}",
  "{n} debts · {a}": "{n} deudas · {a}",
  "Restore my backup": "Restaurar mi copia",
  "Keep this device's data": "Conservar los datos de este dispositivo",
  "Either way the other copy is kept in your account's version history, so this is recoverable.": "En cualquier caso la otra copia queda en el historial de versiones de tu cuenta, así que es recuperable.",
  // grouped repeat occurrences (v0.33)
  "×{n}": "×{n}",
  "Show each date": "Ver cada fecha",
  "Hide dates": "Ocultar fechas",
  // statement cycles, due dates & dated entries (v0.24)
  "Payment due on (day of month)": "Vencimiento (día del mes)",
  "e.g. 15": "ej.: 15",
  "The day your payment is due each month (1–28). Aurio uses it to work out your real statement cycle, so interest is charged on what you actually carried — and to remind you before it's due.": "El día en que vence tu pago cada mes (1–28). Aurio lo usa para calcular tu ciclo de facturación real, así el interés se cobra sobre lo que realmente arrastraste — y para avisarte antes del vencimiento.",
  "Advanced: set the statement closing day": "Avanzado: definir el día de cierre del estado de cuenta",
  "Statement closes on (day of month)": "El estado de cuenta cierra el (día del mes)",
  "The day your billing period ends — interest is worked out on the average balance you carried across that period. We assume it's {n} days before your due date; change it to match your statement.": "El día en que termina tu período de facturación — el interés se calcula sobre el saldo promedio que arrastraste en ese período. Asumimos {n} días antes del vencimiento; ajústalo según tu estado de cuenta.",
  "Date paid": "Fecha del pago",
  "When the payment actually left your account — the date decides how much interest this cycle costs. You can only date back to the start of the current statement cycle.": "Cuándo salió realmente el pago de tu cuenta — la fecha determina cuánto interés cuesta este ciclo. Solo puedes retroceder hasta el inicio del ciclo de facturación actual.",
  "Date charged": "Fecha del cargo",
  "Payment due today": "El pago vence hoy",
  "Payment due tomorrow": "El pago vence mañana",
  "Payment due in {d} days": "El pago vence en {d} días",
  "✓ Minimum paid this cycle": "✓ Mínimo pagado en este ciclo",
  // debt end date + save/grow tab (v0.22)
  "Ends on (optional)": "Termina el (opcional)",
  "For a fixed-term loan (e.g. a car payment) — the Save tab's money map uses this to show you when that payment frees up. Leave blank for revolving credit like cards.": "Para un préstamo a plazo fijo (ej.: pago del auto) — el mapa financiero de la pestaña Ahorrar usa esto para mostrarte cuándo se libera ese pago. Déjalo en blanco para crédito rotativo como tarjetas.",
  "ends {d}": "termina {d}",
  "Grow": "Crecer",
  "You can save": "Puedes ahorrar",
  "mo": "mes",
  "Savings rate {r}%": "Tasa de ahorro {r}%",
  "This year's money map": "Mapa financiero de este año",
  "saveable": "ahorrable",
  "committed": "comprometido",
  "≈ {a} you could save this year.": "≈ {a} que podrías ahorrar este año.",
  "→ From {d}: +{a}/mo ({r})": "→ Desde {d}: +{a}/mes ({r})",
  "{n} ends": "{n} termina",
  "you're debt-free": "estás libre de deudas",
  "Savings goals": "Metas de ahorro",
  "by {d}": "para {d}",
  "+ Add money": "+ Añadir dinero",
  "+ New goal": "+ Nueva meta",
  "💰 You have {a} saveable this month.": "💰 Tienes {a} ahorrable este mes.",
  "Add it to {n} →": "Añadir a {n} →",
  "Add to {n}": "Añadir a {n}",
  "Add money": "Añadir dinero",
  "Edit goal": "Editar meta",
  "New goal": "Nueva meta",
  "e.g. Emergency fund, House deposit": "ej.: Fondo de emergencia, Pie de la casa",
  "Target amount": "Monto objetivo",
  "Target date (optional)": "Fecha objetivo (opcional)",
  "Save goal": "Guardar meta",
  "Goal updated": "Meta actualizada",
  "Goal added": "Meta añadida",
  "Delete {n}? This can't be undone.": "¿Eliminar {n}? Esto no se puede deshacer.",
  "Goal deleted": "Meta eliminada",
  "{a} added to {n} 🎉": "{a} añadido a {n} 🎉",
};

function makeT(lang) {
  const dict = lang === "pt-BR" ? PT : lang === "es" ? ES : lang === "en-US" ? US : null;
  return (key, vars) => {
    let s = (dict && dict[key]) || key;
    if (vars) for (const k of Object.keys(vars)) s = s.split(`{${k}}`).join(vars[k]);
    return s;
  };
}

/* ═══ data ═══ */

const CATEGORIES = [
  { id: "groceries", label: "Groceries", emoji: "🛒" },
  { id: "eating", label: "Eating out", emoji: "🍜" },
  { id: "transport", label: "Fuel & transport", emoji: "⛽" },
  { id: "rent", label: "Rent", emoji: "🏠" },
  { id: "utilities", label: "Utilities & phone", emoji: "💡" },
  { id: "subs", label: "Subscriptions", emoji: "📺" },
  { id: "insurance", label: "Insurance", emoji: "🛡️" },
  { id: "family", label: "Family overseas", emoji: "💸" },
  { id: "fitness", label: "Fitness & supps", emoji: "🏋️" },
  { id: "personal", label: "Personal", emoji: "🧴" },
  { id: "trip", label: "Trip fund", emoji: "✈️" },
  { id: "other", label: "Other", emoji: "📦" },
];

/* Built-ins (with any user rename/re-emoji override applied) + user-added
   categories (Premium to add/delete; renaming is free for everyone — it's
   personalization of what's already there, not more categories). A custom
   category's `label`, and an override's `label`, IS the user-entered name
   (not a translation key) — t() harmlessly returns it unchanged since it
   won't match any PT/ES dict entry, so every existing t(c.label) call site
   works for all three cases without a branch. */
/* Sentinel for a charge that isn't consumption — a balance transfer, cash
   advance or annual fee. It still moves the balance, it just has no business
   in a spending category. */
/* "Repeats: once" reads as a contradiction, so the one-off cadence shows as
   "one-off" while the stored value stays `once`. */
const freqLabel = (t, f) => t(f === "once" ? "one-off" : f);
const NO_CATEGORY = "__none__";
const categoriesOf = (state) => {
  const overrides = state.categoryOverrides || {};
  const builtins = CATEGORIES.map(c => (overrides[c.id] ? { ...c, ...overrides[c.id] } : c));
  return [...builtins, ...(state.customCategories || [])];
};

/* One definition of "what the living budget adds up to", so the headline and
   the rows can never disagree. See budgetTotal() in money.js for why it is
   keyed off the category list rather than the budgets object. */
const budgetTotalOf = (state) => budgetTotal(state.budgets, categoriesOf(state).map(c => c.id));

const RECURRING_HINTS = { utilities: "power, gas, water, phone", insurance: "health, car, income protection", subs: "Netflix, Spotify, iCloud", family: "monthly transfer to family" };

const DEBT_TYPES = [
  { id: "card", label: "Credit card" },
  { id: "personal", label: "Personal loan" },
  { id: "car", label: "Car loan" },
  { id: "other", label: "Other" },
];


// NZ market benchmarks (editable; verified July 2026)
const RATE_BENCH = {
  card: { good: 13.9, tip: "Low-rate NZ cards sit around 12.9–13.9%, and 0% balance-transfer offers exist." },
  personal: { good: 11, tip: "Consolidation loans run ~11–13% for good credit; challenger lenders from ~8%." },
  car: { good: 9, tip: "NZ car loans start around 7.25% for decent credit profiles." },
  other: null,
};

/* Educational market guides — factual instrument types per country.
   No product recommendations. en used for both English variants. */
const GUIDES = {
  en: [
    { flag: "🇳🇿", name: "New Zealand", body: "Common income routes: KiwiSaver (employer + govt contributions), PIE funds (tax capped at 28%), and NZX-listed index/dividend ETFs (e.g. broad NZX 50 trackers). NZ generally has no capital gains tax on long-term share investing, but foreign holdings over NZ$50k cost basis fall under FIF tax rules." },
    { flag: "🇦🇺", name: "Australia", body: "Superannuation is the main retirement vehicle (concessional tax). Outside super, ASX index and dividend ETFs are popular; Australian dividends often carry franking credits that offset tax on the income." },
    { flag: "🇺🇸", name: "United States", body: "The world's deepest market: broad index ETFs (S&P 500 / total-market), dividend-growth ETFs, and REITs (required to distribute ~90% of income). Non-resident investors typically face 15% dividend withholding under the NZ/AU/BR tax treaties via a W-8BEN form." },
    { flag: "🇧🇷", name: "Brazil", body: "Income-focused Brazilians commonly use Tesouro Direto (government bonds, incl. inflation-linked IPCA+), FIIs (listed real-estate funds paying monthly distributions, historically income-tax-exempt for individuals — rules can change), and dividend-paying ações. Brazilian nominal returns are high, but so are inflation and currency volatility." },
  ],
  "pt-BR": [
    { flag: "🇳🇿", name: "Nova Zelândia", body: "Caminhos comuns de renda: KiwiSaver (contribuições do empregador + governo), fundos PIE (imposto limitado a 28%) e ETFs de índice/dividendos listados na NZX (ex.: NZX 50). A NZ em geral não cobra imposto sobre ganho de capital em ações de longo prazo, mas investimentos estrangeiros acima de NZ$50 mil entram nas regras FIF." },
    { flag: "🇦🇺", name: "Austrália", body: "A Superannuation é o principal veículo de aposentadoria (tributação favorecida). Fora dela, ETFs de índice e dividendos da ASX são populares; dividendos australianos costumam trazer franking credits que abatem o imposto sobre a renda." },
    { flag: "🇺🇸", name: "Estados Unidos", body: "O mercado mais profundo do mundo: ETFs de índice amplo (S&P 500 / total market), ETFs de crescimento de dividendos e REITs (obrigados a distribuir ~90% da renda). Investidores não residentes normalmente pagam 15% de retenção sobre dividendos pelos tratados fiscais, via formulário W-8BEN." },
    { flag: "🇧🇷", name: "Brasil", body: "Brasileiros focados em renda usam muito o Tesouro Direto (títulos públicos, incl. IPCA+), FIIs (fundos imobiliários com distribuições mensais, historicamente isentos de IR para pessoa física — regras podem mudar) e ações pagadoras de dividendos. Os retornos nominais no Brasil são altos, mas inflação e volatilidade cambial também." },
  ],
  es: [
    { flag: "🇳🇿", name: "Nueva Zelanda", body: "Rutas comunes de ingreso: KiwiSaver (aportes del empleador + gobierno), fondos PIE (impuesto limitado al 28%) y ETFs de índice/dividendos listados en la NZX (ej.: NZX 50). NZ en general no cobra impuesto a las ganancias de capital en acciones a largo plazo, pero las inversiones extranjeras sobre NZ$50 mil entran en las reglas FIF." },
    { flag: "🇦🇺", name: "Australia", body: "La Superannuation es el principal vehículo de retiro (tributación favorecida). Fuera de ella, los ETFs de índice y dividendos de la ASX son populares; los dividendos australianos suelen traer franking credits que reducen el impuesto sobre ese ingreso." },
    { flag: "🇺🇸", name: "Estados Unidos", body: "El mercado más profundo del mundo: ETFs de índice amplio (S&P 500 / mercado total), ETFs de crecimiento de dividendos y REITs (obligados a distribuir ~90% de su renta). Los inversionistas no residentes normalmente pagan 15% de retención sobre dividendos según los tratados fiscales, vía formulario W-8BEN." },
    { flag: "🇧🇷", name: "Brasil", body: "Los brasileños enfocados en renta usan mucho el Tesouro Direto (bonos públicos, incl. IPCA+), los FIIs (fondos inmobiliarios con distribuciones mensuales, históricamente exentos de impuesto para personas físicas — las reglas pueden cambiar) y acciones que pagan dividendos. Los retornos nominales en Brasil son altos, pero la inflación y la volatilidad cambiaria también." },
  ],
};

/* First-home scheme guidance — factual, no product recommendations, same
   standard as GUIDES above. Launches with NZ + AU (the beachhead: Brazilian
   and Latino communities living IN those countries, buying homes there — not
   in Brazil). Country is picked from state.currency (NZD/AUD), same gating
   RATE_BENCH already uses.

   Each entry is `warning: true` when it corrects a widely-held but wrong
   belief rather than describing something that still exists — the NZ First
   Home Grant is the clearest example: scrapped 22 May 2024 with no
   replacement, yet still listed on many lenders' own sites.

   `verified` is a quiet footnote, not a substitute for review: scheme rules
   change often enough (the AU income cap was removed 1 Oct 2025; the grant
   vanished entirely) that stale content should be visible, not silent. */
const HOME_VERIFIED = "2026-08";
const HOME_SCHEMES = {
  en: {
    NZ: [
      { emoji: "🏠", title: "KiwiSaver first-home withdrawal", calc: "kiwisaver",
        body: "Available once you've been a KiwiSaver member for 3+ years, for a first home you'll live in. You can withdraw your full balance except $1,000, which must stay in the account. No maximum withdrawal. Apply through your scheme provider roughly 4 weeks before settlement so the funds arrive in time." },
      { emoji: "🏦", title: "First Home Loan (Kāinga Ora)",
        body: "Lets an eligible buyer purchase with as little as a 5% deposit instead of the usual 20%, without paying the interest-rate premium that normally comes with a low-deposit loan. Income caps and regional house-price caps apply, and you apply through a participating lender, not directly." },
      { emoji: "⚠️", title: "First Home Grant — no longer exists", warning: true,
        body: "The First Home Grant was discontinued on 22 May 2024 and has no replacement. It still turns up in older articles and even some lenders' own pages — don't budget for it. The KiwiSaver withdrawal and the First Home Loan above are the support that remains." },
    ],
    AU: [
      { emoji: "💰", title: "First Home Super Saver Scheme (FHSS)", calc: "fhss",
        body: "Lets you save part of a deposit inside super, where contributions are taxed at a concessional 15% instead of your marginal rate. Up to $15,000 of voluntary contributions per financial year counts, capped at $50,000 lifetime. Each eligible person (partners, siblings, friends) can use their own FHSS toward the same property." },
      { emoji: "🏦", title: "First Home Guarantee", calc: "fhbg5",
        body: "Lets an eligible buyer purchase with a 5% deposit and no Lenders Mortgage Insurance, which typically saves $20,000–$40,000. The income cap was removed on 1 October 2025 — any citizen or permanent resident buying to live in is eligible, subject to place caps and property-price limits." },
      { emoji: "🏛️", title: "Help to Buy",
        body: "A shared-equity scheme that opened 5 December 2025: the government can contribute up to 40% of the price of a new home or 30% of an existing one, so you need a smaller deposit (as low as 2%) and a smaller mortgage. Income caps apply — $100,000 for an individual, $160,000 for joint applicants or a single parent." },
    ],
  },
  "pt-BR": {
    NZ: [
      { emoji: "🏠", title: "Saque do KiwiSaver para a primeira casa", calc: "kiwisaver",
        body: "Disponível depois de 3+ anos como membro do KiwiSaver, para uma primeira casa onde você vai morar. Você pode sacar todo o saldo, exceto $1.000, que precisa ficar na conta. Sem limite máximo de saque. Peça à sua administradora cerca de 4 semanas antes da liquidação para o dinheiro chegar a tempo." },
      { emoji: "🏦", title: "First Home Loan (Kāinga Ora)",
        body: "Permite comprar com entrada de apenas 5% em vez dos 20% usuais, sem pagar o adicional de juros que normalmente vem com um empréstimo de entrada baixa. Há limites de renda e de preço do imóvel por região, e o pedido é feito por um banco participante, não diretamente." },
      { emoji: "⚠️", title: "First Home Grant — não existe mais", warning: true,
        body: "O First Home Grant foi encerrado em 22 de maio de 2024 e não tem substituto. Ainda aparece em artigos antigos e até em páginas de alguns bancos — não conte com ele no seu planejamento. O saque do KiwiSaver e o First Home Loan acima são o apoio que ainda existe." },
    ],
    AU: [
      { emoji: "💰", title: "First Home Super Saver Scheme (FHSS)", calc: "fhss",
        body: "Permite guardar parte da entrada dentro do fundo de aposentadoria (super), onde as contribuições são tributadas a uma alíquota reduzida de 15% em vez da sua alíquota normal. Até $15.000 de contribuições voluntárias por ano fiscal contam, com limite vitalício de $50.000. Cada pessoa elegível (cônjuge, irmãos, amigos) pode usar seu próprio FHSS no mesmo imóvel." },
      { emoji: "🏦", title: "First Home Guarantee", calc: "fhbg5",
        body: "Permite comprar com entrada de 5% e sem o seguro Lenders Mortgage Insurance, o que costuma economizar entre $20.000 e $40.000. O limite de renda foi removido em 1º de outubro de 2025 — qualquer cidadão ou residente permanente comprando para morar é elegível, sujeito a limites de vagas e de preço do imóvel." },
      { emoji: "🏛️", title: "Help to Buy",
        body: "Um programa de equity compartilhada que começou em 5 de dezembro de 2025: o governo pode contribuir com até 40% do preço de um imóvel novo ou 30% de um usado, reduzindo a entrada necessária (a partir de 2%) e o financiamento. Há limites de renda — $100.000 para uma pessoa, $160.000 para candidatos em conjunto ou pai/mãe solo." },
    ],
  },
  es: {
    NZ: [
      { emoji: "🏠", title: "Retiro de KiwiSaver para la primera casa", calc: "kiwisaver",
        body: "Disponible tras 3+ años como miembro de KiwiSaver, para una primera vivienda en la que vayas a vivir. Puedes retirar todo tu saldo excepto $1.000, que deben quedarse en la cuenta. Sin retiro máximo. Solicítalo a tu proveedor del esquema unas 4 semanas antes de la liquidación para que el dinero llegue a tiempo." },
      { emoji: "🏦", title: "First Home Loan (Kāinga Ora)",
        body: "Permite comprar con un depósito de solo 5% en vez del 20% habitual, sin pagar el recargo de interés que normalmente viene con un préstamo de depósito bajo. Aplican topes de ingresos y de precio de vivienda por región, y se solicita a través de un prestamista participante, no directamente." },
      { emoji: "⚠️", title: "First Home Grant — ya no existe", warning: true,
        body: "El First Home Grant se eliminó el 22 de mayo de 2024 y no tiene reemplazo. Todavía aparece en artículos antiguos e incluso en páginas de algunos bancos — no lo incluyas en tu plan. El retiro de KiwiSaver y el First Home Loan de arriba son el apoyo que sigue vigente." },
    ],
    AU: [
      { emoji: "💰", title: "First Home Super Saver Scheme (FHSS)", calc: "fhss",
        body: "Permite ahorrar parte del depósito dentro del fondo de jubilación (super), donde las contribuciones se gravan a una tasa concesional del 15% en lugar de tu tasa marginal. Hasta $15.000 de contribuciones voluntarias por año fiscal cuentan, con un tope vitalicio de $50.000. Cada persona elegible (pareja, hermanos, amigos) puede usar su propio FHSS para la misma propiedad." },
      { emoji: "🏦", title: "First Home Guarantee", calc: "fhbg5",
        body: "Permite comprar con un depósito del 5% y sin el seguro Lenders Mortgage Insurance, lo que suele ahorrar entre $20.000 y $40.000. El tope de ingresos se eliminó el 1 de octubre de 2025 — cualquier ciudadano o residente permanente que compre para vivir es elegible, sujeto a cupos y topes de precio de propiedad." },
      { emoji: "🏛️", title: "Help to Buy",
        body: "Un esquema de propiedad compartida que abrió el 5 de diciembre de 2025: el gobierno puede aportar hasta el 40% del precio de una vivienda nueva o el 30% de una existente, reduciendo el depósito necesario (desde 2%) y la hipoteca. Aplican topes de ingresos — $100.000 para una persona, $160.000 para solicitantes conjuntos o un padre/madre soltero." },
    ],
  },
};

const DEFAULT_STATE = {
  lang: "en-GB",
  onboarded: false,
  income: 7500,
  incomeFreq: "monthly",
  /* Demo data for the pre-onboarding preview. `lastInterestDate` /
     `lastStatementBalance` are deliberately absent — hydrate() stamps them to
     the current cycle on first load, so a fresh install can never be charged
     backdated interest from a hardcoded date. */
  debts: [
    { id: "d1", name: "Credit Card A", balance: 15000, apr: 22.95, minPayment: 450, limit: 16000, type: "card", minPaymentAuto: true, dueDay: 15 },
    { id: "d2", name: "Credit Card B", balance: 13000, apr: 20.95, minPayment: 390, limit: 15000, type: "card", minPaymentAuto: true, dueDay: 5 },
    { id: "d3", name: "Credit Card C", balance: 8000, apr: 19.95, minPayment: 240, limit: 10000, type: "card", minPaymentAuto: true, dueDay: 25 },
    { id: "d4", name: "Car Loan", balance: 2400, apr: 11.5, minPayment: 480, limit: null, type: "car", endDate: "2027-01-01", dueDay: 1 },
  ],
  startingDebt: 38400,
  startDate: "2026-07-01",
  extraPayment: 600,
  strategy: "avalanche",
  budgets: { groceries: 600, eating: 300, transport: 250, rent: 2400, utilities: 300, subs: 80, insurance: 250, family: 500, fitness: 150, personal: 150, trip: 400, other: 200 },
  customCategories: [],
  categoryOverrides: {},
  expenses: [],
  recurring: [],
  payments: [],
  /* `inflation` restates the retirement projection in today's money. Without
     it a 24-year nominal figure is compared against a nominal target, which
     overstates what the money actually buys by roughly half. */
  invest: { age: 36, retireAge: 60, targetMonthly: 4000, current: 0, monthly: 300, expReturn: 7, withdrawRate: 4, redirectPct: 60, inflation: 2.5 },
  payday: 20,
  reminders: { enabled: false, leadDays: 3 },
  billsPaidAhead: {},
  savingsGoals: [],
  /* First-home planner (Grow tab). Opt-in — `active` stays false until the
     user turns it on, since not everyone is saving for a house. Country-
     specific scheme guidance is gated on state.currency (NZD → NZ, AUD → AU),
     the same pattern RATE_BENCH already uses. */
  home: { active: false, price: 0, depositPct: 20, cash: 0, scheme: 0 },
  /* The only figure the emergency fund needs the user to supply — how much is
     already set aside. Its TARGET (3 months of essentials) is computed from
     state already on the Budget/Debts tabs; see emergencyTarget() in money.js. */
  emergencyFund: { saved: 0 },
  /* CSV import. `merchantRules` is the learning: whenever the user corrects a
     row's category in the import preview, the merchant key is remembered so
     the next import gets it right without a bank connection. `lastImport`
     powers "Undo this import" — every imported expense also carries the
     matching `importId`, so undo is an exact filter, never a guess. */
  merchantRules: {},
  lastImport: null,
  /* Priority order for the Grow tab's allocation waterfall — destination ids
     ("goal:<id>", "home", "invest"). Anything missing appends at the end, so
     adding a goal never scrambles an existing plan. */
  allocationOrder: [],
  seenMilestones: [],
  seenSliderHint: false,
  /* The self-serve trial offer is shown once and dismissed for good — see
     TrialOffer. Starting the trial also sets this, so it never re-appears. */
  seenTrialOffer: false,
  currency: "NZD",
};

const STORAGE_KEY = "aurio-state-v1";
/* Rolling backup refreshed after every successful load — the recovery
   source when the main key is missing or unreadable. */
const BACKUP_KEY = "aurio-state-v1.backup";
/* Which bill reminders have already fired ({recurringId:dueDate} → ts). */
const NOTIFIED_KEY = "aurio-notified-v1";

/* ═══ helpers ═══ */

/* Currency: symbol + locale used by every money string in the app.
   `fmt` is called in ~100 places, so instead of threading currency
   through every component, `applyCurrency` sets a module-level current
   currency — called on state load and whenever the setting changes
   (always immediately before React re-renders, so it stays in sync). */
const CURRENCIES = [
  { code: "NZD", symbol: "$", locale: "en-NZ" },
  { code: "AUD", symbol: "$", locale: "en-AU" },
  { code: "USD", symbol: "$", locale: "en-US" },
  { code: "BRL", symbol: "R$", locale: "pt-BR" },
  { code: "EUR", symbol: "€", locale: "de-DE" },
  { code: "GBP", symbol: "£", locale: "en-GB" },
  { code: "MXN", symbol: "$", locale: "es-MX" },
  { code: "ARS", symbol: "$", locale: "es-AR" },
  { code: "COP", symbol: "$", locale: "es-CO" },
  { code: "CLP", symbol: "$", locale: "es-CL" },
];
let CUR = CURRENCIES[0];
const applyCurrency = (code) => { CUR = CURRENCIES.find(c => c.code === code) || CURRENCIES[0]; };
/* Backstop, not the primary fix: a runaway-debt bug should be caught and
   explained at the source (see simulate()'s neverPaysOff), but this ceiling
   means even an un-flagged bad number renders as "—" instead of a
   40-digit string that blows out the layout. */
const FMT_CEILING = 1e15;
const fmt = (n, dp = 0) => {
  const v = Number(n || 0);
  if (!Number.isFinite(v) || Math.abs(v) >= FMT_CEILING) return "—";
  return CUR.symbol + v.toLocaleString(CUR.locale, { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

/* Cloud backup: mirror the full state blob to Supabase for signed-in
   users. Fire-and-forget — local storage stays the source of truth and
   errors (offline, migration not applied yet) never block the app. */
let cloudPushWarned = false;
async function pushCloudState(user, payload) {
  if (!user || !supabase) return { ok: false, error: "not signed in" };
  try {
    /* Read the row back and check it actually holds what we just sent.
       "No error" is NOT proof of a write: a BEFORE UPDATE trigger returning
       OLD silently discards the incoming row while Postgres still reports
       success and 1 row affected. That is exactly what happened in
       production — 12 days of backups reported as saved while the server
       kept a stale copy, and the manual button cheerfully confirmed it.
       Confirmation has to come from what the server ended up holding. */
    const { data, error } = await supabase.from("user_state").upsert({
      user_id: user.id,
      state: payload,
      saved_at: payload._savedAt,
    }).select("saved_at").maybeSingle();
    if (error) throw error;
    /* Compare INSTANTS, not strings. The client sends an ISO string ending in
       "Z"; PostgREST renders the identical timestamptz as "+00:00". Same
       moment, two spellings — so the string compare this used to do failed on
       every SUCCESSFUL write and told people "the server didn't keep that
       save" about saves that had landed perfectly. A guard that cries wolf on
       every write is worse than no guard: it trains you to ignore it, and the
       real thing it watches for is silent data loss.
       It still catches that real thing. The v0.35.1 trigger bug left the
       server holding a genuinely DIFFERENT instant, which parses to a
       different number and is still rejected here. */
    const persisted = data?.saved_at ? Date.parse(data.saved_at) : NaN;
    const intended = Date.parse(payload._savedAt);
    if (!Number.isFinite(persisted) || !Number.isFinite(intended) || persisted !== intended) {
      return { ok: false, error: "write-not-persisted", stale: data?.saved_at || null };
    }
    /* Report what the SERVER holds, not what we sent — the display is a claim
       about the other end, and that's the whole point of reading it back. */
    return { ok: true, savedAt: data.saved_at };
  } catch (err) {
    if (!cloudPushWarned) {
      cloudPushWarned = true;
      console.warn("[Aurio] cloud backup failed", err?.message || err);
    }
    /* The automatic path still ignores this — a failed background push must
       never interrupt someone mid-edit. The manual button reports it, because
       there the whole point is finding out whether it worked. */
    return { ok: false, error: err?.message || String(err) };
  }
}

/* What the SERVER actually holds, not what this device believes it sent.
   "Am I really backed up?" can only be answered by the other end. */
async function fetchCloudSavedAt(user) {
  if (!user || !supabase) return { ok: false };
  try {
    const { data, error } = await supabase
      .from("user_state").select("saved_at").eq("user_id", user.id).maybeSingle();
    if (error) throw error;
    return { ok: true, savedAt: data?.saved_at || null };
  } catch {
    return { ok: false };
  }
}

/* Bill reminders: browser notifications for recurring items AND dated debt
   minimums due within `leadDays`, at most once per item per due date. Runs on
   app open — push while the app is closed needs a push server (see ROADMAP).
   A bill is skipped when ticked "paid ahead"; a debt minimum is skipped when
   the ledger already shows it paid this cycle. */
async function notifyDueBills(state, t) {
  if (typeof Notification === "undefined") return;
  if (!state.reminders?.enabled || Notification.permission !== "granted") return;
  const lead = state.reminders.leadDays ?? 3;
  const paidAhead = state.billsPaidAhead || {};
  const due = [
    ...upcomingBills(state, lead).filter(b => !paidAhead[`${b.id}:${b.nextDue}`]),
    ...upcomingDebtPayments(state, lead).filter(p => !p.covered && p.amount > 0),
  ].sort((a, b) => a.days - b.days);
  if (!due.length) return;

  let log = {};
  try {
    const r = await window.storage.get(NOTIFIED_KEY);
    if (r?.value) log = JSON.parse(r.value);
  } catch { /* start a fresh log */ }
  const fresh = due.filter((b) => !log[`${b.id}:${b.nextDue}`]);
  if (!fresh.length) return;
  const cutoff = Date.now() - 45 * 86400000;
  for (const k of Object.keys(log)) if (log[k] < cutoff) delete log[k];
  for (const b of fresh) log[`${b.id}:${b.nextDue}`] = Date.now();
  try { await window.storage.set(NOTIFIED_KEY, JSON.stringify(log)); } catch { /* non-fatal */ }

  const show = async (body) => {
    const opts = { body, icon: "/icons/icon-192.png", badge: "/icons/icon-192.png", tag: "solvente-bills" };
    try {
      const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : null;
      if (reg?.showNotification) reg.showNotification("Aurio", opts);
      else new Notification("Aurio", opts);
    } catch (err) {
      console.warn("[Aurio] notification failed", err);
    }
  };
  if (fresh.length === 1) {
    const b = fresh[0];
    show(
      b.days === 0
        ? t("{n} is due today — {a}", { n: b.name, a: fmt(b.amount, 2) })
        : t("{n} is due in {d} days — {a}", { n: b.name, d: b.days, a: fmt(b.amount, 2) })
    );
  } else {
    show(t("{c} payments due in the next {d} days — {a} total", {
      c: fresh.length, d: lead, a: fmt(fresh.reduce((s, b) => s + b.amount, 0)),
    }));
  }
}


/* "6 years 4 months" (localized, skipping zero parts). */
function fmtTerm(months, t) {
  const y = Math.floor(months / 12);
  const m = months % 12;
  const parts = [];
  if (y > 0) parts.push(t(y === 1 ? "{n} year" : "{n} years", { n: y }));
  if (m > 0 || y === 0) parts.push(t(m === 1 ? "{n} month" : "{n} months", { n: m }));
  return parts.join(" ");
}

/* CSV export: one sectioned, accountant-ready financial statement \u2014
   income, debts (with minimum-only true cost), budgets, recurring bills,
   transactions, and a plan summary. `monthKey` (YYYY-MM) limits only the
   TRANSACTIONS section; everything else is always the full picture.
   Leading BOM so Excel decodes UTF-8 (accented notes) correctly. */
function buildCsv(state, t, monthKey) {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [];
  const push = (row) => lines.push(row.map(esc).join(","));
  const blank = () => lines.push("");

  const monthlyIncome = monthlyIncomeOf(state);
  const totalDebt = totalOwed(state.debts);
  const totalBudget = budgetTotalOf(state);
  const sim = simulate(state.debts, state.extraPayment, state.strategy);
  const simMin = simulate(state.debts, 0, state.strategy);
  const nowKey = monthKeyOf(new Date());
  const spentThisMonth = (id) =>
    state.expenses.filter(e => e.category === id && e.date?.slice(0, 7) === nowKey).reduce((s, e) => s + e.amount, 0);

  push([t("Aurio financial export"), todayISO(), monthKey || t("all data")]);
  blank();

  push([t("Income")]);
  push([t("frequency"), t("amount"), t("monthly equivalent"), t("Currency")]);
  push([t(state.incomeFreq), Number(state.income || 0).toFixed(2), monthlyIncome.toFixed(2), state.currency || "NZD"]);
  blank();

  push([t("Debts")]);
  push([t("Name"), t("Type"), t("balance"), t("APR %"), t("min payment / month"), t("credit limit"), t("due day"), t("statement day"), t("interest posted through"), t("payoff at minimum only"), t("interest at minimum only")]);
  for (const d of state.debts) {
    const proj = minOnlyProjection(d);
    push([
      d.name, t(DEBT_TYPES.find(x => x.id === d.type)?.label || "Debt"),
      Number(d.balance || 0).toFixed(2), d.apr ?? 0, Number(d.minPayment || 0).toFixed(2), d.limit ?? "",
      d.dueDay ?? "", d.dueDay != null ? statementDayOf(d) : "", d.lastInterestDate || "",
      proj ? (proj.months === 0 ? "" : fmtTerm(proj.months, t)) : (d.balance > 0 ? t("never at this minimum") : ""),
      proj && proj.months > 0 ? proj.interest.toFixed(2) : "",
    ]);
  }
  blank();

  const cats = categoriesOf(state);
  push([t("Budgets")]);
  push([t("category"), t("monthly budget"), t("spent this month")]);
  for (const c of cats) push([t(c.label), (state.budgets[c.id] || 0).toFixed(2), spentThisMonth(c.id).toFixed(2)]);
  blank();

  push([t("Recurring bills")]);
  push([t("Name"), t("category"), t("amount"), t("frequency"), t("next due")]);
  for (const r of state.recurring || []) {
    push([r.name, t(cats.find(c => c.id === r.category)?.label || "Other"), Number(r.amount || 0).toFixed(2), freqLabel(t, r.freq), r.nextDue || ""]);
  }
  blank();

  const inRange = (d) => d && (!monthKey || d.slice(0, 7) === monthKey);
  const items = [];
  for (const e of state.expenses) {
    if (!inRange(e.date)) continue;
    const c = cats.find(x => x.id === e.category);
    items.push([e.date, t("expense"), t(c?.label || "Other"), e.note || "", Number(e.amount || 0).toFixed(2)]);
  }
  for (const p of state.payments || []) {
    if (!inRange(p.date)) continue;
    const d = state.debts.find(x => x.id === p.debtId);
    items.push([p.date, t("debt payment"), d?.name || t("Debt"), "", Number(p.amount || 0).toFixed(2)]);
  }
  items.sort((a, b) => a[0].localeCompare(b[0]));
  push([t("Transactions")]);
  push([t("date"), t("Type"), t("category"), t("description"), t("amount")]);
  for (const i of items) push(i);
  blank();

  push([t("Summary")]);
  push([t("total debt"), totalDebt.toFixed(2)]);
  push([t("monthly income"), monthlyIncome.toFixed(2)]);
  push([t("total monthly budget"), totalBudget.toFixed(2)]);
  push([t("Extra payment / month"), Number(state.extraPayment || 0).toFixed(2)]);
  if (totalDebt > 0) {
    // Only print projections that actually terminate — a plan that never
    // clears (minimum below interest) would compound into nonsense numbers.
    if (sim.curve[sim.curve.length - 1] <= 0) {
      push([t("debt-free date (current plan)"), sim.freeDate.toISOString().slice(0, 10)]);
      push([t("projected interest (current plan)"), sim.totalInterest.toFixed(2)]);
    }
    if (simMin.curve[simMin.curve.length - 1] <= 0) {
      push([t("projected interest (minimums only)"), simMin.totalInterest.toFixed(2)]);
    }
  }

  return "\uFEFF" + lines.join("\r\n");
}

/* Download that survives Firefox and installed PWAs: the anchor must be in
   the DOM when clicked, and the object URL must outlive the click. Returns
   false on failure so callers can surface an error instead of silence. */
function downloadFile(name, content, type) {
  try {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1500);
    return true;
  } catch (err) {
    console.error("[Aurio] download failed", err);
    return false;
  }
}

/* ─── Private payoff milestones ──────────────────────────────────
   A reward the user sees for themselves — never shared. Keys:
   pct25/50/75, debtfree, and debt:<id> for each cleared debt. */
function earnedMilestones(state) {
  const out = [];
  const start = derivedStartingDebt(state);
  const total = totalOwed(state.debts);
  if (start > 0) {
    const pct = Math.min(100, (Math.max(0, start - total) / start) * 100);
    for (const m of [25, 50, 75]) if (pct >= m) out.push(`pct${m}`);
    if (total <= 0) out.push("debtfree");
  }
  for (const d of state.debts) if (d.balance <= 0) out.push(`debt:${d.id}`);
  return out;
}
const MILESTONE_RANK = (k) => (k === "debtfree" ? 5 : k.startsWith("debt:") ? 4 : k === "pct75" ? 3 : k === "pct50" ? 2 : 1);
function milestoneContent(key, state, t) {
  if (key === "debtfree") return { title: t("You're debt-free."), sub: t("Every debt, gone. This is what you built.") };
  if (key.startsWith("debt:")) {
    const d = state.debts.find((x) => x.id === key.slice(5));
    return { title: t("A debt, gone."), sub: t("{n} is fully paid off.", { n: d?.name || t("Debt") }) };
  }
  return {
    pct75: { title: t("The home stretch."), sub: t("75% destroyed — the end is in sight.") },
    pct50: { title: t("Halfway there."), sub: t("Half of your starting debt is gone.") },
    pct25: { title: t("A quarter down."), sub: t("You've destroyed 25% of your starting debt.") },
  }[key];
}

/* ═══ atoms ═══ */

/* Numeric input that commits numbers upward but lets the field be fully
   cleared while typing (a plain value={number} snaps back to 0 the moment
   the field empties, trapping the user with an undeletable 0). */
function NumField({ value, onCommit, style, ...props }) {
  const [text, setText] = useState(value == null ? "" : String(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value == null ? "" : String(value));
  }, [value]);
  return (
    <input
      type="number" inputMode="decimal" {...props}
      value={text}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; setText(value == null ? "" : String(value)); }}
      onChange={(e) => {
        setText(e.target.value);
        onCommit(e.target.value === "" ? 0 : Number(e.target.value) || 0);
      }}
      style={style}
    />
  );
}
const Card = ({ children, style }) => (
  <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 16, padding: 16, ...style }}>{children}</div>
);
const Label = ({ children, style }) => (
  <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: T.faint, fontWeight: 600, ...style }}>{children}</div>
);
/* A number is an atom — it must never break mid-figure. This carried
   `overflowWrap: "anywhere"` from the v0.20.1 overflow armour, which did stop a
   runaway value blowing out the layout but also let every ordinary amount split
   at any character ($2,032 → "$2,03" / "2"). The runaway case is already
   handled properly by fmt()'s FMT_CEILING, which returns "—"; that is the right
   guard. Wherever a figure sits beside text, the figure takes `flexShrink: 0`
   and the text truncates instead. */
const Mono = ({ children, style }) => (
  <span style={{
    fontFamily: "'Geist Mono', monospace",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    ...style,
  }}>{children}</span>
);
/* Companion for the label sitting opposite a figure: yields space by
   truncating rather than squeezing the number into a wrap. */
const ELLIPSIS = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
/* Chart legend key: an 8×8 colour chip followed by label text in normal ink.
   Charts identify a series with this, never by colouring the label itself. */
const Swatch = ({ color, children, style }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, ...style }}>
    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
    {children}
  </span>
);
const Btn = ({ children, onClick, variant = "gold", style, small, disabled }) => {
  const base = {
    gold: { background: T.gold, color: T.midnight, border: "none" },
    ghost: { background: "transparent", color: T.dim, border: `1px solid ${T.line}` },
    danger: { background: "transparent", color: T.garnet, border: `1px solid ${T.garnet}44` },
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...base, borderRadius: 10, padding: small ? "7px 12px" : "12px 18px",
      fontWeight: 600, fontSize: small ? 12 : 14, cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.55 : 1, ...style,
    }}>{children}</button>
  );
};
const Input = (props) => (
  <input {...props} style={{
    width: "100%", background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 10,
    color: T.white, padding: "11px 12px", fontSize: 15, ...props.style,
  }} />
);

/* Area+line chart. The optional dashed `points2` is a comparison scenario
   ("current plan" vs "with the boost") — a dashed DATA series is deliberate and
   distinct from a dashed gridline, which this app never draws.
   `label` becomes the accessible description; without it the chart is invisible
   to assistive tech, which was true of every chart here before v0.25.0. */
function Curve({ points, points2, color = T.gold, color2 = T.greenHi, h = 64, label }) {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points, ...(points2 || [0])) || 1;
  const w = 300;
  const y = (p) => h - (p / max) * (h - 6) - 2;
  const toPath = (pts) => {
    const step = w / (pts.length - 1);
    return pts.map((p, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${y(p).toFixed(1)}`).join(" ");
  };
  const path = toPath(points);
  const endY = y(points[points.length - 1]);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: h, display: "block", overflow: "visible" }}
      role="img" aria-label={label || undefined}>
      <path d={`${path} L${w},${h} L0,${h} Z`} fill={color} opacity="0.10" />
      {points2 && points2.length > 1 && <path d={toPath(points2)} fill="none" stroke={color2} strokeWidth="2" strokeDasharray="4 3" />}
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
      {/* Endpoint marker: r 4 (8px) with a 2px surface ring so it stays legible
          where it sits on the line or crosses the comparison series. */}
      <circle cx={w} cy={endY} r="4" fill={color} stroke={T.panel} strokeWidth="2" />
    </svg>
  );
}

/* Category breakdown — a ranked bar per category, biggest first.
   This replaced a donut (removed in v0.25.0) that couldn't be read: it shaded
   every slice the same gold at 12 descending opacities, so past the 6th slice
   the arcs were indistinguishable from the panel behind them, and it carried no
   labels, percentages or legend at all.

   The fix isn't a nicer palette — it's not using colour to carry identity.
   Spend categories are nominal (there's no order to "rent" vs "groceries"), so
   shading them by rank double-encodes the length the bar already shows, and the
   set is user-extensible (custom categories are unbounded), which no fixed
   palette can seat. Here the bar length is the only encoding, identity comes
   from the emoji + label + amount beside it, and every bar is the same gold.
   Garnet is used only where it means something — over budget.

   Bars are a share of the month's TOTAL spend, not of the largest category:
   scaling to the largest made the top category a full-width bar every month,
   reading as "rent is all your spending" when it was 40% of it. */
const CATEGORY_ROW_CAP = 8;
function CategoryBars({ rows, total, t, cats, expanded, onToggle }) {
  if (!rows.length) return null;
  /* Only fold when folding actually buys something. Hiding a single row behind
     a "show all" costs the reader a tap to reveal one line — so the cap is soft:
     go one over rather than fold a lone tail row. */
  const cap = rows.length === CATEGORY_ROW_CAP + 1 ? rows.length : CATEGORY_ROW_CAP;
  const shown = expanded ? rows : rows.slice(0, cap);
  const rest = expanded ? [] : rows.slice(cap);
  const restTotal = rest.reduce((s, r) => s + r.total, 0);
  const pctOf = (v) => (total > 0 ? (v / total) * 100 : 0);
  /* Whole numbers, but never round a real amount down to "0%". */
  const pctLabel = (p) => (p > 0 && p < 1 ? t("<1%") : `${Math.round(p)}%`);

  const bar = (pct, color) => (
    <div style={{ height: 6, background: T.line, borderRadius: 3, overflow: "hidden", marginTop: 4 }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: color, borderRadius: 3 }} />
    </div>
  );

  return (
    <div
      role="img"
      aria-label={
        rows.length
          ? t("Spending by category: {list}", {
              list: rows.slice(0, 3).map(r => {
                const c = cats.find(x => x.id === r.id);
                return `${t(c?.label || r.id)} ${pctLabel(pctOf(r.total))}`;
              }).join(", "),
            })
          : undefined
      }
    >
      {shown.map(({ id, total: amount, budget }) => {
        const c = cats.find(x => x.id === id);
        const over = budget > 0 && amount > budget;
        const pct = pctOf(amount);
        return (
          <div key={id} style={{ marginBottom: 9 }} title={`${t(c?.label || id)} · ${fmt(amount)} · ${pctLabel(pct)}`}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, gap: 8 }}>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c?.emoji} {t(c?.label || "")}
                {over && <span style={{ color: T.garnet, fontSize: 10.5, marginLeft: 6, fontWeight: 700 }}>{t("over budget by {a}", { a: fmt(amount - budget) })}</span>}
              </span>
              <span style={{ flexShrink: 0, display: "flex", gap: 8, alignItems: "baseline" }}>
                <Mono style={{ color: over ? T.garnet : T.white }}>{fmt(amount)}</Mono>
                <Mono style={{ color: T.faint, fontSize: 11, minWidth: 30, textAlign: "right" }}>{pctLabel(pct)}</Mono>
              </span>
            </div>
            {bar(pct, over ? T.garnet : T.gold)}
          </div>
        );
      })}

      {rest.length > 0 && (
        <button
          onClick={onToggle}
          style={{ width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", color: "inherit", marginBottom: 9 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, gap: 8 }}>
            {/* Deliberately not called "Other" — there's a built-in category by
                that name, and two rows with the same label mean different things. */}
            <span style={{ color: T.dim }}>⋯ {t(rest.length === 1 ? "{n} smaller category" : "{n} smaller categories", { n: rest.length })} <span style={{ color: T.gold, fontSize: 10.5, fontWeight: 700 }}>{t("show all")}</span></span>
            <span style={{ flexShrink: 0, display: "flex", gap: 8, alignItems: "baseline" }}>
              <Mono style={{ color: T.dim }}>{fmt(restTotal)}</Mono>
              <Mono style={{ color: T.faint, fontSize: 11, minWidth: 30, textAlign: "right" }}>{pctLabel(pctOf(restTotal))}</Mono>
            </span>
          </div>
          {bar(pctOf(restTotal), T.faint)}
        </button>
      )}

      {expanded && rows.length > CATEGORY_ROW_CAP && (
        <button onClick={onToggle} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: T.gold, fontSize: 11, fontWeight: 700 }}>
          {t("show less")}
        </button>
      )}
    </div>
  );
}

/* Bottom-nav icons — inline stroke SVGs (no icon-font dependency), drawn
   in the Tabler outline style so they inherit `currentColor` from the tab
   button (gold when active, faint otherwise). Replaces the earlier cryptic
   Unicode glyphs (◈ ▤ ＋ ▦ ◎). */
const NAV_ICON_PATHS = {
  home: <><path d="M5 12l7-7 7 7" /><path d="M6 10.5V19h12v-8.5" /></>,
  debts: <><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18" /></>,
  spend: <><path d="M6 3h9l3 3v15l-2.2-1.4L13.6 21l-2.1-1.4L9.4 21l-2.1-1.4L5 21V5a2 2 0 0 1 1-2z" /><path d="M9 8h6M9 12h6" /></>,
  budget: <><path d="M4 7h13a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" /><path d="M4 7V6a2 2 0 0 1 2-2h9" /><circle cx="16.5" cy="12.5" r="1.2" fill="currentColor" stroke="none" /></>,
  invest: <><path d="M4 17l5-5 3 3 6-7" /><path d="M15 8h4v4" /></>,
  /* Grow sub-tabs. `growDeposit` is a KEY, not a house, on purpose: the bottom
     nav already owns a house glyph for the Home tab, and two house icons on
     one screen meaning different things is exactly the sort of detail that
     quietly erodes trust in a money app. */
  growNext: <><circle cx="12" cy="12" r="9" /><path d="M10 8l4 4-4 4" /></>,
  growSave: <><path d="M12 3v9" /><path d="M8.5 9.5L12 13l3.5-3.5" /><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></>,
  growDeposit: <><circle cx="8" cy="14.5" r="3.4" /><path d="M10.5 12L19 3.5" /><path d="M16.2 6.3l2.4 2.4" /></>,
  /* Not the trend line — that is the bottom nav's own Grow glyph, and the two
     would sit on one screen meaning different things. A sun over the horizon
     instead: the years after the work, which is what this panel projects. */
  growRetire: <><circle cx="12" cy="12.5" r="3.6" /><path d="M12 4v2" /><path d="M4.8 12.5H3" /><path d="M21 12.5h-1.8" /><path d="M6.9 7.4L5.6 6.1" /><path d="M17.1 7.4l1.3-1.3" /><path d="M3 19h18" /></>,
};
function NavIcon({ id, size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {NAV_ICON_PATHS[id]}
    </svg>
  );
}

/* Sub-navigation inside a tab. Mirrors the pattern Forma already ships on its
   Fuel tab so the two Zoldan Labs apps share one navigation idiom: icon over
   label, a gold rule under the active one, and — deliberately — inactive
   labels kept in normal ink rather than dimmed. The icon carries the selected
   state, so the label never has to be made hard to read to say "not this one".

   Proper tablist semantics (Forma's version has role="tab" without the
   wrapping tablist, which leaves the group unannounced to a screen reader). */
function SubTabs({ items, value, onChange, label }) {
  return (
    <div role="tablist" aria-label={label} style={{ display: "flex", borderBottom: `1px solid ${T.line}`, marginBottom: 16 }}>
      {items.map(([id, icon, text]) => {
        const on = value === id;
        return (
          <button key={id} role="tab" aria-selected={on} id={`growtab-${id}`} aria-controls={`growpanel-${id}`}
            onClick={() => onChange(id)}
            style={{
              flex: 1, textAlign: "center", padding: "9px 2px 8px", cursor: "pointer",
              background: "none", border: "none", color: on ? T.gold : T.dim,
              /* -1 pulls the 2px active rule over the 1px hairline below it, or
                 the two stack into a 3px seam under the selected tab. */
              borderBottom: `2px solid ${on ? T.gold : "transparent"}`, marginBottom: -1,
            }}>
            <NavIcon id={icon} size={18} />
            <div style={{ fontSize: 10.5, marginTop: 3, fontWeight: 600, color: on ? T.gold : T.white, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {text}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* Grow's four concerns. "Deposit" rather than "Home" because the bottom nav
   already has a Home tab — and because homePlan() is a general deposit
   calculator (price x deposit% - cash - scheme), so it serves someone buying
   an investment property just as well as a first-home buyer. Only the
   KiwiSaver/FHSSS scheme rules are first-home-specific. */
const GROW_TABS = [
  /* NOT "Next"/"Save": both are already dict keys for other senses of the same
     English word — "Next" is the onboarding ADVANCE button (PT "Avançar") and
     "Save" is the verb (PT "Salvar"). The dicts are keyed by English source, so
     reusing them would silently ship "Advance" as a section name in Portuguese.
     i18n.test.js can't catch it either, because these are read from this array
     rather than written out as a t() literal — hence the dedicated test there. */
  ["next", "growNext", "Next up"],
  ["save", "growSave", "Savings"],
  ["deposit", "growDeposit", "Deposit"],
  ["retire", "growRetire", "Retire"],
];

/* ═══ main ═══ */
export default function Aurio() {
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("home");
  /* Which Grow sub-tab is open. Held here rather than in `state` so a UI
     position never enters the synced blob — it is not the user's money. Like
     `tab` itself this is session-scoped: it survives moving between tabs, not
     a reload. `goGrow` is the deep link, and it sets the sub-tab BEFORE the
     tab so a card that says "build your buffer" lands on the buffer rather
     than on whichever sub-tab happened to be open last. */
  const [growTab, setGrowTab] = useState("next");
  const goGrow = (sub) => { setGrowTab(sub); setTab("invest"); };
  const [toast, setToast] = useState(null);
  const saveTimer = useRef(null);
  const loaded = useRef(false);
  const account = useAccount();
  const accountRef = useRef(account);
  accountRef.current = account;
  const cloudSyncedFor = useRef(null);
  const cloudCheckInFlight = useRef(false);
  const conflictPending = useRef(false);
  // True whenever this device had NO local data at all on load — a pristine
  // device can never outrank a real cloud backup, even though its freshly
  // seeded state gets a "now" timestamp that would otherwise look newer.
  const pristineLoad = useRef(false);
  // Any edit the user has actually made since this load. A pristine device
  // defers to the cloud, but never at the cost of discarding work someone
  // typed in while the check was still in flight (e.g. offline).
  const userEdited = useRef(false);
  const [cloudChecked, setCloudChecked] = useState(false);
  const [cloudUnreachable, setCloudUnreachable] = useState(false);
  /* A backup exists AND this device typed its own data before signing in —
     the two can't be merged, so the person picks. Pushes stay blocked while
     this is set, so neither copy is overwritten before they choose. */
  const [cloudConflict, setCloudConflict] = useState(null);
  const remindedOnce = useRef(false);
  const milestoneInit = useRef(false);
  const [celebration, setCelebration] = useState(null);

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2200); };
  /* Every user-initiated change flows through here, so this doubles as the
     "the person has typed something" signal the cloud check reads before it
     considers replacing what's on screen. */
  const up = (patch) => { userEdited.current = true; setState(s => ({ ...s, ...patch })); };

  /* Shallow-merge a saved/imported/cloud blob over the defaults and run
     the recurring auto-log + interest accrual. Never lets a processing
     crash lose data — each step is independently guarded. */
  const hydrate = (saved) => {
    // Anyone with previously saved data predates onboarding — don't show it to them.
    const raw = saved ? { ...DEFAULT_STATE, ...saved, onboarded: saved.onboarded ?? true } : { ...DEFAULT_STATE };
    if (!raw.invest) raw.invest = DEFAULT_STATE.invest;
    if (!raw.lang) raw.lang = "en-GB";
    if (!raw.reminders) raw.reminders = DEFAULT_STATE.reminders;
    /* Statement-cycle fields: clamp the cycle days and stamp an accrual
       bookmark on any debt that lacks one, migrating the old month bookmark.
       Must run BEFORE processInterest, which reads lastInterestDate. */
    raw.debts = (raw.debts || []).map(d => normalizeDebt(d));
    applyCurrency(raw.currency);
    let afterRecurring;
    try {
      afterRecurring = processRecurring(raw);
    } catch (err) {
      console.error("[Aurio] recurring processing failed", err);
      afterRecurring = { state: raw, logged: 0 };
    }
    try {
      const afterInterest = processInterest(afterRecurring.state);
      return { state: afterInterest.state, logged: afterRecurring.logged, interestPosted: afterInterest.posted };
    } catch (err) {
      console.error("[Aurio] interest processing failed", err);
      return { state: afterRecurring.state, logged: afterRecurring.logged, interestPosted: 0 };
    }
  };

  useEffect(() => {
    (async () => {
      /* Load order: main key → rolling backup. A failed read or corrupt
         payload must NEVER fall through to defaults while real data
         still exists somewhere — that combination is what wipes users. */
      let saved = null;
      let corrupt = null;
      try {
        const r = await window.storage.get(STORAGE_KEY);
        if (r?.value) {
          try { saved = JSON.parse(r.value); } catch { corrupt = r.value; }
        }
      } catch (err) {
        console.error("[Aurio] state read failed", err);
      }
      if (!saved) {
        try {
          const b = await window.storage.get(BACKUP_KEY);
          if (b?.value) saved = JSON.parse(b.value);
        } catch { /* no usable backup */ }
      }
      pristineLoad.current = !saved;
      if (corrupt) {
        // Preserve the unreadable payload for manual recovery before anything overwrites it.
        try { await window.storage.set(`${STORAGE_KEY}.corrupt`, corrupt); } catch { /* best effort */ }
      }
      const { state: processed, logged, interestPosted } = hydrate(saved);
      setState(processed);
      loaded.current = true;
      if (saved) {
        try { await window.storage.set(BACKUP_KEY, JSON.stringify(saved)); } catch { /* best effort */ }
      }
      const tt = makeT(processed.lang);
      if (logged > 0) {
        const msg = tt(logged > 1 ? "{n} recurring expenses auto-logged" : "{n} recurring expense auto-logged", { n: logged });
        setTimeout(() => { setToast(msg); setTimeout(() => setToast(null), 2600); }, 400);
      }
      if (interestPosted > 0) {
        // Stagger behind the recurring toast (if any) — they share one toast slot.
        const delay = 400 + (logged > 0 ? 2900 : 0);
        const msg = tt(interestPosted > 1 ? "Interest posted on {n} debts" : "Interest posted on {n} debt", { n: interestPosted });
        setTimeout(() => { setToast(msg); setTimeout(() => setToast(null), 2600); }, delay);
      }
    })();
  }, []);

  useEffect(() => {
    if (!loaded.current || !state) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = { ...state, _savedAt: new Date().toISOString() };
      try { await window.storage.set(STORAGE_KEY, JSON.stringify(payload)); } catch (e) { console.error(e); }
      // Cloud backup is a Premium feature; free accounts stay local-only.
      // Never push before the cloud-check effect below has resolved once
      // this session — pushing an unconfirmed state first (e.g. a freshly
      // seeded placeholder before onboarding) is exactly how a real user's
      // cloud backup got silently overwritten once. cloudChecked flips true
      // as soon as it's actually safe, so this only ever delays a push by
      // the time of one round trip, never drops it.
      if (accountRef.current?.access?.premiumEnabled && cloudChecked) {
        pushCloudState(accountRef.current?.user, payload);
      }
    }, 600);
  }, [state, cloudChecked]);

  /* Cloud restore (Premium): once per signed-in premium user, reconcile this
     device against the Supabase copy BEFORE any local write is allowed to
     reach the cloud (see cloudChecked, used by the push effect above). A
     pristine device (pristineLoad — no local data at all on this load) is
     never authoritative and always defers to an existing remote backup,
     regardless of relative timestamps: a freshly seeded placeholder state
     stamps a "now" _savedAt that would otherwise look newer than an older,
     real backup — that exact trick is what caused the incident this guards
     against. */
  useEffect(() => {
    if (account.loading) return;
    const user = account.user;
    if (!user || !supabase || !account.access.premiumEnabled) { setCloudChecked(true); return; }
    if (!state || cloudSyncedFor.current === user.id || cloudCheckInFlight.current) return;
    if (conflictPending.current) return; // waiting on the user's choice
    cloudCheckInFlight.current = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("user_state").select("state, saved_at").eq("user_id", user.id).maybeSingle();
        if (error) throw error;
        if (data?.state) {
          const localAt = state._savedAt ? Date.parse(state._savedAt) : 0;
          const remoteAt = data.saved_at ? Date.parse(data.saved_at) : 0;
          /* Adopt the cloud copy when this device brought nothing of its own,
             or when the cloud copy is genuinely newer — but never overwrite
             edits the person has made since this load (the offline case: they
             started typing before the check could finish). Whichever side
             loses here is still recoverable: user_state_history archives every
             version server-side before it is replaced. */
          if (!userEdited.current && (pristineLoad.current || remoteAt > localAt)) {
            const { state: processed } = hydrate(data.state);
            setState(processed);
            notify(makeT(processed.lang)("Restored from your cloud backup ☁️"));
          } else if (pristineLoad.current && userEdited.current) {
            /* This device brought nothing of its own yet already holds typed
               data — onboarding ran before sign-in. That local copy is NOT a
               descendant of the backup, so neither timestamps nor "local wins"
               can decide honestly: silently keeping local is what pushed
               placeholder data over a real backup once. Ask instead, and leave
               cloudSyncedFor unset so the push effect stays blocked until the
               answer comes back. */
            conflictPending.current = true;
            setCloudConflict({ remote: data.state, savedAt: data.saved_at });
            return;
          }
        }
        // Either a backup existed (and was adopted if it mattered) or none
        // exists yet for this account — both are now confirmed, so local
        // writes can no longer clobber anything real.
        pristineLoad.current = false;
        cloudSyncedFor.current = user.id;
      } catch {
        // Offline or the table hiccuped. A device with its own real local
        // data is still safe to trust as-is; a pristine device genuinely
        // doesn't know what the cloud holds, so stay blocked (no push) and
        // surface that plainly rather than guess.
        if (!pristineLoad.current) cloudSyncedFor.current = user.id;
        else setCloudUnreachable(true);
      } finally {
        cloudCheckInFlight.current = false;
        setCloudChecked(cloudSyncedFor.current === user.id);
      }
    })();
  }, [account.loading, account.user?.id, account.access.premiumEnabled, state]);

  /* Bill reminders fire once per app open, after the state is loaded. */
  useEffect(() => {
    if (!state || remindedOnce.current) return;
    remindedOnce.current = true;
    notifyDueBills(state, makeT(state.lang));
  }, [state]);

  /* Private milestone celebrations. First run silently backfills already-
     earned milestones (so existing progress doesn't retro-celebrate); after
     that, any newly-crossed milestone shows a one-off reward overlay. */
  useEffect(() => {
    if (!state || !loaded.current) return;
    const earned = earnedMilestones(state);
    const seen = state.seenMilestones || [];
    const fresh = earned.filter((k) => !seen.includes(k));
    const firstRun = !milestoneInit.current;
    milestoneInit.current = true;
    if (!fresh.length) return;
    setState((s) => ({ ...s, seenMilestones: [...(s.seenMilestones || []), ...fresh] }));
    if (firstRun) return; // silently backfill pre-existing progress, never retro-celebrate
    const top = [...fresh].sort((a, b) => MILESTONE_RANK(b) - MILESTONE_RANK(a))[0];
    setCelebration(milestoneContent(top, state, makeT(state.lang)));
  }, [state]);

  const uc = useUpdateCenter(notify);
  const [showAccount, setShowAccount] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  /* Premium gate. `premium` is true for trial/tester/paid/lifetime while
     active; `requirePremium()` runs a premium-only action or, for free
     users, opens the upgrade sheet and returns false so the caller stops. */
  const premium = account.access.premiumEnabled;
  const requirePremium = () => {
    if (premium) return true;
    setShowUpgrade(true);
    return false;
  };

  /* A ?ref= link lands before there's an account, so park the code now and
     let useAccount redeem it once a session exists (it survives the emailed
     magic-link round trip). Runs before anything else touches the URL. */
  useEffect(() => { captureReferralFromUrl(); }, []);

  /* Celebrate a referral exactly once, whichever side it landed on. */
  useEffect(() => {
    const ev = account.referralEvent;
    if (!ev) return;
    const tr = makeT(state?.lang);
    notify(ev.type === "redeemed"
      ? tr("A free month of Premium is yours 🎉")
      : tr(ev.months === 1
          ? "A friend stuck with Aurio — a free month is yours 🎉"
          : "{n} friends stuck with Aurio — {n} free months are yours 🎉", { n: ev.months }));
    account.clearReferralEvent();
  }, [account.referralEvent]);

  /* Returning from Stripe Checkout: refresh the grant (the webhook has
     usually already flipped it) and celebrate, then clean the URL. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "1") {
      account.refreshAccess?.();
      setTimeout(() => account.refreshAccess?.(), 2500); // webhook race cushion
      notify(makeT(state?.lang)("Welcome to Premium 🎉"));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("checkout") === "cancelled") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  /* Full-state replacement used by backup restore in the account sheet. */
  const replaceState = (obj) => {
    const { state: processed } = hydrate(obj);
    setState(processed);
  };

  /* Nuclear reset: erase every copy — main key + IndexedDB mirror,
     rolling backup, reminder log, and the cloud row — then return to
     onboarding. Confirmation UX lives in the account sheet. */
  const resetAll = async () => {
    clearTimeout(saveTimer.current);
    loaded.current = false;
    try { await window.storage.delete(STORAGE_KEY); } catch { /* best effort */ }
    try { await window.storage.delete(BACKUP_KEY); } catch { /* best effort */ }
    try { await window.storage.delete(NOTIFIED_KEY); } catch { /* best effort */ }
    const user = accountRef.current?.user;
    if (user && supabase) {
      try { await supabase.from("user_state").delete().eq("user_id", user.id); } catch { /* offline */ }
    }
    setState({ ...DEFAULT_STATE });
    loaded.current = true;
  };

  /* Manual "Back up now". Deliberately obeys the SAME gate as the automatic
     push: while the cloud check is unresolved or a conflict is unanswered,
     this device does not yet know what the account holds, and a button press
     is not a reason to overwrite it. Returns a result so the caller can say
     plainly whether it worked — the reassurance is the entire feature. */
  const backupNow = async () => {
    const user = accountRef.current?.user;
    if (!user || !accountRef.current?.access?.premiumEnabled) return { ok: false, reason: "not-premium" };
    if (!cloudChecked || cloudConflict) return { ok: false, reason: "not-ready" };
    const payload = { ...state, _savedAt: new Date().toISOString() };
    try { await window.storage.set(STORAGE_KEY, JSON.stringify(payload)); } catch (e) { console.error(e); }
    return pushCloudState(user, payload);
  };

  if (!state) return (
    <div style={{ minHeight: "100vh", background: T.midnight, display: "grid", placeItems: "center", color: T.dim, fontFamily: "'Geist', sans-serif" }}>
      <style>{FONT_CSS}</style>Loading your money…
    </div>
  );

  const t = makeT(state.lang);
  const lang = state.lang;

  /* Two copies that can't be merged, so the person decides. Rendered ahead of
     onboarding because the usual way to get here is onboarding-before-sign-in:
     the screen underneath would otherwise be the very "skip — use example
     data" flow whose output is in the running. Pushes stay blocked until a
     choice is made, so nothing is overwritten while this is on screen. */
  if (cloudConflict) {
    const savedLabel = cloudConflict.savedAt
      ? new Date(cloudConflict.savedAt).toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" })
      : String.fromCharCode(8212);
    const countDebts = (st) => (st?.debts || []).filter(d => (d.balance || 0) > 0).length;
    const sumDebts = (st) => (st?.debts || []).reduce((n, d) => n + (d.balance || 0), 0);
    const resolve = (useRemote) => {
      if (useRemote) {
        const { state: processed } = hydrate(cloudConflict.remote);
        userEdited.current = false;
        setState(processed);
        notify(makeT(processed.lang)("Restored from your cloud backup ☁️"));
      }
      pristineLoad.current = false;
      cloudSyncedFor.current = account.user?.id || null;
      conflictPending.current = false;
      setCloudConflict(null);
      setCloudChecked(true);
    };
    const opt = (title, meta, onClick, primary) => (
      <div style={{ background: T.panel, border: `1px solid ${primary ? T.gold : T.line}`, borderRadius: 12, padding: 14, marginTop: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.white }}>{title}</div>
        <div style={{ fontSize: 12, color: T.dim, marginTop: 5, lineHeight: 1.5 }}>{meta}</div>
        <Btn variant={primary ? "gold" : "ghost"} style={{ width: "100%", marginTop: 12 }} onClick={onClick}>
          {primary ? t("Restore my backup") : t("Keep this device's data")}
        </Btn>
      </div>
    );
    return (
      <div style={{ minHeight: "100vh", background: T.midnight, color: T.white, fontFamily: "'Geist', system-ui, sans-serif", padding: "32px 20px" }}>
        <style>{FONT_CSS}</style>
        <div style={{ maxWidth: 420, margin: "0 auto" }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 30, lineHeight: 1.15 }}>{t("Which one should Aurio keep?")}</div>
          <div style={{ fontSize: 13, color: T.dim, marginTop: 12, lineHeight: 1.6 }}>
            {t("You're signed in and there's a backup on your account, but this device also has data you entered before signing in. They can't be merged — pick one. Nothing is saved over until you choose.")}
          </div>
          {opt(
            t("Your cloud backup"),
            t("Saved {d} · {n} debts · {a}", {
              d: savedLabel, n: countDebts(cloudConflict.remote), a: fmt(sumDebts(cloudConflict.remote)),
            }),
            () => resolve(true), true,
          )}
          {opt(
            t("What's on this device"),
            t("{n} debts · {a}", { n: countDebts(state), a: fmt(sumDebts(state)) }),
            () => resolve(false), false,
          )}
          <div style={{ fontSize: 11, color: T.faint, marginTop: 16, lineHeight: 1.5 }}>
            {t("Either way the other copy is kept in your account's version history, so this is recoverable.")}
          </div>
        </div>
      </div>
    );
  }

  if (!state.onboarded) {
    /* A signed-in Premium account can still have a real cloud backup even
       though this device's local copy is pristine. Never offer onboarding
       (and its "skip — use example data") while a restore might be one round
       trip away: that screen is how placeholder data reaches a real account.
       If the backup can't be reached we say so and offer a retry rather than
       spin forever — and cloud pushes stay blocked until it succeeds, so
       nothing that happens meanwhile can overwrite the backup. */
    const awaitingCloudCheck = account.loading || (!!account.user && account.access.premiumEnabled && !cloudChecked);
    if (awaitingCloudCheck) {
      const wrap = { minHeight: "100vh", background: T.midnight, display: "grid", placeItems: "center", color: T.dim, fontFamily: "'Geist', sans-serif", padding: 24, textAlign: "center" };
      if (!cloudUnreachable) {
        return <div style={wrap}><style>{FONT_CSS}</style>{t("Checking your cloud backup…")}</div>;
      }
      return (
        <div style={wrap}>
          <style>{FONT_CSS}</style>
          <div style={{ maxWidth: 340 }}>
            <div style={{ fontSize: 15, color: T.white, marginBottom: 10 }}>{t("Couldn't reach your cloud backup")}</div>
            <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 20 }}>
              {t("You're signed in, so there may be data saved to your account. Aurio won't start a fresh setup until it can check — that's what stops a new setup from replacing your real data.")}
            </div>
            <Btn style={{ width: "100%" }} onClick={() => { setCloudUnreachable(false); cloudCheckInFlight.current = false; setState(s => ({ ...s })); }}>{t("Try again")}</Btn>
          </div>
        </div>
      );
    }
    return <Onboarding up={up} t={t} lang={lang} currency={state.currency} account={account} notify={notify} />;
  }

  const totalDebt = totalOwed(state.debts);
  const sim = simulate(state.debts, state.extraPayment, state.strategy);
  const simAlt = simulate(state.debts, state.extraPayment, otherStrategy(state.strategy));
  const simZero = simulate(state.debts, 0, state.strategy);
  /* Derived, not read from state: the stored figure was written once at
     onboarding and went stale the moment a debt was added afterwards — a
     skipped onboarding left it at 0, which pins the bar to 0% forever. */
  const startingDebt = derivedStartingDebt(state);
  const paidOff = Math.max(0, startingDebt - totalDebt);
  const pct = startingDebt > 0 ? Math.min(100, (paidOff / startingDebt) * 100) : 0;

  const now = new Date();
  const monthKey = now.toISOString().slice(0, 7);
  const monthExpenses = state.expenses.filter(e => e.date?.slice(0, 7) === monthKey);
  const spentThisMonth = monthExpenses.reduce((s, e) => s + e.amount, 0);
  const totalBudget = budgetTotalOf(state);
  const totalMins = state.debts.reduce((s, d) => s + (d.balance > 0 ? d.minPayment : 0), 0);
  const monthlyIncome = monthlyIncomeOf(state);
  const leftover = monthlyIncome - totalBudget - totalMins - state.extraPayment;
  const debtBudget = totalMins + state.extraPayment; // becomes investing power after payoff
  /* One walk of the forecast for the whole app — Home warns about a tight
     month, Grow plans around the same months, and they must never disagree
     about which months those are. */
  const map = moneyMap(state, sim, monthlyIncome, totalBudget);

  const tabs = [
    { id: "home", label: t("Home") },
    { id: "debts", label: t("Debts") },
    { id: "spend", label: t("Spend") },
    { id: "budget", label: t("Budget") },
    { id: "invest", label: t("Grow") },
  ];

  const shared = { state, up, t, lang, notify, premium, requirePremium };
  const homeExtras = { account, onOpenAccount: () => setShowAccount(true) };

  return (
    <div style={{ minHeight: "100vh", background: T.midnight, color: T.white, fontFamily: "'Geist', system-ui, sans-serif", paddingBottom: 90 }}>
      <style>{FONT_CSS}</style>

      <div style={{ padding: "18px 20px 6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 26, letterSpacing: "0.01em" }}>
          Aurio<span style={{ color: T.gold }}>.</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UpdateChip uc={uc} T={T} />
          <select value={lang} onChange={e => up({ lang: e.target.value })} aria-label={t("Language")} style={{
            background: T.panel, color: T.dim, border: `1px solid ${T.line}`, borderRadius: 8,
            padding: "5px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>
            {LANGS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
          <button onClick={() => setShowAccount(true)} aria-label={t("My Info")} title={t("My Info")} style={{
            display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30,
            background: T.panel, border: `1px solid ${T.line}`, borderRadius: 8, cursor: "pointer", padding: 0, flexShrink: 0,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="8" r="3.6" />
              <path d="M4.5 20c1.6-3.4 4.3-5 7.5-5s5.9 1.6 7.5 5" />
            </svg>
          </button>
        </div>
      </div>

      <UpdateBanner uc={uc} T={T} />

      {showAccount && <AccountSheet account={account} t={t} lang={lang} notify={notify} state={state} backupNow={backupNow} replaceState={replaceState} resetAll={resetAll} premium={premium} onUpgrade={() => { setShowAccount(false); setShowUpgrade(true); }} onClose={() => setShowAccount(false)} />}

      {showUpgrade && <UpgradeSheet account={account} t={t} notify={notify} onClose={() => setShowUpgrade(false)} />}

      {celebration && <MilestoneModal content={celebration} t={t} onClose={() => setCelebration(null)} />}

      <div style={{ padding: "8px 16px", maxWidth: 560, margin: "0 auto" }}>
        {tab === "home" && <Home {...shared} {...homeExtras} {...{ sim, simAlt, simZero, totalDebt, pct, paidOff, startingDebt, leftover, spentThisMonth, totalBudget, monthExpenses, map, setTab, goGrow }} />}
        {tab === "debts" && <Debts {...shared} {...{ sim, simAlt }} />}
        {tab === "spend" && <Spend {...shared} {...{ monthExpenses }} />}
        {tab === "budget" && <Budget {...shared} {...{ monthExpenses, leftover, totalBudget, totalMins, monthlyIncome }} />}
        {tab === "invest" && <Invest {...shared} {...{ sim, debtBudget, totalDebt, monthlyIncome, totalBudget, setTab, map, growTab, setGrowTab }} />}
      </div>

      {toast && (
        <div style={{ position: "fixed", bottom: 96, left: "50%", transform: "translateX(-50%)", background: T.panelHi, border: `1px solid ${T.gold}55`, color: T.white, padding: "10px 18px", borderRadius: 12, fontSize: 13, zIndex: 70, boxShadow: "0 8px 24px rgba(0,0,0,0.5)", maxWidth: "90vw" }}>
          {toast}
        </div>
      )}

      <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(14,17,24,0.92)", backdropFilter: "blur(12px)", borderTop: `1px solid ${T.line}`, display: "flex", justifyContent: "space-around", alignItems: "flex-start", padding: "10px 0 22px", zIndex: 40 }}>
        {tabs.map(tb => tb.id === "spend" ? (
          /* Raised center action: logging an expense is the app's highest-
             frequency task, so it gets the prominent gold circle. */
          <button key={tb.id} onClick={() => setTab(tb.id)} aria-label={tb.label} aria-current={tab === tb.id ? "page" : undefined}
            style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, width: 62, marginTop: -32, padding: 0 }}>
            <span style={{
              width: 54, height: 54, borderRadius: "50%", background: T.gold, color: T.midnight,
              border: `4px solid ${T.midnight}`, display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: tab === tb.id ? `0 0 0 2px ${T.gold}, 0 6px 18px rgba(0,0,0,0.45)` : "0 6px 18px rgba(0,0,0,0.45)",
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </span>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: tab === tb.id ? T.gold : T.faint }}>{tb.label}</span>
          </button>
        ) : (
          <button key={tb.id} onClick={() => setTab(tb.id)} aria-label={tb.label} aria-current={tab === tb.id ? "page" : undefined} style={{ background: "none", border: "none", cursor: "pointer", color: tab === tb.id ? T.gold : T.faint, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, width: 62 }}>
            <NavIcon id={tb.id} />
            <span style={{ fontSize: 10.5, fontWeight: 600 }}>{tb.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/* ═══ ONBOARDING ═══
   First-run questionnaire. Answers seed income (Budget/Home), debts
   (Debts), category budgets (Budget/Spend) and retirement goals (Invest).
   Skippable — skipping keeps the example data so every tab still shows
   something, and each number stays editable inside the app. */

const OB_BUDGET_CATS = ["rent", "groceries", "utilities", "transport", "eating", "subs"];
const OB_STEPS = 5; // income · debts · budget · goals · preview

function Onboarding({ up, t, lang, currency, account, notify }) {
  const [signingIn, setSigningIn] = useState(false);
  const [step, setStep] = useState(0);
  const [income, setIncome] = useState("");
  const [incomeFreq, setIncomeFreq] = useState("monthly");
  const [debts, setDebts] = useState([]);
  const [draft, setDraft] = useState({ type: "card", name: "", balance: "", apr: "", minPayment: "" });
  const [budgets, setBudgets] = useState({});
  const [budgetFreq, setBudgetFreq] = useState("monthly");
  const [goals, setGoals] = useState({ age: "", retireAge: "", targetMonthly: "" });
  const [extra, setExtra] = useState(0);

  const totalDebt = totalOwed(debts);
  const sim = useMemo(() => simulate(debts, extra, "avalanche"), [debts, extra]);

  /* Onboarding deliberately doesn't ask for a due date — it's the one flow
     where brevity beats completeness, and the Debts tab prompts for it later.
     Still routed through normalizeDebt so the accrual bookmark is stamped and
     the debt is the same shape as one saved anywhere else. */
  const addDraft = () => {
    if (!draft.name || draft.balance === "") return;
    setDebts([...debts, normalizeDebt({
      id: uid(), type: draft.type, name: draft.name,
      balance: Number(draft.balance) || 0, apr: Number(draft.apr) || 0,
      minPayment: Number(draft.minPayment) || 0, limit: null,
    })]);
    setDraft({ type: "card", name: "", balance: "", apr: "", minPayment: "" });
  };

  const skip = () => up({ onboarded: true });

  const finish = () => {
    const fullBudgets = {};
    // budgets are stored monthly; convert from the frequency the user entered
    for (const c of CATEGORIES) fullBudgets[c.id] = Math.round((Number(budgets[c.id]) || 0) * FREQ_FACTOR[budgetFreq]);
    const inv = DEFAULT_STATE.invest;
    up({
      onboarded: true,
      income: Number(income) || 0,
      incomeFreq,
      debts,
      startingDebt: totalDebt,
      startDate: todayISO(),
      extraPayment: extra,
      budgets: fullBudgets,
      expenses: [],
      recurring: [],
      payments: [],
      invest: {
        ...inv,
        age: Number(goals.age) || inv.age,
        retireAge: Number(goals.retireAge) || inv.retireAge,
        targetMonthly: Number(goals.targetMonthly) || inv.targetMonthly,
      },
    });
  };

  const stepHead = (title, hint) => (
    <div style={{ margin: "18px 0 14px" }}>
      <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 30, lineHeight: 1.15 }}>{title}</div>
      <div style={{ fontSize: 13, color: T.dim, marginTop: 8, lineHeight: 1.5 }}>{hint}</div>
    </div>
  );

  const freqChips = (value, onPick) => (
    <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
      {["weekly", "fortnightly", "monthly"].map(f => (
        <button key={f} onClick={() => onPick(f)} style={{
          flex: 1, background: value === f ? T.goldSoft : T.midnight,
          border: `1px solid ${value === f ? T.gold : T.line}`,
          color: value === f ? T.gold : T.dim,
          borderRadius: 99, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
        }}>{t(f)}</button>
      ))}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.midnight, color: T.white, fontFamily: "'Geist', system-ui, sans-serif", paddingBottom: 40 }}>
      <style>{FONT_CSS}</style>

      <div style={{ padding: "18px 20px 6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 26, letterSpacing: "0.01em" }}>
          Aurio<span style={{ color: T.gold }}>.</span>
        </div>
        <select value={lang} onChange={e => up({ lang: e.target.value })} aria-label={t("Language")} style={{
          background: T.panel, color: T.dim, border: `1px solid ${T.line}`, borderRadius: 8,
          padding: "5px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer",
        }}>
          {LANGS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>
      </div>

      <div style={{ padding: "8px 20px", maxWidth: 560, margin: "0 auto" }}>
        {step === 0 && (
          <>
            <div style={{ textAlign: "center", padding: "48px 8px 8px" }}>
              <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 42, lineHeight: 1.1 }}>{t("Welcome to Aurio")}</div>
              <div style={{ fontSize: 14, color: T.dim, margin: "16px auto 0", lineHeight: 1.6, maxWidth: 420 }}>
                {t("Answer a few quick questions and your Home, Debts, Budget and Invest tabs will be built around your real numbers. It takes about two minutes.")}
              </div>
            </div>
            <Btn style={{ width: "100%", marginTop: 28 }} onClick={() => setStep(1)}>{t("Set up my plan")}</Btn>
            <Btn variant="ghost" style={{ width: "100%", marginTop: 10 }} onClick={skip}>{t("Skip for now — use example data")}</Btn>
            {/* The escape hatch for a returning user on a wiped or new device.
                It has to live HERE, before any number is entered: once this
                screen has written example or hand-typed data, restoring a
                backup stops being obvious and starts being a conflict. */}
            {account?.configured && !account.user && (
              <>
                <Btn variant="ghost" style={{ width: "100%", marginTop: 10 }} onClick={() => setSigningIn(true)}>
                  {t("I already have an account — sign in")}
                </Btn>
                <div style={{ fontSize: 11, color: T.faint, textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
                  {t("Signing in first restores your cloud backup instead of starting over.")}
                </div>
              </>
            )}
            <div style={{ fontSize: 11, color: T.faint, textAlign: "center", marginTop: 16, lineHeight: 1.5 }}>
              {t("You can redo any of this later — every number stays editable inside the app.")}
            </div>
          </>
        )}

        {signingIn && (
          <Sheet title={t("Sign in")} onClose={() => setSigningIn(false)}>
            <div style={{ fontSize: 13, color: T.dim, lineHeight: 1.6, marginBottom: 14 }}>
              {t("Signing in first restores your cloud backup instead of starting over.")}
            </div>
            <SignInPanel account={account} t={t} notify={notify} onSignedIn={() => setSigningIn(false)} />
          </Sheet>
        )}

        {step > 0 && (
          <Label style={{ marginTop: 10 }}>{t("Step {n} of {m}", { n: step, m: OB_STEPS })}</Label>
        )}

        {step === 1 && (
          <>
            {stepHead(t("What do you take home?"), t("Your pay after tax. Pick how often you're paid — we'll do the monthly maths."))}
            <Card>
              <Label style={{ marginBottom: 8 }}>{t("Income (after tax)")}</Label>
              <Input type="number" inputMode="decimal" placeholder="0.00" value={income} onChange={e => setIncome(e.target.value)}
                style={{ fontSize: 26, fontFamily: "'Geist Mono', monospace", textAlign: "center", padding: 14 }} autoFocus />
              {freqChips(incomeFreq, setIncomeFreq)}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                <Label>{t("Currency")}</Label>
                <select value={currency || "NZD"} aria-label={t("Currency")}
                  onChange={e => { applyCurrency(e.target.value); up({ currency: e.target.value }); }}
                  style={{ background: T.midnight, color: T.dim, border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
                </select>
              </div>
            </Card>
          </>
        )}

        {step === 2 && (
          <>
            {stepHead(t("What do you owe?"), t("Add each card or loan — estimates are fine. This powers your payoff plan."))}
            {debts.map(d => (
              <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: "10px 14px", marginBottom: 6 }}>
                <div style={{ fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{d.name}</span>
                  <span style={{ color: T.faint, fontSize: 11, marginLeft: 8 }}><Mono>{d.apr}%</Mono> · {t("min")} <Mono>{fmt(d.minPayment)}</Mono></span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Mono style={{ fontSize: 14 }}>{fmt(d.balance)}</Mono>
                  <button onClick={() => setDebts(debts.filter(x => x.id !== d.id))} style={{ background: "none", border: "none", color: T.faint, cursor: "pointer", fontSize: 15 }}>×</button>
                </div>
              </div>
            ))}
            <Card style={{ marginTop: debts.length ? 8 : 0 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                {DEBT_TYPES.map(x => (
                  <button key={x.id} onClick={() => setDraft({ ...draft, type: x.id })} style={{
                    background: draft.type === x.id ? T.goldSoft : T.midnight,
                    border: `1px solid ${draft.type === x.id ? T.gold : T.line}`,
                    color: draft.type === x.id ? T.gold : T.dim,
                    borderRadius: 99, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}>{t(x.label)}</button>
                ))}
              </div>
              {[
                ["name", "Name", "text"],
                ["balance", "Current balance", "number"],
                ["apr", "APR %", "number"],
                ["minPayment", "Minimum payment / month", "number"],
              ].map(([k, label, type]) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <Label style={{ marginBottom: 5 }}>{t(label)}</Label>
                  <Input type={type} inputMode={type === "number" ? "decimal" : undefined} value={draft[k]}
                    onChange={e => setDraft({ ...draft, [k]: e.target.value })} />
                  {k === "apr" && (
                    <div style={{ fontSize: 11, color: T.faint, marginTop: 5, lineHeight: 1.5 }}>
                      {t("APR is the yearly interest rate you're charged on this debt — you'll find it on your card or loan statement.")}
                    </div>
                  )}
                </div>
              ))}
              <Btn small variant="ghost" style={{ width: "100%" }} onClick={addDraft}>{t("Add this debt")}</Btn>
            </Card>
            {debts.length === 0 && (
              <div style={{ fontSize: 12, color: T.faint, textAlign: "center", marginTop: 12 }}>{t("No debts? Lucky you — just tap Next.")}</div>
            )}
          </>
        )}

        {step === 3 && (
          <>
            {stepHead(t("What does a month cost you?"), t("Rough numbers are perfect. You can fine-tune every category later in Budget."))}
            <Card style={{ marginBottom: 12, padding: "12px 14px" }}>
              <Label style={{ marginBottom: 4 }}>{t("How often do you think about these costs?")}</Label>
              {freqChips(budgetFreq, setBudgetFreq)}
              <div style={{ fontSize: 11, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
                {t("Amounts below are per {c} — we'll convert everything to monthly for your plan.", {
                  c: t(budgetFreq === "weekly" ? "week" : budgetFreq === "fortnightly" ? "fortnight" : "month"),
                })}
              </div>
            </Card>
            {OB_BUDGET_CATS.map(id => {
              const c = CATEGORIES.find(x => x.id === id);
              return (
                <Card key={id} style={{ marginBottom: 8, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{c.emoji} {t(c.label)}</div>
                  <input type="number" inputMode="decimal" placeholder="0" value={budgets[id] ?? ""}
                    onChange={e => setBudgets({ ...budgets, [id]: e.target.value })}
                    style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 8, color: T.white, fontFamily: "'Geist Mono', monospace", fontSize: 13, padding: "8px 10px", width: 96, textAlign: "right" }} />
                </Card>
              );
            })}
          </>
        )}

        {step === 4 && (
          <>
            {stepHead(t("Where are you headed?"), t("Used by the Invest tab to project your passive income."))}
            <Card>
              {[
                ["age", "Age now", DEFAULT_STATE.invest.age],
                ["retireAge", "Retire at", DEFAULT_STATE.invest.retireAge],
                ["targetMonthly", "Target passive income / month", DEFAULT_STATE.invest.targetMonthly],
              ].map(([k, label, ph]) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <Label style={{ marginBottom: 5 }}>{t(label)}</Label>
                  <Input type="number" inputMode="decimal" placeholder={String(ph)} value={goals[k]}
                    onChange={e => setGoals({ ...goals, [k]: e.target.value })} />
                </div>
              ))}
            </Card>
          </>
        )}

        {step === 5 && (
          <>
            {stepHead(t("Here's your starting plan"), t("Drag to see your date move"))}
            <div style={{ textAlign: "center", padding: "8px 8px 14px" }}>
              <Label style={{ color: T.gold }}>{t("You are debt-free on")}</Label>
              <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 44, lineHeight: 1.05, margin: "10px 0 4px" }}>
                {totalDebt <= 0 ? t("Today 🎉") : monthName(sim.freeDate, lang)}
              </div>
            </div>
            {totalDebt > 0 && (
              <Card>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
                  <Label>{t("Extra payment / month")}</Label>
                  <Mono style={{ color: T.gold, fontSize: 18, fontWeight: 600 }}>{fmt(extra)}</Mono>
                </div>
                <input type="range" min="0" max="3000" step="50" value={extra} onChange={e => setExtra(Number(e.target.value))} />
                <div style={{ marginTop: 14 }}>
                  <Curve points={sim.curve}
                    label={t("Debt falling from {a} to zero by {d}.", { a: fmt(totalDebt), d: monthName(sim.freeDate, lang) })} />
                </div>
              </Card>
            )}
          </>
        )}

        {step > 0 && (
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <Btn variant="ghost" onClick={() => setStep(step - 1)}>{t("Back")}</Btn>
            {step < OB_STEPS
              ? <Btn style={{ flex: 1 }} onClick={() => setStep(step + 1)}>{t("Next")}</Btn>
              : <Btn style={{ flex: 1 }} onClick={finish}>{t("Open my plan")}</Btn>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══ MILESTONE CELEBRATION ═══
   Private, full-screen reward shown when a payoff milestone is crossed.
   Deliberately NOT shareable — the win is the user's own. */
function MilestoneModal({ content, t, onClose }) {
  if (!content) return null;
  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 90, background: "rgba(14,17,24,0.92)",
      backdropFilter: "blur(6px)", display: "grid", placeItems: "center", padding: 24,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        maxWidth: 360, width: "100%", textAlign: "center", background: T.panel,
        border: `1px solid ${T.gold}55`, borderRadius: 20, padding: "40px 28px 28px",
        boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
      }}>
        <div style={{ width: 68, height: 68, margin: "0 auto 20px", borderRadius: "50%", background: T.goldSoft, border: `1.5px solid ${T.gold}`, display: "grid", placeItems: "center" }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 34, lineHeight: 1.1, color: T.white }}>{content.title}</div>
        <div style={{ fontSize: 14, color: T.dim, lineHeight: 1.5, margin: "12px 0 24px" }}>{content.sub}</div>
        <Btn style={{ width: "100%" }} onClick={onClose}>{t("Keep going")}</Btn>
      </div>
    </div>
  );
}

/* ═══ ACCOUNT · My Info ═══
   Sheet opened from the header person button. Three states:
   1. Supabase not configured → informative note (app stays local-first)
   2. signed out → email OTP sign-in (signup and login are the same flow)
   3. signed in → email, subscription plan, profile name, sign out,
      plus the admin panel when the signed-in user is in `admins`. */

const PLAN_LABELS = { free: "Free", trial: "Trial", tester: "Tester", paid: "Paid", lifetime: "Lifetime" };
const STATUS_LABELS = { active: "Active", expired: "Expired", cancelled: "Cancelled" };

/* ═══ UPGRADE · Premium plans ═══
   Two Stripe-backed options. A one-time Lifetime tier was retired on
   2026-08-24 — it traded a single payment for an obligation with no end date
   — and replaced by an early-bird intro rate for the first 100 subscribers.
   Existing lifetime grants are untouched and stay Premium forever; this
   retires the buy path, not the entitlement.
   Two Stripe-backed options. Tapping a plan opens Stripe-hosted Checkout
   (via the create-checkout edge function); access is granted server-side by
   the webhook, never here. Displayed prices come from the shared regional
   map (public/pricing.js, mirrored in Stripe currency_options) — DISPLAY
   ONLY: the charging currency is decided by Stripe at checkout from the
   customer's own location, so a spoofed display can't change the charge. */
function planOptions() {
  const P = typeof window !== "undefined" ? window.AURIO_PRICING : null;
  const region = P?.detect?.() || "USD";
  const c = P?.currencies?.[region] || { symbol: "$", monthly: "3.99", yearly: "29", intro: "1.99" };
  return [
    { id: "yearly", name: "Yearly", price: `${c.symbol}${c.yearly}`, per: "/year", note: "Best value — save 40%", featured: true },
    /* The note stays a plain literal so t() can translate it — an
       interpolated price would never match a dict key. */
    { id: "monthly", name: "Monthly", price: `${c.symbol}${c.monthly}`, per: "/month", note: "Early bird: first 100 get 3 months half price" },
  ];
}

function UpgradeSheet({ account, t, notify, onClose }) {
  const [busy, setBusy] = useState(null);
  const PLAN_OPTIONS = planOptions();
  const pick = async (plan) => {
    if (busy) return;
    setBusy(plan);
    const { url, error } = await account.startCheckout(plan);
    if (url) { window.location.href = url; return; }
    setBusy(null);
    notify(error || t("Couldn't start checkout. Try again."));
  };
  return (
    <Sheet title={t("Unlock Premium")} onClose={onClose}>
      <div style={{ fontSize: 13, color: T.dim, lineHeight: 1.6, marginBottom: 14 }}>
        {t("Plan ahead for big bills, test what-if scenarios, sync across devices, unlimited history, CSV export and bill reminders. Cancel anytime.")}
      </div>
      {PLAN_OPTIONS.map(p => (
        <button key={p.id} onClick={() => pick(p.id)} disabled={!!busy} style={{
          width: "100%", textAlign: "left", marginBottom: 10, cursor: busy ? "wait" : "pointer",
          background: p.featured ? T.goldSoft : T.midnight, border: `1px solid ${p.featured ? T.gold : T.line}`,
          borderRadius: 12, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center",
          color: T.white, opacity: busy && busy !== p.id ? 0.5 : 1,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{t(p.name)}</div>
            {p.note && <div style={{ fontSize: 11.5, color: p.featured ? T.gold : T.faint, marginTop: 2 }}>{t(p.note)}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            <Mono style={{ fontSize: 19, fontWeight: 700, color: T.white }}>{p.price}</Mono>
            <span style={{ fontSize: 11, color: T.dim }}>{t(p.per)}</span>
          </div>
        </button>
      ))}
      {busy && <div style={{ fontSize: 12, color: T.faint, textAlign: "center", marginTop: 6 }}>{t("Opening secure checkout…")}</div>}
      <div style={{ fontSize: 10.5, color: T.faint, textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
        {t("Secure payment by Stripe. Your card details never touch Aurio.")}<br />
        {t("Final price is confirmed at checkout for your region.")}
      </div>
    </Sheet>
  );
}

/* The email-code sign-in, on its own so the two places that need it can't
   drift apart: My Info, and the onboarding welcome — where someone returning
   on a wiped or new device has to be able to reach their backup BEFORE
   entering anything, since data typed first is what makes a restore ambiguous. */
function SignInPanel({ account, t, notify, onSignedIn }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [phase, setPhase] = useState("email"); // email → code
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    if (!email.includes("@") || busy) return;
    setBusy(true);
    const { error } = await account.requestCode(email.trim());
    setBusy(false);
    if (error) { notify(t("Couldn't send the code. Check the email and try again.")); return; }
    setPhase("code");
    notify(t("Code sent — check your email."));
  };

  const verify = async () => {
    if (code.trim().length < 6 || busy) return;
    setBusy(true);
    const { error } = await account.verifyCode(email.trim(), code.trim());
    setBusy(false);
    if (error) { notify(t("That code didn't work. Try again.")); return; }
    notify(t("Signed in ✓"));
    setPhase("email"); setCode("");
    if (onSignedIn) onSignedIn();
  };

  if (phase === "email") return (
    <>
      <Label style={{ marginBottom: 5 }}>{t("Email")}</Label>
      <Input type="email" inputMode="email" autoComplete="email" placeholder="you@example.com"
        value={email} onChange={e => setEmail(e.target.value)} autoFocus />
      <div style={{ fontSize: 11, color: T.faint, margin: "8px 0 0", lineHeight: 1.5 }}>
        {t("We'll email you a sign-in code — no password needed.")}
      </div>
      <Btn style={{ width: "100%", marginTop: 14, opacity: busy ? 0.6 : 1 }} onClick={sendCode}>{t("Send code")}</Btn>
    </>
  );
  return (
    <>
      <Label style={{ marginBottom: 5 }}>{t("Enter the code from your email")}</Label>
      <Input inputMode="numeric" autoComplete="one-time-code" placeholder="••••••" value={code}
        onChange={e => setCode(e.target.value)} autoFocus
        style={{ fontSize: 24, fontFamily: "'Geist Mono', monospace", textAlign: "center", letterSpacing: "0.3em" }} />
      <div style={{ fontSize: 11, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
        {t("No code in the email? Tapping its sign-in link works too — this app signs in automatically.")}
      </div>
      <Btn style={{ width: "100%", marginTop: 14, opacity: busy ? 0.6 : 1 }} onClick={verify}>{t("Verify & sign in")}</Btn>
      <Btn small variant="ghost" style={{ width: "100%", marginTop: 8 }} onClick={() => { setPhase("email"); setCode(""); }}>{t("Back")}</Btn>
    </>
  );
}

function AccountSheet({ account, t, lang, notify, state, backupNow, replaceState, resetAll, premium, onUpgrade, onClose }) {
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(account.profile?.full_name || "");
  const [confirmErase, setConfirmErase] = useState(false);
  /* null = still asking the server, undefined = never backed up. Read from the
     server rather than from anything this device remembers sending — the point
     of the panel is to answer "is my data actually up there?". */
  const [cloudAt, setCloudAt] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setName(account.profile?.full_name || ""); }, [account.profile?.full_name]);

  useEffect(() => {
    let alive = true;
    if (!account.user || !premium) return;
    fetchCloudSavedAt(account.user).then(r => { if (alive) setCloudAt(r.ok ? (r.savedAt || undefined) : null); });
    return () => { alive = false; };
  }, [account.user?.id, premium]);

  const exportData = () => {
    const ok = downloadFile(`aurio-backup-${todayISO()}.json`, JSON.stringify(state, null, 2), "application/json");
    notify(t(ok ? "Backup downloaded" : "Couldn't export — try again."));
  };

  const importData = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      // Minimal shape check so a random JSON file can't nuke the state.
      if (!parsed || typeof parsed !== "object" || (!Array.isArray(parsed.debts) && !parsed.budgets)) throw new Error("shape");
      if (!confirm(t("Replace the data on this device with this backup?"))) return;
      replaceState(parsed);
      notify(t("Backup restored ✓"));
      onClose();
    } catch {
      notify(t("That file doesn't look like a Aurio backup."));
    }
  };

  /* The value here is often an email address, which has no natural break
     opportunity — `anywhere` is genuinely right for that one case. The LABEL
     truncates instead, so the value keeps its room. */
  const row = (k, v) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: `1px solid ${T.line}`, fontSize: 13, gap: 10 }}>
      <span style={{ color: T.dim, ...ELLIPSIS }}>{k}</span>
      <span style={{ fontWeight: 600, textAlign: "right", overflowWrap: "anywhere" }}>{v}</span>
    </div>
  );

  return (
    <Sheet title={t("My Info")} onClose={onClose}>
      {!account.configured ? (
        <div style={{ fontSize: 13, color: T.dim, lineHeight: 1.6 }}>{t("Account features aren't set up in this build.")}</div>
      ) : !account.user ? (
        <>
          <div style={{ fontSize: 13, color: T.dim, lineHeight: 1.6, marginBottom: 14 }}>
            {t("Sign in to see your plan and keep your profile with Zoldan Labs. Your money data stays on this device.")}
          </div>
          <SignInPanel account={account} t={t} notify={notify} />
        </>
      ) : (
        <>
          {row(t("Email"), account.user.email)}
          {row(t("Subscription plan"), t(PLAN_LABELS[account.access.plan] || "Free"))}
          {row(t("Status"), t(STATUS_LABELS[account.access.status] || "Active"))}
          {account.access.plan === "trial" && account.access.trialExpiresAt &&
            row(t("Trial ends"), new Date(account.access.trialExpiresAt).toLocaleDateString(lang))}
          {account.access.trialExpired && (
            <div style={{ marginTop: 12, background: T.garnetSoft, border: `1px solid ${T.garnet}44`, borderRadius: 10, padding: "11px 12px", fontSize: 12, lineHeight: 1.5 }}>
              {t("Your trial has ended. Upgrade to keep Premium.")}
              <Btn small style={{ marginTop: 8 }} onClick={onUpgrade}>{t("See plans")}</Btn>
            </div>
          )}
          <Label style={{ margin: "16px 0 5px" }}>{t("Full name")}</Label>
          <div style={{ display: "flex", gap: 8 }}>
            <Input placeholder={t("Name")} value={name} onChange={e => setName(e.target.value)} />
            <Btn small variant="ghost" onClick={async () => {
              const { error } = await account.saveName(name.trim());
              notify(error ? t("Couldn't send the code. Check the email and try again.") : t("Saved"));
            }}>{t("Save")}</Btn>
          </div>
          {premium ? (
            <>
              <div style={{ marginTop: 14, background: T.greenSoft, border: `1px solid ${T.green}44`, borderRadius: 10, padding: "9px 12px", fontSize: 12, lineHeight: 1.5 }}>
                {t("✓ Premium is active — cloud sync, unlimited history, CSV export, reminders and full projections are unlocked.")}
                {account.access.creditDaysLeft > 0 && (
                  <div style={{ color: T.greenHi, marginTop: 5, fontWeight: 600 }}>
                    {t("Includes {n} days of referral credit.", { n: account.access.creditDaysLeft })}
                  </div>
                )}
              </div>
              {account.grant?.stripe_customer_id && (
                <Btn variant="ghost" style={{ width: "100%", marginTop: 10 }} onClick={async () => {
                  const { url, error } = await account.openBillingPortal();
                  if (url) window.location.href = url; else notify(error || t("Couldn't open billing. Try again."));
                }}>{t("Manage subscription")}</Btn>
              )}
            </>
          ) : (
            <div style={{ marginTop: 14, background: T.goldSoft, border: `1px solid ${T.gold}55`, borderRadius: 12, padding: "13px 14px" }}>
              <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 19, color: T.white }}>{t("Unlock Premium")}</div>
              <div style={{ fontSize: 12, color: T.dim, lineHeight: 1.5, margin: "6px 0 10px" }}>
                {t("Cloud sync across devices, unlimited history, CSV export, bill reminders and full retirement projections.")}
              </div>
              {/* Permanent home for the trial: the Home offer is one-shot and
                  dismissible, so anyone who waved it away can still find it. */}
              {account.access.canStartTrial && (
                <Btn small style={{ width: "100%", marginBottom: 8 }} onClick={async () => {
                  const { error } = await account.startTrial();
                  if (error) { notify(t("Couldn't start the trial — try again.")); return; }
                  notify(t("Your 14-day Premium trial is live 🎉"));
                }}>{t("Start my free 14-day trial")}</Btn>
              )}
              <Btn small variant={account.access.canStartTrial ? "ghost" : "gold"} style={{ width: "100%" }} onClick={onUpgrade}>{t("See plans")}</Btn>
            </div>
          )}
          <ReferralCard account={account} t={t} notify={notify} />
          <Btn variant="ghost" style={{ width: "100%", marginTop: 18 }} onClick={async () => {
            await account.signOut();
            notify(t("Signed out"));
          }}>{t("Sign out")}</Btn>
          {account.isAdmin && <AdminPanel account={account} notify={notify} />}
        </>
      )}

      <div style={{ marginTop: 22, borderTop: `1px solid ${T.line}`, paddingTop: 14 }}>
        {account.user && premium && (() => {
          const stamp = cloudAt ? new Date(cloudAt) : null;
          /* Stale is worth naming: a backup that quietly stopped days ago looks
             identical to a healthy one unless the age is shown. */
          const staleDays = stamp ? Math.floor((Date.now() - stamp.getTime()) / 86400000) : null;
          const stale = staleDays != null && staleDays >= 2;
          const run = async () => {
            if (backingUp) return;
            setBackingUp(true);
            const r = await backupNow();
            setBackingUp(false);
            if (r?.ok) {
              setCloudAt(r.savedAt);
              notify(t("Backed up to your account ✓"));
            } else if (r?.error === "write-not-persisted") {
              notify(t("The server didn't keep that save. Nothing was lost here — keep a file backup and report this."));
            } else if (r?.reason === "not-ready") {
              notify(t("Still checking your account — try again in a moment."));
            } else {
              notify(t("Couldn't back up. Check your connection and try again."));
            }
          };
          return (
            <div style={{ marginBottom: 18 }}>
              <Label style={{ marginBottom: 8 }}>{t("Cloud backup")}</Label>
              <div style={{ fontSize: 11.5, color: stale ? T.gold : T.faint, lineHeight: 1.5, marginBottom: 10 }}>
                {cloudAt === null ? t("Checking your account…")
                  : cloudAt === undefined ? t("Nothing backed up to your account yet.")
                  : t("Last backup: {d}", { d: stamp.toLocaleString(lang, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) })}
                {stale && ` · ${t("that's {n} days ago", { n: staleDays })}`}
              </div>
              <Btn small variant="ghost" onClick={run} style={{ opacity: backingUp ? 0.6 : 1 }}>
                {backingUp ? t("Backing up…") : t("Back up now")}
              </Btn>
              <div style={{ fontSize: 11, color: T.faint, lineHeight: 1.5, marginTop: 8 }}>
                {t("Aurio backs up on its own a moment after every change — this is here for when you want to be certain.")}
              </div>
            </div>
          );
        })()}

        <Label style={{ marginBottom: 8 }}>{t("Backup & restore")}</Label>
        <div style={{ fontSize: 11.5, color: T.faint, lineHeight: 1.5, marginBottom: 10 }}>
          {t("Download a backup file of everything in the app — you can restore it here on any device, any time.")}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn small variant="ghost" onClick={exportData}>{t("Download backup")}</Btn>
          <Btn small variant="ghost" onClick={() => fileRef.current?.click()}>{t("Restore backup")}</Btn>
          <Btn small variant="ghost" onClick={() => {
            if (!premium) { onUpgrade(); return; }
            let ok = false;
            try { ok = downloadFile("aurio-all.csv", buildCsv(state, t, null), "text/csv;charset=utf-8"); }
            catch (err) { console.error("[Aurio] csv build failed", err); }
            notify(t(ok ? "CSV exported" : "Couldn't export — try again."));
          }}>{t("Export all as CSV")}{!premium && " 🔒"}</Btn>
          <input ref={fileRef} type="file" accept="application/json,.json" style={{ display: "none" }} onChange={importData} />
        </div>
      </div>

      <div style={{ marginTop: 22, borderTop: `1px solid ${T.garnet}44`, paddingTop: 14 }}>
        <Label style={{ color: T.garnet, marginBottom: 8 }}>{t("Danger zone")}</Label>
        {!confirmErase ? (
          <Btn small variant="danger" onClick={() => setConfirmErase(true)}>{t("Erase all data")}</Btn>
        ) : (
          <>
            <div style={{ fontSize: 12.5, background: T.garnetSoft, border: `1px solid ${T.garnet}55`, borderRadius: 10, padding: "10px 12px", lineHeight: 1.6, marginBottom: 10 }}>
              {t("⚠️ This permanently erases all debts, expenses, budgets and settings from this device and your cloud backup. This cannot be undone.")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Btn small onClick={async () => { exportData(); await resetAll(); onClose(); }}>{t("Download backup, then erase")}</Btn>
              <Btn small variant="danger" onClick={async () => { await resetAll(); onClose(); }}>{t("Erase without backup")}</Btn>
              <Btn small variant="ghost" onClick={() => setConfirmErase(false)}>{t("Cancel")}</Btn>
            </div>
          </>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: 18, display: "flex", gap: 16, justifyContent: "center" }}>
        <a href="/privacy.html" target="_blank" rel="noopener" style={{ color: T.faint, fontSize: 12, textDecoration: "none", borderBottom: `1px solid ${T.line}` }}>
          {t("Privacy")}
        </a>
        <a href="/terms.html" target="_blank" rel="noopener" style={{ color: T.faint, fontSize: 12, textDecoration: "none", borderBottom: `1px solid ${T.line}` }}>
          {t("Terms")}
        </a>
      </div>
    </Sheet>
  );
}

/* Admin panel — visible only to users in the `admins` table; every
   operation is re-checked server-side (is_admin()) so this UI is a
   convenience, not the security boundary. Intentionally English-only. */
function AdminPanel({ account, notify }) {
  const [users, setUsers] = useState(null);
  const [edits, setEdits] = useState({});
  const [invites, setInvites] = useState(null);
  const [invEmail, setInvEmail] = useState("");
  const [invDays, setInvDays] = useState(14);

  const load = async () => {
    const { data, error } = await account.adminListUsers();
    if (error) { notify("Admin list failed: " + error.message); return; }
    setUsers(data || []);
  };
  const loadInvites = async () => {
    const { data, error } = await account.adminListInvites();
    if (!error) setInvites(data || []);
  };
  useEffect(() => { load(); loadInvites(); }, []);

  const [inviting, setInviting] = useState(false);

  const sendInvite = async () => {
    const email = invEmail.trim().toLowerCase();
    if (!email.includes("@")) return;
    setInviting(true);
    const { error } = await account.adminInvite({ email, days: invDays });
    setInviting(false);
    if (error) {
      notify(error.alreadyExists
        ? `${email} already has an account — grant their trial from Users & access above.`
        : "Invite failed: " + error.message);
      return;
    }
    notify(`${email} invited — account created, trial active, invite email sent`);
    setInvEmail("");
    loadInvites();
    load();
  };

  /* Personal outreach beats an automated system email for early testers —
     open a prefilled bilingual draft in the admin's own mail client. */
  const inviteMailto = (email, days) => {
    const appUrl = window.location.origin; // wherever this build is served from — no hardcoded domain
    const subject = encodeURIComponent("Your Aurio trial invite · Seu convite para o Aurio");
    const body = encodeURIComponent(
      `Hi!\n\nI'd love your feedback on Aurio, our personal finance app. Your ${days}-day full-access trial is ready — sign in with this email address and it activates automatically:\n\n${appUrl}\n\n---\n\nOlá!\n\nAdoraria seu feedback sobre o Aurio, nosso app de finanças pessoais. Seu teste de ${days} dias com acesso completo está pronto — entre com este endereço de e-mail e ele ativa automaticamente:\n\n${appUrl}\n\n— Zoldan Labs`
    );
    return `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const editFor = (u) => edits[u.user_id] || {
    plan: u.plan, status: u.status,
    trial: u.trial_expires_at ? u.trial_expires_at.slice(0, 10) : "",
    notes: u.notes || "",
  };
  const setEdit = (id, patch) => setEdits({ ...edits, [id]: { ...editFor(users.find(u => u.user_id === id)), ...patch } });

  const save = async (u) => {
    const e = editFor(u);
    const { error } = await account.adminSetAccess({
      userId: u.user_id, plan: e.plan, status: e.status,
      trialExpiresAt: e.plan === "trial" && e.trial ? new Date(e.trial + "T23:59:59").toISOString() : null,
      notes: e.notes,
    });
    if (error) { notify("Save failed: " + error.message); return; }
    notify(`Access updated for ${u.email}`);
    load();
  };

  const sel = (value, options, labels, onChange) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      background: T.midnight, color: T.white, border: `1px solid ${T.line}`, borderRadius: 8,
      padding: "6px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1,
    }}>
      {options.map(o => <option key={o} value={o}>{labels ? labels[o] : o}</option>)}
    </select>
  );

  return (
    <div style={{ marginTop: 22, borderTop: `1px solid ${T.gold}44`, paddingTop: 14 }}>
      <Label style={{ color: T.gold, marginBottom: 10 }}>Admin · users & access</Label>
      {users === null && <div style={{ fontSize: 12, color: T.faint }}>Loading…</div>}
      {users?.length === 0 && <div style={{ fontSize: 12, color: T.faint }}>No users yet.</div>}
      {users?.map(u => {
        const e = editFor(u);
        return (
          <div key={u.user_id} style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 12, padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{u.email}</div>
            <div style={{ fontSize: 11, color: T.faint, margin: "3px 0 10px" }}>
              {u.full_name ? `${u.full_name} · ` : ""}
              last seen {u.last_seen_at ? new Date(u.last_seen_at).toLocaleDateString() : "never"}
              {u.app_version ? ` · v${u.app_version}` : ""}{u.platform ? ` · ${u.platform}` : ""}
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              {sel(e.plan, PLANS, PLAN_LABELS, v => setEdit(u.user_id, { plan: v }))}
              {sel(e.status, STATUSES, STATUS_LABELS, v => setEdit(u.user_id, { status: v }))}
            </div>
            {e.plan === "trial" && (
              <div style={{ marginBottom: 8 }}>
                <Label style={{ marginBottom: 4 }}>Trial ends</Label>
                <Input type="date" value={e.trial} onChange={ev => setEdit(u.user_id, { trial: ev.target.value })} style={{ padding: "8px 10px", fontSize: 13 }} />
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <Input placeholder="Notes (e.g. friend tester)" value={e.notes}
                onChange={ev => setEdit(u.user_id, { notes: ev.target.value })} style={{ padding: "8px 10px", fontSize: 12 }} />
              <Btn small onClick={() => save(u)}>Save</Btn>
            </div>
          </div>
        );
      })}

      <Label style={{ color: T.gold, margin: "20px 0 10px" }}>Admin · trial invites</Label>
      <div style={{ fontSize: 11, color: T.faint, lineHeight: 1.5, marginBottom: 8 }}>
        "Invite" creates their account now, activates the trial immediately, and sends them a real sign-in email — they'll appear above right away. "Draft email" is optional, for a personal-touch follow-up from your own inbox.
      </div>
      <Input type="email" inputMode="email" placeholder="friend@example.com" value={invEmail}
        onChange={e => setInvEmail(e.target.value)} style={{ padding: "8px 10px", fontSize: 13 }} />
      <div style={{ display: "flex", gap: 6, margin: "8px 0 10px", alignItems: "center" }}>
        {[7, 14, 30].map(d => (
          <button key={d} onClick={() => setInvDays(d)} style={{
            background: invDays === d ? T.goldSoft : T.midnight,
            border: `1px solid ${invDays === d ? T.gold : T.line}`,
            color: invDays === d ? T.gold : T.dim,
            borderRadius: 99, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>{d} days</button>
        ))}
        <Btn small style={{ marginLeft: "auto" }} onClick={sendInvite} disabled={inviting}>{inviting ? "Inviting…" : "Invite"}</Btn>
      </div>
      {invites?.length === 0 && <div style={{ fontSize: 12, color: T.faint }}>No invites yet.</div>}
      {invites?.map(i => (
        <div key={i.email} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 10, padding: "8px 12px", marginBottom: 6, gap: 8 }}>
          <div style={{ fontSize: 12, overflowWrap: "anywhere" }}>
            {i.email}
            <span style={{ color: T.faint, fontSize: 11, marginLeft: 6 }}>
              {i.trial_days}d · {i.claimed_at ? `claimed ${new Date(i.claimed_at).toLocaleDateString()}` : "pending"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {!i.claimed_at && (
              <>
                <a href={inviteMailto(i.email, i.trial_days)} style={{ color: T.gold, fontSize: 11, fontWeight: 600, textDecoration: "none" }}>Draft email</a>
                <button onClick={async () => {
                  const { error } = await account.adminRevokeInvite(i.email);
                  if (error) { notify("Revoke failed: " + error.message); return; }
                  loadInvites();
                }} style={{ background: "none", border: "none", color: T.faint, cursor: "pointer", fontSize: 15 }}>×</button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══ HOME ═══ */

/* Upcoming-payments planner: what's due in the next 30 days — recurring
   bills AND dated debt minimums — how much to set aside before the next
   payday, and the reminder opt-in. */
function BillPlanner({ state, up, t, lang, notify, premium, requirePremium }) {
  const [openBill, setOpenBill] = useState(null); // before the early return — hooks can't be conditional
  const debtDues = upcomingDebtPayments(state, 30);
  if (!(state.recurring || []).length && !debtDues.length) return null;

  const bills = [...upcomingBills(state, 30), ...debtDues].sort((a, b) => a.days - b.days);
  const payday = Math.min(28, Math.max(1, Number(state.payday) || 20));
  const nextPay = nextPaydayDate(payday);
  /* "Paid ahead" is a pure marker — ticking it off doesn't log an expense
     or move the due date; the real auto-log still fires on schedule. It
     only affects what's shown here: the set-aside total, and (elsewhere)
     which bills still get a reminder. It self-clears once the bill rolls
     to its next occurrence (see processRecurring).

     Debt minimums don't use it: for a debt we can read the answer straight
     off the ledger (`covered`), so the tick is unnecessary and can't drift. */
  const paidAhead = state.billsPaidAhead || {};
  const isPaidAhead = (b) => b.isDebt ? Boolean(b.covered) : Boolean(paidAhead[`${b.id}:${b.nextDue}`]);
  const togglePaidAhead = (b) => {
    const key = `${b.id}:${b.nextDue}`;
    const map = { ...paidAhead };
    if (map[key]) delete map[key]; else map[key] = true;
    up({ billsPaidAhead: map });
  };
  /* Counts every occurrence, so a weekly bill lands in here four or five
     times — which is the point: this total is what actually has to be found
     before payday, and counting each bill once understated it fourfold. */
  const beforePayday = bills.filter(b => new Date(b.nextDue + "T12:00") < nextPay && !isPaidAhead(b) && b.amount > 0);
  const setAside = beforePayday.reduce((s, b) => s + b.amount, 0);

  /* One row per bill rather than per occurrence: five weekly bills would
     otherwise push ~20 rows onto Home and bury everything else. The row
     carries the soonest date and ×n; opening it reveals the individual
     dates, each with its own tick — you might have handled one and not the
     rest. Debt minimums are monthly, so they never group. */
  const groups = [];
  const byBill = new Map();
  for (const b of bills) {
    let g = byBill.get(b.id);
    if (!g) { g = { id: b.id, first: b, items: [], total: 0 }; byBill.set(b.id, g); groups.push(g); }
    g.items.push(b);
    g.total += b.amount;
  }
  const remindersOn = Boolean(state.reminders?.enabled) && typeof Notification !== "undefined" && Notification.permission === "granted";
  const leadDays = state.reminders?.leadDays ?? 3;

  const dueLabel = (b) =>
    b.days === 0 ? t("today") : b.days === 1 ? t("tomorrow") : t("in {d} days", { d: b.days });

  const toggleReminders = async () => {
    if (remindersOn) {
      up({ reminders: { ...state.reminders, enabled: false } });
      /* Drop the server-side endpoint too, or reminders keep arriving with the
         app closed after the user has plainly said stop. */
      if (pushSupported()) unsubscribeFromPush();
      notify(t("Bill reminders off"));
      return;
    }
    if (!requirePremium()) return; // reminders are Premium
    if (typeof Notification === "undefined") {
      notify(t("Notifications aren't supported in this browser."));
      return;
    }
    let perm = Notification.permission;
    if (perm === "default") perm = await Notification.requestPermission();
    if (perm !== "granted") {
      notify(t("Notifications are blocked — allow them in your browser settings."));
      return;
    }
    up({ reminders: { ...(state.reminders || {}), enabled: true, leadDays } });
    /* On-open notifications work for everyone; delivery while the app is
       CLOSED needs a registered push endpoint, which needs an account. Say
       which of the two they actually got rather than implying the stronger
       one — the feature is sold on arriving when you are not looking. */
    const pushed = pushSupported() ? await subscribeToPush() : false;
    notify(pushed ? t("Bill reminders on 🔔") : t("Reminders on 🔔 — they'll show when you open Aurio. Sign in to get them with the app closed."));
  };

  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Label>{t("Upcoming payments")}</Label>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.faint }}>
          {t("Payday")}
          <NumField inputMode="numeric" min="1" max="28" value={payday}
            onCommit={n => up({ payday: Math.min(28, Math.max(1, n || 20)) })}
            style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 8, color: T.gold, fontFamily: "'Geist Mono', monospace", fontSize: 12, fontWeight: 600, padding: "5px 6px", width: 44, textAlign: "center" }} />
        </div>
      </div>

      {bills.length === 0 ? (
        <div style={{ fontSize: 12, color: T.faint, marginTop: 10 }}>{t("Nothing due in the next 30 days.")}</div>
      ) : (
        <div style={{ marginTop: 10 }}>
          {groups.map(g => {
            const many = g.items.length > 1;
            const isOpen = openBill === g.id;
            const allPaid = g.items.every(isPaidAhead);
            /* The headline date is the next one still owed, not the next one
               overall — once you've ticked this week's rent, "in 2 days" is
               about the one you've handled and reads as stale. */
            const head = g.items.find(x => !isPaidAhead(x)) || g.first;
            const checked = many ? allPaid : isPaidAhead(head);
            const c = categoriesOf(state).find(x => x.id === head.category);
            const urgent = head.days <= leadDays && !checked;

            const tickMark = (on) => on && (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.midnight} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            );
            const tickStyle = (on) => ({
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0, padding: 0,
              background: on ? T.gold : "transparent", border: `1.5px solid ${on ? T.gold : T.line}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            });

            return (
              <div key={g.id} style={{ borderBottom: `1px solid ${T.line}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", fontSize: 13, opacity: checked ? 0.55 : 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                    {/* A repeating bill takes the caret instead of a tick: one
                        tick can't honestly represent four dates you may have
                        handled separately, so you open it and tick the one you
                        actually paid. A debt minimum's tick is a read-out of
                        the ledger — you clear it by logging on the Debts tab —
                        so it renders as a static mark. */}
                    {many ? (
                      <button
                        onClick={() => setOpenBill(isOpen ? null : g.id)}
                        aria-label={t(isOpen ? "Hide dates" : "Show each date")}
                        title={t(isOpen ? "Hide dates" : "Show each date")}
                        aria-expanded={isOpen}
                        style={{ ...tickStyle(false), border: "none", background: "none", color: T.faint, fontSize: 10, cursor: "pointer" }}>
                        {isOpen ? "▼" : "▶"}
                      </button>
                    ) : head.isDebt ? (
                      <span style={tickStyle(checked)} title={t(checked ? "Minimum already paid this cycle" : "Log this on the Debts tab")} aria-hidden="true">{tickMark(checked)}</span>
                    ) : (
                      <button
                        onClick={() => togglePaidAhead(head)}
                        aria-label={t(checked ? "Mark as not paid ahead" : "Mark as paid ahead")}
                        title={t(checked ? "Mark as not paid ahead" : "Mark as paid ahead")}
                        style={{ ...tickStyle(checked), cursor: "pointer" }}>
                        {tickMark(checked)}
                      </button>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <span style={{ marginRight: 7 }}>{head.isDebt ? "💳" : c?.emoji}</span>{head.name}
                      <span style={{ color: T.dim, fontSize: 11, marginLeft: 7 }}>· {head.isDebt ? t("minimum") : freqLabel(t, head.freq)}</span>
                      <span style={{ color: checked ? T.greenHi : urgent ? T.gold : T.faint, fontSize: 11, marginLeft: 7, fontWeight: urgent ? 700 : 400 }}>
                        · {checked ? t(head.isDebt ? "paid this cycle" : "paid ahead") : dueLabel(head)}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexShrink: 0, marginLeft: 8 }}>
                    {many && <span style={{ fontSize: 10.5, color: T.gold, fontWeight: 700 }}>{t("×{n}", { n: g.items.length })}</span>}
                    <Mono style={{ fontSize: 13, textDecoration: checked ? "line-through" : "none", color: checked ? T.faint : T.white }}>{fmt(g.total, 2)}</Mono>
                  </div>
                </div>
                {many && isOpen && (
                  <div style={{ paddingBottom: 8 }}>
                    {g.items.map(x => {
                      const on = isPaidAhead(x);
                      return (
                        <div key={x.occurrenceKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0 5px 27px", fontSize: 12.5, opacity: on ? 0.55 : 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                            <button
                              onClick={() => togglePaidAhead(x)}
                              aria-label={t(on ? "Mark as not paid ahead" : "Mark as paid ahead")}
                              title={t(on ? "Mark as not paid ahead" : "Mark as paid ahead")}
                              style={{ ...tickStyle(on), width: 16, height: 16, cursor: "pointer" }}>
                              {tickMark(on)}
                            </button>
                            <span style={{ color: on ? T.greenHi : T.dim }}>
                              {new Date(x.nextDue + "T12:00").toLocaleDateString(lang, { weekday: "short", day: "numeric", month: "short" })}
                              {on && <> · {t("paid ahead")}</>}
                            </span>
                          </div>
                          <Mono style={{ fontSize: 12.5, textDecoration: on ? "line-through" : "none", color: on ? T.faint : T.dim }}>{fmt(x.amount, 2)}</Mono>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {beforePayday.length > 0 && (
            <div style={{ marginTop: 10, background: T.goldSoft, border: `1px solid ${T.gold}33`, borderRadius: 10, padding: "8px 12px", fontSize: 12, lineHeight: 1.5 }}>
              {t("💰 Set aside {a} for the {n} payments due before your payday on {d}.", {
                a: fmt(setAside), n: beforePayday.length,
                d: nextPay.toLocaleDateString(lang, { day: "numeric", month: "short" }),
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 12, borderTop: `1px solid ${T.line}`, paddingTop: 11 }}>
        <div style={{ fontSize: 12, color: T.dim, lineHeight: 1.4 }}>
          {t("🔔 Remind me when the app opens up to {n} days before each payment", { n: leadDays })}
        </div>
        <button onClick={toggleReminders} aria-label={t("Upcoming payments")} style={{
          width: 40, height: 22, borderRadius: 99, flexShrink: 0, cursor: "pointer", position: "relative",
          background: remindersOn ? T.gold : T.line, border: "none", transition: "background 0.2s",
        }}>
          <span style={{ position: "absolute", top: 3, left: remindersOn ? 21 : 3, width: 16, height: 16, borderRadius: "50%", background: T.midnight, transition: "left 0.2s" }} />
        </button>
      </div>
    </Card>
  );
}

/* Referral card — "give a friend a month, get a month".
   Deliberately the only thing Aurio lets you share: an opaque code, never a
   number from your finances. That's the whole reason this replaced the
   rejected debt-sharing idea — debt is shame-loaded, and a growth loop that
   asks people to broadcast it would cost more trust than it earns.

   The friend's month is instant; yours lands once they're still using the app
   a week later, which the copy states plainly rather than burying — a reward
   that silently fails to arrive is worse than one that was never promised. */
function ReferralCard({ account, t, notify }) {
  const [code, setCode] = useState(null);
  const [stats, setStats] = useState({ rewarded: 0, pending: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [c, s] = await Promise.all([account.myReferralCode(), account.referralStats()]);
      if (!alive) return;
      if (c.code) setCode(c.code);
      setStats(s);
    })();
    return () => { alive = false; };
  }, [account.user?.id]);

  if (!code) return null;
  const link = `${window.location.origin}/?ref=${code}`;

  const share = async () => {
    const text = t("I'm using Aurio to clear my debt — here's a free month of Premium: {link}", { link });
    setBusy(true);
    try {
      if (navigator.share) await navigator.share({ title: "Aurio", text });
      else { await navigator.clipboard.writeText(link); notify(t("Invite link copied")); }
    } catch { /* user cancelled the share sheet — not an error */ }
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 22, borderTop: `1px solid ${T.line}`, paddingTop: 14 }}>
      <Label style={{ marginBottom: 8 }}>{t("Give a month, get a month")}</Label>
      <div style={{ fontSize: 11.5, color: T.faint, lineHeight: 1.55, marginBottom: 10 }}>
        {t("Your friend gets a free month of Premium straight away. You get one too, once they're still using Aurio a week later.")}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 12px" }}>
        <Mono style={{ fontSize: 18, fontWeight: 600, color: T.gold, letterSpacing: "0.14em", flex: 1 }}>{code}</Mono>
        <Btn small variant="ghost" onClick={async () => {
          try { await navigator.clipboard.writeText(link); notify(t("Invite link copied")); }
          catch { notify(t("Couldn't copy — long-press the code instead.")); }
        }}>{t("Copy link")}</Btn>
      </div>
      <Btn style={{ width: "100%", marginTop: 10 }} onClick={share} disabled={busy}>{t("Invite a friend")}</Btn>
      {(stats.rewarded > 0 || stats.pending > 0) && (
        <div style={{ fontSize: 11.5, color: T.dim, marginTop: 10, lineHeight: 1.5 }}>
          {stats.rewarded > 0 && (
            <span style={{ color: T.greenHi, fontWeight: 600 }}>
              {t(stats.rewarded === 1 ? "{n} free month earned" : "{n} free months earned", { n: stats.rewarded })}
            </span>
          )}
          {stats.rewarded > 0 && stats.pending > 0 && " · "}
          {stats.pending > 0 && t(stats.pending === 1 ? "{n} friend still to qualify" : "{n} friends still to qualify", { n: stats.pending })}
        </div>
      )}
    </div>
  );
}

/* Self-serve trial offer, shown once at the debt-free-date reveal — the one
   moment the app has actually delivered something worth paying for. An opt-in
   trial converts far better than a plain freemium prompt, but only if it lands
   here rather than at a paywall the user hit by accident.

   Two entry states, because Aurio works fully offline: a signed-out user has
   nowhere to put a grant, so their CTA opens sign-in first and the offer
   reappears afterwards. Dismissal is permanent (a nag would cost more trust
   than the conversion is worth) — My Info keeps a quiet permanent entry. */
function TrialOffer({ account, t, notify, onOpenAccount, onDismiss }) {
  const [busy, setBusy] = useState(false);
  if (!account?.configured) return null;
  const signedIn = Boolean(account.user);
  if (signedIn && !account.access.canStartTrial) return null;

  const start = async () => {
    if (!signedIn) { onOpenAccount(); return; }
    setBusy(true);
    const { error } = await account.startTrial();
    setBusy(false);
    if (error) {
      notify(t("Couldn't start the trial — try again."));
      console.warn("[Aurio] start trial failed", error);
      return;
    }
    onDismiss();
    notify(t("Your 14-day Premium trial is live 🎉"));
  };

  return (
    <Card style={{ marginTop: 14, borderColor: `${T.gold}55`, background: T.goldSoft, position: "relative" }}>
      <button onClick={onDismiss} aria-label={t("Dismiss")} title={t("Dismiss")}
        style={{ position: "absolute", top: 10, right: 12, background: "none", border: "none", color: T.faint, fontSize: 16, cursor: "pointer", lineHeight: 1 }}>×</button>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.gold, paddingRight: 20 }}>
        {t("Try Premium free for 14 days")}
      </div>
      <div style={{ fontSize: 12.5, color: T.white, lineHeight: 1.6, marginTop: 8 }}>
        {t("Sync across your devices, unlimited history, CSV export and bill reminders. No card needed — it simply ends on its own.")}
      </div>
      <Btn style={{ width: "100%", marginTop: 12 }} onClick={start} disabled={busy}>
        {busy ? t("Starting…") : signedIn ? t("Start my free trial") : t("Create a free account to start")}
      </Btn>
    </Card>
  );
}

function Home({ state, up, t, lang, notify, premium, requirePremium, account, onOpenAccount, sim, simAlt, simZero, totalDebt, pct, paidOff, startingDebt, leftover, spentThisMonth, totalBudget, monthExpenses, map, setTab, goGrow }) {
  const free = sim.freeDate;
  const monthsSooner = simZero.months - sim.months;
  const interestSaved = simZero.totalInterest - sim.totalInterest;
  const stratDelta = simAlt.totalInterest - sim.totalInterest;

  return (
    <>
      <div style={{ textAlign: "center", padding: "20px 8px 10px" }}>
        <Label style={{ color: T.gold }}>{t("You are debt-free on")}</Label>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: sim.neverPaysOff ? 32 : 52, lineHeight: 1.05, margin: "10px 0 6px", color: sim.neverPaysOff ? T.garnet : T.white }}>
          {totalDebt <= 0 ? t("Today 🎉") : sim.neverPaysOff ? t("Not on this plan") : monthName(free, lang)}
        </div>
        {totalDebt > 0 && !sim.neverPaysOff && monthsSooner > 0 && simZero.curve[simZero.curve.length - 1] <= 0 && (
          /* Badge only when the minimums-only baseline actually terminates —
             otherwise "interest saved" compounds into a meaningless number. */
          <div style={{ display: "inline-block", background: T.goldSoft, color: T.gold, borderRadius: 99, padding: "5px 14px", fontSize: 13, fontWeight: 600 }}>
            {t(monthsSooner === 1 ? "{n} month sooner" : "{n} months sooner", { n: monthsSooner })} · {fmt(interestSaved)} {t("interest saved")}
          </div>
        )}
      </div>

      {!state.seenTrialOffer && !premium && (
        <TrialOffer account={account} t={t} notify={notify}
          onOpenAccount={onOpenAccount}
          onDismiss={() => up({ seenTrialOffer: true })} />
      )}

      <Card style={{ marginTop: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
          <Label>{t("Extra payment / month")}</Label>
          <Mono style={{ color: T.gold, fontSize: 18, fontWeight: 600 }}>{fmt(state.extraPayment)}</Mono>
        </div>
        {totalDebt > 0 && !state.seenSliderHint && (
          <div style={{ background: T.goldSoft, border: `1px solid ${T.gold}44`, borderRadius: 10, padding: "8px 12px", marginBottom: 12, fontSize: 12.5, color: T.white, lineHeight: 1.5 }}>
            {t("👇 Try it — drag to watch your debt-free date jump.")}
          </div>
        )}
        <input type="range" min="0" max="3000" step="50" value={state.extraPayment}
          onChange={e => up(state.seenSliderHint ? { extraPayment: Number(e.target.value) } : { extraPayment: Number(e.target.value), seenSliderHint: true })} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: T.faint }}>
          <span>$0</span><span>{t("Drag to see your date move")}</span><span>$3,000</span>
        </div>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <Label>{t("Total debt today")}</Label>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 36, marginTop: 4 }}>{fmt(totalDebt)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <Label>{t("Paid off")}</Label>
            <Mono style={{ color: T.greenHi, fontSize: 17, fontWeight: 600 }}>{fmt(paidOff)}</Mono>
          </div>
        </div>
        <div style={{ height: 8, background: T.line, borderRadius: 4, marginTop: 14, overflow: "hidden" }}
          role="img" title={t("{p}% of your {a} starting debt destroyed", { p: pct.toFixed(1), a: fmt(startingDebt) })}
          aria-label={t("{p}% of your {a} starting debt destroyed", { p: pct.toFixed(1), a: fmt(startingDebt) })}>
          <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg, ${T.green}, ${T.gold})`, borderRadius: 4, transition: "width 0.4s" }} />
        </div>
        <div style={{ fontSize: 12, color: T.dim, marginTop: 7 }}>{t("{p}% of your {a} starting debt destroyed", { p: pct.toFixed(1), a: fmt(startingDebt) })}</div>
        {totalDebt > 0 && (() => {
          /* If any minimum can't even cover its interest, the minimums-only
             simulation compounds forever and its total is meaningless —
             warn instead of printing an absurd number. */
          const minNeverEnds = state.debts.some(d => d.balance > 0 && !minOnlyProjection(d));
          return (
            <div style={{ marginTop: 10, borderTop: `1px solid ${T.line}`, paddingTop: 10, fontSize: 12.5, lineHeight: 1.7 }}>
              {sim.curve[sim.curve.length - 1] <= 0 ? (
                <div style={{ color: T.white }}>
                  {t("True cost on your current plan: {a} ({i} interest)", { a: fmt(totalDebt + sim.totalInterest), i: fmt(sim.totalInterest) })}
                </div>
              ) : (
                <div style={{ color: T.garnet }}>
                  {t("⚠️ Your current payments never clear this debt — raise the minimums or the extra payment.")}
                </div>
              )}
              {minNeverEnds ? (
                <div style={{ color: T.garnet }}>
                  {t("⚠️ At minimums only this debt never ends — a minimum doesn't even cover its interest. See Debts.")}
                </div>
              ) : simZero.totalInterest - sim.totalInterest > 1 ? (
                <div style={{ color: T.greenHi }}>
                  {t("At minimums only it would be {a} — your extra payment saves {s}.", { a: fmt(totalDebt + simZero.totalInterest), s: fmt(simZero.totalInterest - sim.totalInterest) })}
                </div>
              ) : null}
            </div>
          );
        })()}
        <div style={{ marginTop: 14 }}>
          <Curve points={sim.curve}
            label={sim.neverPaysOff
              ? t("Debt balance over time — it never reaches zero on this plan.")
              : t("Debt falling from {a} to zero by {d}.", { a: fmt(totalDebt), d: monthName(free, lang) })} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.faint }}>
          <span>{t("Now")}</span><span>{sim.neverPaysOff ? t("Never") : monthName(free, lang)}</span>
        </div>
      </Card>

      <Card style={{ marginTop: 12, borderColor: `${T.gold}33` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Label>{t("Strategy")}</Label>
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4, textTransform: "capitalize" }}>{t(state.strategy)}</div>
            <div style={{ fontSize: 12, color: sim.neverPaysOff ? T.garnet : T.dim, marginTop: 3 }}>
              {sim.neverPaysOff ? t("⚠️ Neither strategy clears this debt yet")
                : stratDelta > 1 ? t("Saves {a} vs {s}", { a: fmt(stratDelta), s: t(otherStrategy(state.strategy)) })
                : t("Same cost as the alternative")}
            </div>
          </div>
          <Btn small variant="ghost" onClick={() => up({ strategy: otherStrategy(state.strategy) })}>
            {t("Switch")}
          </Btn>
        </div>
      </Card>

      {/* Deliberately free. Telling someone a month ahead will break them is a
          safety message, not a feature to sell — and it's the reason to look at
          the plan in Grow, which is where the paid detail lives. Shown only
          when there IS a short month, so it never becomes wallpaper. */}
      {(() => {
        const tight = (map || []).find(m => m.shortfall > 0);
        if (!tight) return null;
        const named = tight.causes.slice(0, 2);
        return (
          <Card style={{ marginTop: 12, background: T.garnetSoft, borderColor: `${T.garnet}55` }}>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: T.white }}>
              {t("⚠️ {m} looks short by {a}.", { m: monthName(tight.monthStart, lang), a: fmt(tight.shortfall) })}
              {named.length > 0 && " " + t("{names} land that month.", {
                names: named.map(b => `${b.name} (${fmt(b.amount)})`).join(t(" and ")),
              })}
            </div>
            <div style={{ fontSize: 12, color: T.dim, marginTop: 8, lineHeight: 1.5 }}>
              {t("Setting aside {a}/month between now and then covers it.", {
                /* Divide by the months BEFORE the bill lands, matching the
                   big-bills list in Grow — the index is already that count,
                   and the two figures describe the same runway. */
                a: fmt(tight.shortfall / Math.max(1, (map || []).indexOf(tight))),
              })}
            </div>
            <Btn small variant="ghost" style={{ marginTop: 10 }} onClick={() => goGrow("save")}>
              {t("Plan for it")}
            </Btn>
          </Card>
        );
      })()}

      <BillPlanner state={state} up={up} t={t} lang={lang} notify={notify} premium={premium} requirePremium={requirePremium} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <Card>
          <Label>{t("Spent this month")}</Label>
          <Mono style={{ fontSize: 20, fontWeight: 600, display: "block", marginTop: 6, color: spentThisMonth > totalBudget ? T.garnet : T.white }}>{fmt(spentThisMonth)}</Mono>
          <div style={{ fontSize: 11, color: T.faint, marginTop: 3 }}>{t("of {a} budget · {n} expenses", { a: fmt(totalBudget), n: monthExpenses.length })}</div>
        </Card>
        <Card>
          <Label>{t("Left after plan")}</Label>
          <Mono style={{ fontSize: 20, fontWeight: 600, display: "block", marginTop: 6, color: leftover < 0 ? T.garnet : T.greenHi }}>{fmt(leftover)}</Mono>
          <div style={{ fontSize: 11, color: T.faint, marginTop: 3 }}>{t("income − budget − minimums − extra")}</div>
        </Card>
      </div>

      {leftover > 100 && (() => {
        const boosted = simulate(state.debts, state.extraPayment + leftover, state.strategy);
        if (boosted.neverPaysOff) return null; // same APR-outpacing problem — nothing useful to suggest here
        return (
          <Card style={{ marginTop: 12, background: T.goldSoft, borderColor: `${T.gold}44` }}>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              {t("💡 You have {a} unassigned. Sending it to debt moves your debt-free date to {d}.", {
                a: fmt(leftover),
                d: monthName(boosted.freeDate, lang),
              })}
            </div>
            <Btn small style={{ marginTop: 10 }} onClick={() => up({ extraPayment: state.extraPayment + leftover })}>{t("Send it to debt")}</Btn>
          </Card>
        );
      })()}
    </>
  );
}

/* ═══ DEBTS ═══ */
function Debts({ state, up, t, lang, sim, simAlt, notify }) {
  const [editing, setEditing] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [payDate, setPayDate] = useState(todayISO());
  const [chargeFor, setChargeFor] = useState(null);
  const [chargeAmt, setChargeAmt] = useState("");
  const [chargeNote, setChargeNote] = useState("");
  const [chargeDate, setChargeDate] = useState(todayISO());
  /* NO_CATEGORY covers balance transfers, cash advances and annual fees —
     real ledger entries that aren't consumption and mustn't skew a budget. */
  const [chargeCat, setChargeCat] = useState(state.lastChargeCategory || "groceries");
  const [expanded, setExpanded] = useState(null);
  /* Statement-day row stays folded away unless the user asks for it, or has
     already set one — the due date alone is enough for most people. */
  const [showCycle, setShowCycle] = useState(false);
  const openEditor = (d) => { setEditing(d); setShowCycle(d.statementDay != null); };

  /* Entries can be dated back to the start of the OPEN statement cycle, but no
     further: interest for a closed cycle is already posted, and moving money
     into that window would silently invalidate it. Never into the future. */
  const earliestEntryDate = (d) => d?.lastInterestDate || closeOnOrBefore(todayISO(), statementDayOf(d || {}));
  const clampEntryDate = (d, iso) => {
    const min = earliestEntryDate(d), max = todayISO();
    return !iso ? max : iso < min ? min : iso > max ? max : iso;
  };

  const order = [...state.debts].sort(byStrategy(state.strategy));
  const target = order.find(d => d.balance > 0);

  const saveDebt = (d) => {
    const next = withAutoMin(normalizeDebt(d));
    const debts = next.id
      ? state.debts.map(x => x.id === next.id ? next : x)
      : [...state.debts, { ...next, id: uid() }];
    up({ debts });
    setEditing(null);
    notify(t(d.id ? "Debt updated" : "Debt added"));
  };

  const logPayment = () => {
    const amt = Number(payAmt);
    if (!amt || amt <= 0) return;
    const date = clampEntryDate(payFor, payDate);
    const balanceBefore = payFor.balance;
    const debts = state.debts.map(d => d.id === payFor.id ? withAutoMin({ ...d, balance: Math.max(0, d.balance - amt) }) : d);
    up({ debts, payments: [...state.payments, { id: uid(), debtId: payFor.id, amount: amt, date, balanceBefore, type: "payment" }] });
    setPayFor(null); setPayAmt("");
    notify(t("{a} payment logged 🔥", { a: fmt(amt) }));
  };

  /* The mirror of logPayment — for when the card gets used again. Keeps
     the exact same history/undo/auto-min machinery, just adding instead
     of subtracting from the balance. */
  const logCharge = () => {
    const amt = Number(chargeAmt);
    if (!amt || amt <= 0) return;
    const date = clampEntryDate(chargeFor, chargeDate);
    const balanceBefore = chargeFor.balance;
    const debts = state.debts.map(d => d.id === chargeFor.id ? withAutoMin({ ...d, balance: d.balance + amt }) : d);
    const note = chargeNote.trim() || null;
    const chargeId = uid();
    const patch = {
      debts,
      payments: [...state.payments, { id: chargeId, debtId: chargeFor.id, amount: amt, date, balanceBefore, type: "charge", note, category: chargeCat === NO_CATEGORY ? null : chargeCat }],
    };
    /* A card purchase is spending the moment it happens, so it also becomes a
       real expense — that is what puts it in "Where it went" and its category
       budget. `debtId`/`chargeId` mark where it came from: the cash-flow figure
       skips it (nothing left the account yet) and undoing the charge removes
       it again. Mirrors how auto-logged bills carry `recurringId`. */
    if (chargeCat !== NO_CATEGORY) {
      patch.expenses = [{ id: uid(), amount: amt, category: chargeCat, note, date, debtId: chargeFor.id, chargeId }, ...state.expenses];
      patch.lastChargeCategory = chargeCat;
    }
    up(patch);
    setChargeFor(null); setChargeAmt(""); setChargeNote("");
    notify(t(chargeCat === NO_CATEGORY ? "{a} charge logged" : "{a} charge logged to {c}", { a: fmt(amt), c: t(categoriesOf(state).find(c => c.id === chargeCat)?.label || "") }));
  };

  /* Restores the exact pre-payment balance (not just balance + amount —
     an overpayment that was clamped to 0 would otherwise overshoot). Older
     payments logged before this field existed fall back to the simple sum.
     Works identically for charges and auto-posted interest. */
  const undoPayment = (p) => {
    const debts = state.debts.map(d => d.id === p.debtId
      ? withAutoMin({ ...d, balance: balanceBeforeEntry(p, d.balance) })
      : d);
    up({
      debts,
      payments: state.payments.filter(x => x.id !== p.id),
      // A charge wrote a linked expense; undoing must remove it too, or the
      // budget keeps counting spending that no longer exists.
      expenses: (state.expenses || []).filter(e => e.chargeId !== p.id),
    });
    notify(t("Payment undone"));
  };

  return (
    <>
      <Card>
        <Label>{t("Strategy comparison")}</Label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
          {STRATEGIES.map(s => {
            const r = s === state.strategy ? sim : simAlt;
            const active = s === state.strategy;
            return (
              <button key={s} onClick={() => up({ strategy: s })} style={{
                background: active ? T.goldSoft : T.midnight, border: `1px solid ${active ? T.gold : T.line}`,
                borderRadius: 12, padding: 12, textAlign: "left", cursor: "pointer", color: T.white,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: active ? T.gold : T.dim }}>{t(s === "avalanche" ? "Avalanche" : "Snowball")}</div>
                <div style={{ fontSize: 11, color: T.faint, marginBottom: 8 }}>{t(s === "avalanche" ? "Highest APR first" : "Smallest balance first")}</div>
                <Mono style={{ fontSize: 15, fontWeight: 600, display: "block", color: r.neverPaysOff ? T.garnet : T.white }}>
                  {r.neverPaysOff ? t("Never") : monthName(r.freeDate, lang)}
                </Mono>
                <div style={{ fontSize: 11, color: r.neverPaysOff ? T.garnet : T.dim, marginTop: 3 }}>
                  {r.neverPaysOff ? t("Payments don't cover the interest") : `${fmt(r.totalInterest)} ${t("total interest")}`}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {order.map(d => {
        const util = d.limit ? (d.balance / d.limit) * 100 : null;
        const isTarget = target && d.id === target.id && d.balance > 0;
        const done = d.balance <= 0;
        const bench = RATE_BENCH[d.type];
        // The benchmarks are NZ-market figures — only meaningful in NZD.
        const rateGap = (state.currency || "NZD") === "NZD" && bench && d.balance > 0 && d.apr > bench.good + 1.5;
        const yearlySaving = rateGap ? d.balance * (d.apr - bench.good) / 100 : 0;
        const typeLabel = t(DEBT_TYPES.find(x => x.id === d.type)?.label || "Debt");
        const debtPayments = (state.payments || [])
          .filter(p => p.debtId === d.id)
          .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        const totalPaid = debtPayments.filter(isPaymentEntry).reduce((s, p) => s + p.amount, 0);
        const totalCharged = debtPayments.filter(p => p.type === "charge").reduce((s, p) => s + p.amount, 0);
        const totalInterestPosted = debtPayments.filter(p => p.type === "interest").reduce((s, p) => s + p.amount, 0);
        const nowMonthKey = todayISO().slice(0, 7);
        const paidThisMonth = debtPayments
          .filter(p => p.date?.slice(0, 7) === nowMonthKey && isPaymentEntry(p))
          .reduce((s, p) => s + p.amount, 0);
        const isExpanded = expanded === d.id;
        /* Due chip: how many days until this month's payment date, and whether
           the minimum has already been met inside the current statement cycle
           (read off the ledger — same source the planner uses). */
        const due = d.dueDay != null && !done ? (() => {
          const nextDue = nextDayOfMonth(d.dueDay);
          const days = daysBetween(todayISO(), nextDue);
          const cycleStart = d.lastInterestDate || closeOnOrBefore(todayISO(), statementDayOf(d));
          const paidInCycle = debtPayments
            .filter(p => isPaymentEntry(p) && p.date > cycleStart)
            .reduce((s, p) => s + p.amount, 0);
          const min = Math.min(d.minPayment || 0, d.balance);
          return { days, nextDue, covered: min > 0 && paidInCycle >= min - 0.01 };
        })() : null;
        return (
          <Card key={d.id} style={{ marginTop: 12, opacity: done ? 0.55 : 1, borderColor: isTarget ? `${T.gold}66` : T.line }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                {/* The name truncates; the badge never does. Keeping the badge
                    inside the ellipsis span clipped it to "ATTACK THI" on a
                    320px screen. */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 15, ...ELLIPSIS }} title={d.name}>
                    {d.name} {done && "✓"}
                  </span>
                  {isTarget && <span style={{ flexShrink: 0, fontSize: 10, background: T.gold, color: T.midnight, borderRadius: 99, padding: "2px 8px", fontWeight: 700 }}>{t("ATTACK THIS")}</span>}
                </div>
                <div style={{ fontSize: 12, color: T.dim, marginTop: 3 }}>
                  <span style={{ color: T.faint }}>{typeLabel}</span> · <Mono>{d.apr}%</Mono> · {t("min")} <Mono>{fmt(d.minPayment)}</Mono>
                  {util !== null && <> · <span style={{ color: util > 80 ? T.garnet : util > 30 ? T.gold : T.greenHi }}>{util.toFixed(0)}% {t("used")}</span></>}
                  {!done && d.endDate && <> · <span style={{ color: T.gold }}>{t("ends {d}", { d: monthName(new Date(d.endDate + "T12:00"), lang) })}</span></>}
                </div>
                {due && (
                  <div style={{ fontSize: 11, marginTop: 5, color: due.covered ? T.greenHi : due.days <= 3 ? T.gold : T.faint, fontWeight: due.covered || due.days <= 3 ? 600 : 400 }}>
                    {due.covered
                      ? t("✓ Minimum paid this cycle")
                      : due.days === 0 ? t("Payment due today")
                      : due.days === 1 ? t("Payment due tomorrow")
                      : t("Payment due in {d} days", { d: due.days })}
                  </div>
                )}
              </div>
              <Mono style={{ fontSize: 18, fontWeight: 600, color: done ? T.greenHi : T.white, flexShrink: 0 }}>{fmt(d.balance)}</Mono>
            </div>
            {util !== null && (
              <div style={{ height: 5, background: T.line, borderRadius: 3, marginTop: 10, overflow: "hidden" }}
                role="img" title={t("{p}% of your {a} limit used", { p: util.toFixed(0), a: fmt(d.limit) })}
                aria-label={t("{p}% of your {a} limit used", { p: util.toFixed(0), a: fmt(d.limit) })}>
                <div style={{ width: `${Math.min(100, util)}%`, height: "100%", background: util > 80 ? T.garnet : util > 30 ? T.gold : T.green, borderRadius: 3 }} />
              </div>
            )}
            {rateGap && (
              <div style={{ marginTop: 10, background: T.goldSoft, border: `1px solid ${T.gold}33`, borderRadius: 10, padding: "9px 12px", fontSize: 12, lineHeight: 1.5 }}>
                {t("💡 Rate check: you're paying {apr}%. {tip} At ~{good}% this balance costs about {save}/yr less in interest. Compare on MoneyHub or Canstar — offered rates depend on your credit file.", {
                  apr: d.apr, tip: t(bench.tip), good: bench.good, save: fmt(yearlySaving),
                })}
              </div>
            )}
            {!done && (() => {
              /* The true cost of coasting: term + interest at minimum only,
                 or the negative-amortization warning when the minimum can
                 never clear the balance. */
              const proj = minOnlyProjection(d);
              return proj ? (
                <div style={{ marginTop: 10, fontSize: 12, color: T.dim, lineHeight: 1.55 }}>
                  {t("At minimum only: paid off in {t} · {i} interest · {tot} total repaid", {
                    t: fmtTerm(proj.months, t), i: fmt(proj.interest), tot: fmt(proj.total),
                  })}
                </div>
              ) : (
                <div style={{ marginTop: 10, background: T.garnetSoft, border: `1px solid ${T.garnet}44`, borderRadius: 10, padding: "8px 12px", fontSize: 12, lineHeight: 1.5 }}>
                  {t("⚠️ The minimum never pays this off — the balance grows every month.")}
                </div>
              );
            })()}
            {paidThisMonth > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: T.greenHi, fontWeight: 600 }}>
                {t("↓ Paid {a} this month", { a: fmt(paidThisMonth) })}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {!done && <Btn small onClick={() => { setPayFor(d); setPayAmt(""); setPayDate(todayISO()); }}>{t("Log payment")}</Btn>}
              {!done && d.type === "card" && (
                <Btn small variant="ghost" style={{ borderColor: `${T.garnet}66`, color: T.garnet }} onClick={() => { setChargeFor(d); setChargeAmt(""); setChargeNote(""); setChargeDate(todayISO()); }}>
                  {t("+ Log charge")}
                </Btn>
              )}
              <Btn small variant="ghost" onClick={() => openEditor({ ...d })}>{t("Edit")}</Btn>
              <Btn small variant="danger" onClick={() => { if (confirm(t("Delete {n}?", { n: d.name }))) { up({ debts: state.debts.filter(x => x.id !== d.id) }); notify(t("Debt deleted")); } }}>{t("Delete")}</Btn>
              {/* No `marginLeft: auto` on Activity — with five buttons the row
                  wraps, and pushing it right strands it alone on its own line. */}
              {(debtPayments.length > 0 || isExpanded) && (
                <Btn small variant="ghost" onClick={() => setExpanded(isExpanded ? null : d.id)}>
                  {isExpanded ? t("Hide history") : t("Activity ({n})", { n: debtPayments.length })}
                </Btn>
              )}
            </div>
            {isExpanded && (
              <div style={{ marginTop: 12, borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
                <div style={{ fontSize: 11, color: T.faint, marginBottom: 8 }}>
                  {[
                    totalPaid > 0 && t("{a} paid", { a: fmt(totalPaid) }),
                    totalCharged > 0 && t("{a} charged", { a: fmt(totalCharged) }),
                    totalInterestPosted > 0 && t("{a} interest", { a: fmt(totalInterestPosted) }),
                  ].filter(Boolean).join(" · ")}
                  {" "}{t("across {n} entries", { n: debtPayments.length })}
                </div>
                {debtPayments.map(p => {
                  const badge = p.type === "charge"
                    ? { bg: `${T.garnet}29`, fg: T.garnet, sym: "↑" }
                    : p.type === "interest"
                    ? { bg: `${T.gold}29`, fg: T.gold, sym: "%" }
                    : { bg: `${T.greenHi}29`, fg: T.greenHi, sym: "↓" };
                  const kindLabel = p.type === "charge" ? t("Charge") : p.type === "interest" ? t("Interest") : t("Payment");
                  return (
                    <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: 12.5 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: badge.bg, color: badge.fg, fontSize: 11, flexShrink: 0 }}>{badge.sym}</span>
                        <span style={{ color: T.dim }}>
                          {new Date(p.date + "T12:00").toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" })} · {kindLabel}
                          {p.note && <span style={{ color: T.faint }}> · {p.note}</span>}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        <Mono style={{ color: badge.fg }}>{p.type === "charge" || p.type === "interest" ? "+" : "−"}{fmt(p.amount)}</Mono>
                        <button
                          onClick={() => { if (confirm(t("Undo this payment? The balance will be restored."))) undoPayment(p); }}
                          aria-label={t("Undo")} title={t("Undo")}
                          style={{ background: "none", border: "none", color: T.faint, cursor: "pointer", fontSize: 15 }}
                        >×</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}

      <Btn variant="ghost" style={{ width: "100%", marginTop: 12 }} onClick={() => openEditor({ name: "", balance: "", apr: "", minPayment: "", limit: "", type: "card", minPaymentAuto: true, dueDay: null })}>
        {t("+ Add a debt")}
      </Btn>

      {payFor && (
        <Sheet title={t("Payment to {n}", { n: payFor.name })} onClose={() => setPayFor(null)}>
          <Input type="number" inputMode="decimal" placeholder={t("Amount")} value={payAmt} onChange={e => setPayAmt(e.target.value)} autoFocus />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            {[payFor.minPayment, payFor.minPayment + state.extraPayment, Math.round(payFor.balance)].map((v, i) => (
              <Btn key={i} small variant="ghost" onClick={() => setPayAmt(String(v))}>{i === 2 ? t("Full") : fmt(v)}</Btn>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Label style={{ marginBottom: 5 }}>{t("Date paid")}</Label>
            <Input type="date" value={payDate} min={earliestEntryDate(payFor)} max={todayISO()}
              onChange={e => setPayDate(e.target.value)} />
            <div style={{ fontSize: 11, color: T.faint, marginTop: 5, lineHeight: 1.5 }}>
              {t("When the payment actually left your account — the date decides how much interest this cycle costs. You can only date back to the start of the current statement cycle.")}
            </div>
          </div>
          <Btn style={{ width: "100%", marginTop: 14 }} onClick={logPayment}>{t("Log payment")}</Btn>
        </Sheet>
      )}

      {chargeFor && (
        <Sheet title={t("Charge to {n}", { n: chargeFor.name })} onClose={() => setChargeFor(null)}>
          <Input type="number" inputMode="decimal" placeholder={t("Amount")} value={chargeAmt} onChange={e => setChargeAmt(e.target.value)} autoFocus />
          <Input placeholder={t("Note (optional)")} value={chargeNote} onChange={e => setChargeNote(e.target.value)} style={{ marginTop: 10 }} />
          <div style={{ marginTop: 14 }}>
            <Label style={{ marginBottom: 7 }}>{t("What was it for?")}</Label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {categoriesOf(state).map(c => (
                <button key={c.id} onClick={() => setChargeCat(c.id)} style={{
                  background: chargeCat === c.id ? T.goldSoft : T.midnight,
                  border: `1px solid ${chargeCat === c.id ? T.gold : T.line}`,
                  color: chargeCat === c.id ? T.gold : T.dim,
                  borderRadius: 99, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}>{c.emoji} {t(c.label)}</button>
              ))}
              <button onClick={() => setChargeCat(NO_CATEGORY)} style={{
                background: chargeCat === NO_CATEGORY ? T.panelHi : T.midnight,
                border: `1px solid ${chargeCat === NO_CATEGORY ? T.dim : T.line}`,
                color: chargeCat === NO_CATEGORY ? T.white : T.faint,
                borderRadius: 99, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>{t("Not a budget expense")}</button>
            </div>
            <div style={{ fontSize: 11, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
              {chargeCat === NO_CATEGORY
                ? t("Only the card balance moves — nothing reaches your budget. Right for a balance transfer, cash advance or fee.")
                : t("This lands in your budget straight away. It counts as money out later, when you pay the card.")}
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Label style={{ marginBottom: 5 }}>{t("Date charged")}</Label>
            <Input type="date" value={chargeDate} min={earliestEntryDate(chargeFor)} max={todayISO()}
              onChange={e => setChargeDate(e.target.value)} />
          </div>
          <div style={{ fontSize: 11, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
            {t("This adds to the balance — for when the card gets used again.")}
          </div>
          <Btn style={{ width: "100%", marginTop: 14, background: T.garnet }} onClick={logCharge}>{t("Log charge")}</Btn>
        </Sheet>
      )}

      {editing && (
        <Sheet title={t(editing.id ? "Edit debt" : "New debt")} onClose={() => setEditing(null)}>
          <Label style={{ marginBottom: 6 }}>{t("Type")}</Label>
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            {DEBT_TYPES.map(x => (
              <button key={x.id} onClick={() => setEditing({ ...editing, type: x.id })} style={{
                background: editing.type === x.id ? T.goldSoft : T.midnight,
                border: `1px solid ${editing.type === x.id ? T.gold : T.line}`,
                color: editing.type === x.id ? T.gold : T.dim,
                borderRadius: 99, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>{t(x.label)}</button>
            ))}
          </div>
          {[
            ["name", "Name", "text"],
            ["balance", "Current balance", "number"],
            ["apr", "APR %", "number"],
          ].map(([k, label, type]) => (
            <div key={k} style={{ marginBottom: 10 }}>
              <Label style={{ marginBottom: 5 }}>{t(label)}</Label>
              <Input type={type} inputMode={type === "number" ? "decimal" : undefined} value={editing[k] ?? ""}
                onChange={e => {
                  const raw = type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value;
                  if (k === "balance" && editing.type === "card" && editing.minPaymentAuto === true) {
                    setEditing({ ...editing, balance: raw, minPayment: raw === "" ? "" : autoMinPayment(Number(raw)) });
                  } else {
                    setEditing({ ...editing, [k]: raw });
                  }
                }} />
              {k === "apr" && (
                <div style={{ fontSize: 11, color: T.faint, marginTop: 5, lineHeight: 1.5 }}>
                  {t("APR is the yearly interest rate you're charged on this debt — you'll find it on your card or loan statement.")}
                </div>
              )}
              {k === "apr" && Number(editing.apr) > 80 && (
                <div style={{ marginTop: 6, background: T.goldSoft, border: `1px solid ${T.gold}44`, borderRadius: 8, padding: "7px 10px", fontSize: 11, color: T.gold, lineHeight: 1.5 }}>
                  {t("That's {apr}% per year — common for revolving credit in some countries. Double-check it's not the monthly rate.", { apr: editing.apr })}
                </div>
              )}
            </div>
          ))}
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <Label>{t("Minimum payment / month")}</Label>
              {editing.type === "card" && (
                editing.minPaymentAuto === true ? (
                  <button
                    onClick={() => setEditing({ ...editing, minPaymentAuto: false })}
                    title={t("Tap to enter your own amount")}
                    style={{ background: T.goldSoft, border: `1px solid ${T.gold}66`, color: T.gold, borderRadius: 99, padding: "3px 9px", fontSize: 10.5, fontWeight: 700, cursor: "pointer" }}
                  >{t("Auto · 3%")}</button>
                ) : (
                  <button
                    onClick={() => setEditing({ ...editing, minPaymentAuto: true, minPayment: autoMinPayment(Number(editing.balance) || 0) })}
                    style={{ background: "none", border: `1px solid ${T.line}`, color: T.dim, borderRadius: 99, padding: "3px 9px", fontSize: 10.5, fontWeight: 700, cursor: "pointer" }}
                  >{t("Use auto 3%")}</button>
                )
              )}
            </div>
            <Input type="number" inputMode="decimal" value={editing.minPayment ?? ""}
              onChange={e => setEditing({ ...editing, minPayment: e.target.value === "" ? "" : Number(e.target.value), minPaymentAuto: false })} />
            {editing.type === "card" && (
              <div style={{ fontSize: 11, color: T.faint, marginTop: 5, lineHeight: 1.5 }}>
                {t("Card issuers usually set the minimum at 1–3% of your balance. We default to 3% and keep it updated as your balance falls — edit it to match your statement and it stays fixed.")}
              </div>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            <Label style={{ marginBottom: 5 }}>{t("Credit limit (blank for loans)")}</Label>
            <Input type="number" inputMode="decimal" value={editing.limit ?? ""}
              onChange={e => setEditing({ ...editing, limit: e.target.value === "" ? "" : Number(e.target.value) })} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <Label style={{ marginBottom: 5 }}>{t("Payment due on (day of month)")}</Label>
            <Input type="number" inputMode="numeric" min="1" max="28" placeholder={t("e.g. 15")} value={editing.dueDay ?? ""}
              onChange={e => setEditing({ ...editing, dueDay: e.target.value === "" ? null : Number(e.target.value) })} />
            <div style={{ fontSize: 11, color: T.faint, marginTop: 5, lineHeight: 1.5 }}>
              {t("The day your payment is due each month (1–28). Aurio uses it to work out your real statement cycle, so interest is charged on what you actually carried — and to remind you before it's due.")}
            </div>
          </div>
          {editing.dueDay != null && editing.dueDay !== "" && (
            showCycle ? (
              <div style={{ marginBottom: 10 }}>
                <Label style={{ marginBottom: 5 }}>{t("Statement closes on (day of month)")}</Label>
                <Input type="number" inputMode="numeric" min="1" max="28"
                  value={editing.statementDay ?? derivedStatementDay(editing.dueDay)}
                  onChange={e => setEditing({ ...editing, statementDay: e.target.value === "" ? null : Number(e.target.value) })} />
                <div style={{ fontSize: 11, color: T.faint, marginTop: 5, lineHeight: 1.5 }}>
                  {t("The day your billing period ends — interest is worked out on the average balance you carried across that period. We assume it's {n} days before your due date; change it to match your statement.", { n: STATEMENT_LEAD_DAYS })}
                </div>
              </div>
            ) : (
              <button onClick={() => setShowCycle(true)} style={{ background: "none", border: "none", color: T.dim, fontSize: 11.5, cursor: "pointer", padding: "0 0 10px", textDecoration: "underline" }}>
                {t("Advanced: set the statement closing day")}
              </button>
            )
          )}
          <div style={{ marginBottom: 10 }}>
            <Label style={{ marginBottom: 5 }}>{t("Ends on (optional)")}</Label>
            <Input type="date" value={editing.endDate ?? ""}
              onChange={e => setEditing({ ...editing, endDate: e.target.value || null })} />
            <div style={{ fontSize: 11, color: T.faint, marginTop: 5, lineHeight: 1.5 }}>
              {t("For a fixed-term loan (e.g. a car payment) — the Save tab's money map uses this to show you when that payment frees up. Leave blank for revolving credit like cards.")}
            </div>
          </div>
          <Btn style={{ width: "100%", marginTop: 6 }} onClick={() => {
            if (!editing.name || editing.balance === "") return;
            saveDebt({ ...editing, balance: Number(editing.balance) || 0, apr: Number(editing.apr) || 0, minPayment: Number(editing.minPayment) || 0, limit: editing.limit === "" || editing.limit == null ? null : Number(editing.limit), endDate: editing.endDate || null });
          }}>{t("Save debt")}</Btn>
        </Sheet>
      )}
    </>
  );
}

/* ═══ SPEND ═══ */
const monthKeyLabel = (key, lang) =>
  new Date(key + "-15T12:00").toLocaleDateString(lang, { month: "long", year: "numeric" });

/* CSV import — three steps in one sheet: pick a file, confirm the detected
   columns, review what will be added.

   Deliberately free while export stays Premium: import lowers the cost of
   moving TO Aurio, and a paywall on a first-run task is where friction costs
   most. Everything happens on-device — no upload, matching the app's
   no-bank-login promise.

   The review step is doing real work, not decoration: it states which
   day/month reading was chosen (getting that wrong shifts every date in the
   file), how many rows were skipped as duplicates or as income, and lets the
   user fix any category — each fix teaching state.merchantRules for next
   time. */
const IMPORT_PREVIEW_CAP = 100;

function ImportSheet({ state, up, t, lang, notify, onClose }) {
  const [step, setStep] = useState("file");
  const [rows, setRows] = useState(null);
  const [col, setCol] = useState(null);
  const [dateOrder, setDateOrder] = useState("DMY");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cats = categoriesOf(state);
  const catIds = cats.map(c => c.id);

  const recompute = (r, c, order) => {
    const out = toExpenses(r, c, {
      rules: state.merchantRules || {},
      existing: state.expenses || [],
      importId: "preview",
      dateOrder: order,
      categoryIds: catIds,
    });
    setResult(out);
  };

  const readFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const { rows: parsed } = parseCsv(await file.text());
      if (parsed.length < 2) throw new Error("empty");
      const detected = detectColumns(parsed, state.currency);
      if (!detected || detected.date === -1 || (detected.amount === -1 && detected.debit === -1 && detected.credit === -1)) {
        throw new Error("columns");
      }
      const body = detected.hasHeader ? parsed.slice(1) : parsed;
      const order = detectDateOrder(body.map(r => r[detected.date]), state.currency);
      setRows(parsed); setCol(detected); setDateOrder(order);
      recompute(parsed, detected, order);
      setStep("review");
    } catch (err) {
      setError(err.message === "columns"
        ? t("Couldn't find a date and an amount column in that file. Check it's a transaction export, not a summary.")
        : t("That file doesn't look like a CSV Aurio can read."));
    }
    setBusy(false);
  };

  /* A category change teaches the merchant for every future import, and
     re-labels the other rows from the same shop in this batch immediately. */
  const setRowCategory = (idx, categoryId) => {
    const row = result.ready[idx];
    const key = merchantKey(row.note);
    const ready = result.ready.map((r, i) =>
      i === idx || (key && merchantKey(r.note) === key) ? { ...r, category: categoryId } : r);
    setResult({ ...result, ready });
    if (key) up({ merchantRules: { ...(state.merchantRules || {}), [key]: categoryId } });
  };

  const commit = () => {
    const importId = uid();
    const stamped = result.ready.map(r => ({ ...r, id: uid(), importId }));
    up({
      expenses: [...stamped, ...(state.expenses || [])],
      lastImport: { id: importId, at: todayISO(), count: stamped.length },
    });
    notify(t("{n} expenses imported", { n: stamped.length }));
    onClose();
  };

  const swapDateOrder = () => {
    const next = dateOrder === "DMY" ? "MDY" : "DMY";
    setDateOrder(next);
    recompute(rows, col, next);
  };

  const sample = result?.ready?.[0];
  const dateHint = dateOrder === "DMY" ? t("day/month (3/4 = 3 April)") : t("month/day (3/4 = 4 March)");

  return (
    <Sheet title={t("Import from CSV")} onClose={onClose}>
      {step === "file" && (
        <>
          <div style={{ fontSize: 12.5, color: T.dim, lineHeight: 1.6, marginBottom: 14 }}>
            {t("Export a CSV from your bank or spreadsheet and pick it here. It's read on your device — nothing is uploaded, and no bank login is ever needed.")}
          </div>
          <Btn style={{ width: "100%" }} disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? t("Reading…") : t("Choose a CSV file")}
          </Btn>
          <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" style={{ display: "none" }} onChange={readFile} />
          {error && (
            <div style={{ marginTop: 12, background: T.garnetSoft, border: `1px solid ${T.garnet}44`, borderRadius: 10, padding: "10px 12px", fontSize: 12, color: T.garnet, lineHeight: 1.5 }}>
              {error}
            </div>
          )}
        </>
      )}

      {step === "review" && result && (
        <>
          <div style={{ fontSize: 12.5, lineHeight: 1.7, marginBottom: 10 }}>
            <span style={{ color: T.gold, fontWeight: 600 }}>{t("{n} to import", { n: result.ready.length })}</span>
            {result.duplicates > 0 && <span style={{ color: T.dim }}> · {t("{n} already in Aurio", { n: result.duplicates })}</span>}
            {result.skippedIncome > 0 && <span style={{ color: T.dim }}> · {t("{n} money-in rows skipped", { n: result.skippedIncome })}</span>}
            {result.unparsable.length > 0 && <span style={{ color: T.garnet }}> · {t("{n} couldn't be read", { n: result.unparsable.length })}</span>}
          </div>

          {/* Reading the dates the wrong way round would shift the entire file,
              so the choice is stated and reversible rather than assumed. */}
          <div style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
            <div style={{ fontSize: 11.5, color: T.dim, lineHeight: 1.5 }}>
              {t("Reading dates as {order}.", { order: dateHint })}
              {sample && ` ${t("First row: {d}", { d: new Date(sample.date + "T12:00").toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }) })}`}
            </div>
            <Btn small variant="ghost" style={{ marginTop: 8 }} onClick={swapDateOrder}>{t("Not right — swap day and month")}</Btn>
          </div>

          {result.ready.length === 0 ? (
            <div style={{ fontSize: 12.5, color: T.faint, lineHeight: 1.6, marginBottom: 12 }}>
              {result.duplicates > 0
                ? t("Everything in this file is already in Aurio — nothing to add.")
                : t("No spending rows found in this file.")}
            </div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 12 }}>
              {result.ready.slice(0, IMPORT_PREVIEW_CAP).map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: `1px solid ${T.line}` }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, ...ELLIPSIS }} title={r.note}>{r.note || t("(no description)")}</div>
                    <div style={{ fontSize: 11, color: T.faint }}>
                      {new Date(r.date + "T12:00").toLocaleDateString(lang, { day: "numeric", month: "short" })}
                    </div>
                  </div>
                  <Mono style={{ fontSize: 12.5, flexShrink: 0 }}>{fmt(r.amount, 2)}</Mono>
                  <select value={r.category} onChange={e => setRowCategory(i, e.target.value)}
                    aria-label={t("Category")}
                    style={{ background: T.midnight, color: T.dim, border: `1px solid ${T.line}`, borderRadius: 8, fontSize: 11, padding: "4px 5px", maxWidth: 92, flexShrink: 0 }}>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.emoji} {t(c.label)}</option>)}
                  </select>
                </div>
              ))}
              {result.ready.length > IMPORT_PREVIEW_CAP && (
                <div style={{ fontSize: 11.5, color: T.faint, padding: "10px 0", lineHeight: 1.5 }}>
                  {t("Showing the first {n}. All {total} will be imported.", { n: IMPORT_PREVIEW_CAP, total: result.ready.length })}
                </div>
              )}
            </div>
          )}

          <Btn style={{ width: "100%" }} disabled={result.ready.length === 0} onClick={commit}>
            {t("Import {n} expenses", { n: result.ready.length })}
          </Btn>
          <div style={{ fontSize: 11, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
            {t("You can undo this straight afterwards if it doesn't look right.")}
          </div>
        </>
      )}
    </Sheet>
  );
}

function Spend({ state, up, t, lang, monthExpenses, notify, premium, requirePremium }) {
  const cats = categoriesOf(state);
  const [amt, setAmt] = useState("");
  const [cat, setCat] = useState("groceries");
  const [note, setNote] = useState("");
  const nowKey = monthKeyOf(new Date());
  const [viewMonth, setViewMonth] = useState(nowKey);
  const [showAllCats, setShowAllCats] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const isCurrent = viewMonth === nowKey;
  /* Free accounts can browse the current + previous 2 months; older history
     is a Premium feature. */
  const oldestFree = shiftMonthKey(nowKey, -2);
  const atFreeLimit = !premium && viewMonth <= oldestFree;

  /* Collapse the category list again when the month changes — an expanded list
     from one month shouldn't carry over to another with different categories. */
  useEffect(() => { setShowAllCats(false); }, [viewMonth]);

  const viewing = useMemo(
    () => (isCurrent ? monthExpenses : state.expenses.filter(e => e.date?.slice(0, 7) === viewMonth)),
    [isCurrent, monthExpenses, state.expenses, viewMonth]
  );
  const viewTotal = viewing.reduce((s, e) => s + e.amount, 0);

  /* Per-category totals for the viewed month, biggest first. */
  const catTotals = useMemo(() => {
    const g = {};
    for (const e of viewing) g[e.category] = (g[e.category] || 0) + e.amount;
    return Object.entries(g)
      .map(([id, total]) => ({ id, total, budget: state.budgets[id] || 0 }))
      .sort((a, b) => b.total - a.total);
  }, [viewing, state.budgets]);

  /* Two bases, deliberately: see monthCashFlow in money.js. `viewTotal` above
     is what was SPENT this month (card purchases included from the day they
     were made); `cashOut` is what actually LEFT the account, which skips those
     purchases and counts the card payment that settles them instead. */
  const monthlyIncome = monthlyIncomeOf(state);
  const { cashOut, cashLeft, debtPaid } = monthCashFlow(state, viewMonth, monthlyIncome);
  const cardSpend = viewing.reduce((s, e) => s + (e.debtId ? e.amount : 0), 0);

  /* Total spend for each of the last six months (oldest → newest). */
  const trend = useMemo(() => {
    const keys = [];
    for (let i = 5; i >= 0; i--) keys.push(shiftMonthKey(nowKey, -i));
    const sums = Object.fromEntries(keys.map(k => [k, 0]));
    for (const e of state.expenses) {
      const k = e.date?.slice(0, 7);
      if (k in sums) sums[k] += e.amount;
    }
    return keys.map(k => ({ key: k, total: sums[k] }));
  }, [state.expenses, nowKey]);
  const trendMax = Math.max(1, ...trend.map(m => m.total));

  const add = () => {
    const amount = Number(amt);
    if (!amount || amount <= 0) return;
    up({ expenses: [{ id: uid(), amount, category: cat, note, date: todayISO() }, ...state.expenses] });
    setAmt(""); setNote("");
    const b = state.budgets[cat] || 0;
    const spent = monthExpenses.filter(e => e.category === cat).reduce((s, e) => s + e.amount, 0) + amount;
    const cLabel = t(cats.find(c => c.id === cat)?.label || "");
    notify(b && spent > b ? t("Logged — ⚠️ {c} is over budget", { c: cLabel }) : t("Expense logged"));
  };

  const byDay = useMemo(() => {
    const g = {};
    for (const e of viewing) (g[e.date] = g[e.date] || []).push(e);
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [viewing]);

  return (
    <>
      <Card style={{ marginBottom: 12 }}>
        <Label style={{ marginBottom: 12 }}>{t("Cash flow")} · {isCurrent ? t("This month") : monthKeyLabel(viewMonth, lang)}</Label>
        {/* The number this card is actually about, led rather than buried in a
            row. Sans (not Instrument Serif) and proportional figures — a display
            face or tabular digits both read as decoration at this size. */}
        <div style={{ fontSize: 34, fontWeight: 600, lineHeight: 1.1, color: T.white, letterSpacing: "-0.01em" }}>
          {fmt(cashOut)}
        </div>
        <div style={{ fontSize: 12, color: T.dim, marginTop: 2, marginBottom: 12 }}>
          {t("left your account this month (cash spend + debt payments)")}
        </div>
        {/* Two figures on one tab that don't match need saying out loud, once.
            Only shown when card spending actually exists, so nobody reads an
            explanation for a distinction they haven't hit. */}
        {cardSpend > 0 && (
          <div style={{ fontSize: 11, color: T.faint, marginTop: -6, marginBottom: 12, lineHeight: 1.5 }}>
            {t("{a} of this month's spending went on a card — that counts here when you pay the card, not when you swipe.", { a: fmt(cardSpend) })}
          </div>
        )}
        {/* Meter, not a two-slice pie: one ratio against a limit. The track is
            the month's income, the fill is what's gone out of it. Gold while
            there's room, garnet once spending passes what came in. */}
        {monthlyIncome > 0 && (() => {
          const usedPct = (cashOut / monthlyIncome) * 100;
          const over = cashOut > monthlyIncome;
          const label = t("{p}% of your income", { p: Math.round(usedPct) });
          return (
            <div style={{ marginBottom: 12 }} role="img" title={label}
              aria-label={t("{a} out of {b} income — {p}% used.", { a: fmt(cashOut), b: fmt(monthlyIncome), p: Math.round(usedPct) })}>
              <div style={{ height: 6, background: T.line, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${Math.min(100, usedPct)}%`, height: "100%", background: over ? T.garnet : T.gold, borderRadius: 3, transition: "width 0.4s" }} />
              </div>
              <div style={{ fontSize: 11, color: over ? T.garnet : T.faint, marginTop: 5, fontWeight: over ? 700 : 400 }}>
                {over ? t("You've spent more than came in this month.") : label}
              </div>
            </div>
          );
        })()}
        <div style={{ fontSize: 12, color: T.dim, lineHeight: 2, borderTop: `1px solid ${T.line}`, paddingTop: 6 }}>
          <Row k={t("Income")} v={fmt(monthlyIncome)} />
          <Row k={t("Left")} v={fmt(cashLeft)} color={cashLeft < 0 ? T.garnet : T.greenHi} bold />
        </div>
        {!isCurrent && (
          <div style={{ fontSize: 10.5, color: T.faint, marginTop: 10 }}>
            {t("Income shown is your current setting — it isn't tracked per past month.")}
          </div>
        )}
      </Card>

      <Card>
        <Label style={{ marginBottom: 10 }}>{t("Log an expense")}</Label>
        <Input type="number" inputMode="decimal" placeholder="0.00" value={amt} onChange={e => setAmt(e.target.value)}
          style={{ fontSize: 28, fontFamily: "'Geist Mono', monospace", textAlign: "center", padding: "14px" }} />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 12 }}>
          {cats.map(c => (
            <button key={c.id} onClick={() => setCat(c.id)} style={{
              background: cat === c.id ? T.goldSoft : T.midnight, border: `1px solid ${cat === c.id ? T.gold : T.line}`,
              color: cat === c.id ? T.gold : T.dim, borderRadius: 99, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>{c.emoji} {t(c.label)}</button>
          ))}
        </div>
        <Input placeholder={t("Note (optional)")} value={note} onChange={e => setNote(e.target.value)} style={{ marginTop: 12 }} />
        <Btn style={{ width: "100%", marginTop: 12 }} onClick={add}>{t("Add expense")}</Btn>
      </Card>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "18px 2px 8px" }}>
        <button onClick={() => { if (atFreeLimit) { requirePremium(); return; } setViewMonth(shiftMonthKey(viewMonth, -1)); }} aria-label={t("Previous month")} style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 8, color: atFreeLimit ? T.faint : T.dim, fontSize: 14, padding: "4px 12px", cursor: "pointer" }}>{atFreeLimit ? "🔒" : "‹"}</button>
        <Label style={{ textAlign: "center" }}>
          {isCurrent ? t("This month") : monthKeyLabel(viewMonth, lang)} · {fmt(viewTotal)}
        </Label>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => !isCurrent && setViewMonth(shiftMonthKey(viewMonth, 1))} aria-label={t("Next month")} disabled={isCurrent} style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 8, color: isCurrent ? T.line : T.dim, fontSize: 14, padding: "4px 12px", cursor: isCurrent ? "default" : "pointer" }}>›</button>
          <button
            onClick={() => {
              if (!requirePremium()) return;
              let ok = false;
              try { ok = downloadFile(`aurio-${viewMonth}.csv`, buildCsv(state, t, viewMonth), "text/csv;charset=utf-8"); }
              catch (err) { console.error("[Aurio] csv build failed", err); }
              notify(t(ok ? "CSV exported" : "Couldn't export — try again."));
            }}
            aria-label={t("Export CSV")} title={t("Export CSV")}
            style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 8, color: T.gold, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", padding: "4px 10px", cursor: "pointer" }}
          >CSV ↓{!premium && " 🔒"}</button>
          {/* Import sits beside export and is deliberately NOT gated — it's an
              onboarding step, not a power feature. */}
          <button onClick={() => setShowImport(true)}
            aria-label={t("Import CSV")} title={t("Import CSV")}
            style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 8, color: T.gold, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", padding: "4px 10px", cursor: "pointer" }}
          >CSV ↑</button>
        </div>
      </div>

      {state.lastImport && (
        <Card style={{ marginBottom: 12, padding: "10px 14px", background: T.goldSoft, borderColor: `${T.gold}44` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontSize: 12, color: T.dim, minWidth: 0, ...ELLIPSIS }}>
              {t("Imported {n} expenses", { n: state.lastImport.count })}
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <Btn small variant="ghost" onClick={() => {
                if (!confirm(t("Remove the {n} expenses from that import?", { n: state.lastImport.count }))) return;
                up({
                  expenses: (state.expenses || []).filter(e => e.importId !== state.lastImport.id),
                  lastImport: null,
                });
                notify(t("Import undone"));
              }}>{t("Undo")}</Btn>
              <Btn small variant="ghost" onClick={() => up({ lastImport: null })}>{t("Keep")}</Btn>
            </div>
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 12, padding: "12px 14px" }}>
        <Label style={{ marginBottom: 10 }}>{t("Last 6 months")}</Label>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 64 }}
          role="img"
          aria-label={t("Monthly spend for the last 6 months: {list}", {
            list: trend.map(m => `${monthKeyLabel(m.key, lang)} ${fmt(m.total)}`).join(", "),
          })}>
          {trend.map(m => {
            const active = m.key === viewMonth;
            return (
              <button key={m.key} onClick={() => setViewMonth(m.key)} title={`${monthKeyLabel(m.key, lang)} · ${fmt(m.total)}`} style={{ flex: 1, background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%", gap: 4 }}>
                {/* 4px rounded data-end, square at the baseline. */}
                <div style={{ width: "100%", height: `${Math.max(3, (m.total / trendMax) * 46)}px`, borderRadius: "4px 4px 0 0", background: active ? T.gold : m.total > 0 ? T.green : T.line, opacity: active ? 1 : 0.75 }} />
                <div style={{ fontSize: 9.5, color: active ? T.gold : T.faint, fontWeight: active ? 700 : 500, width: "100%", textAlign: "center" }}>
                  {new Date(m.key + "-15T12:00").toLocaleDateString(lang, { month: "short" })}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      <Card style={{ marginBottom: 12, padding: "12px 14px" }}>
        <Label style={{ marginBottom: 10 }}>{t("Where it went")}</Label>
        {catTotals.length > 0 ? (
          <CategoryBars
            rows={catTotals} total={viewTotal} t={t} cats={cats}
            expanded={showAllCats} onToggle={() => setShowAllCats(v => !v)}
          />
        ) : (
          <div style={{ fontSize: 12, color: T.faint }}>
            {t("No expenses recorded in {m}.", { m: isCurrent ? t("This month") : monthKeyLabel(viewMonth, lang) })}
          </div>
        )}
      </Card>

      <div style={{ marginTop: 4 }}>
        {byDay.length === 0 && (
          <Card style={{ textAlign: "center", color: T.faint, fontSize: 13 }}>
            {isCurrent ? t("No expenses yet this month. Log your first one above.") : t("No expenses recorded in {m}.", { m: monthKeyLabel(viewMonth, lang) })}
            {isCurrent && (
              <div style={{ marginTop: 10 }}>
                <Btn small variant="ghost" onClick={() => setShowImport(true)}>
                  {t("Or import a CSV from your bank →")}
                </Btn>
              </div>
            )}
          </Card>
        )}
        {byDay.map(([date, items]) => (
          <div key={date} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: T.faint, margin: "10px 2px 6px" }}>
              {new Date(date + "T12:00").toLocaleDateString(lang, { weekday: "short", day: "numeric", month: "short" })}
            </div>
            {items.map(e => {
              const c = cats.find(x => x.id === e.category);
              return (
                <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: "10px 14px", marginBottom: 6, gap: 10 }}>
                  <div style={{ fontSize: 13, ...ELLIPSIS }} title={[e.note, t(c?.label || "")].filter(Boolean).join(" · ")}>
                    <span style={{ marginRight: 8 }}>{c?.emoji}</span>
                    {e.note || t(c?.label || "")}
                    <span style={{ color: T.faint, fontSize: 11, marginLeft: 6 }}>{e.note ? t(c?.label || "") : ""}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                    <Mono style={{ fontSize: 14 }}>{fmt(e.amount, 2)}</Mono>
                    {/* A card-funded row is the shadow of a debt-ledger entry.
                        Deleting it here would leave the charge — and the raised
                        balance — behind, so the ledger stays the one place it
                        can be undone. */}
                    {e.debtId ? (
                      <span title={t("Charged to {n} — undo it from that debt's activity.", { n: state.debts.find(d => d.id === e.debtId)?.name || t("your card") })}
                        style={{ fontSize: 10, color: T.gold, background: T.goldSoft, border: `1px solid ${T.gold}44`, borderRadius: 99, padding: "2px 7px", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {t("card")}
                      </span>
                    ) : (
                      <button onClick={() => up({ expenses: state.expenses.filter(x => x.id !== e.id) })} style={{ background: "none", border: "none", color: T.faint, cursor: "pointer", fontSize: 15 }}>×</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {showImport && (
        <ImportSheet state={state} up={up} t={t} lang={lang} notify={notify} onClose={() => setShowImport(false)} />
      )}
    </>
  );
}

/* ═══ BUDGET ═══ */
function Budget({ state, up, t, monthExpenses, leftover, totalBudget, totalMins, monthlyIncome, notify, premium, requirePremium }) {
  const cats = categoriesOf(state);
  const [view, setView] = useState("monthly");
  const [open, setOpen] = useState(null);
  const [newRec, setNewRec] = useState(null);
  /* One sheet for add and rename: an `id` on the draft means we're editing
     an existing category (built-in or custom) in place; `custom` tells save
     which storage path to write to. */
  const [catDraft, setCatDraft] = useState(null);
  const spentBy = (id) => monthExpenses.filter(e => e.category === id).reduce((s, e) => s + e.amount, 0);
  const vf = { monthly: 1, fortnightly: 12 / 26, weekly: 12 / 52 }[view];
  const per = (n) => fmt(n * vf);
  const cycleWord = { monthly: "mo", fortnightly: "fn", weekly: "wk" }[view];
  const recIn = (id) => (state.recurring || []).filter(r => r.category === id);

  /* One sheet for both adding and editing: an `id` on the draft means
     we're editing an existing item in place. */
  const saveRec = () => {
    if (!newRec.name || !newRec.amount) return;
    const item = { ...newRec, amount: Number(newRec.amount) };
    const list = state.recurring || [];
    up({ recurring: item.id ? list.map(x => (x.id === item.id ? item : x)) : [...list, { ...item, id: uid() }] });
    setNewRec(null);
    notify(t(item.id ? "Recurring expense updated" : "Recurring expense added — it logs itself from its next due date"));
  };

  const saveCatDraft = () => {
    if (!catDraft.name.trim()) return;
    const label = catDraft.name.trim();
    const emoji = catDraft.emoji || "🏷️";
    if (catDraft.id && catDraft.custom) {
      up({ customCategories: (state.customCategories || []).map(x => x.id === catDraft.id ? { ...x, label, emoji } : x) });
    } else if (catDraft.id) {
      up({ categoryOverrides: { ...(state.categoryOverrides || {}), [catDraft.id]: { label, emoji } } });
    } else {
      up({ customCategories: [...(state.customCategories || []), { id: uid(), label, emoji, custom: true }] });
    }
    setCatDraft(null);
    notify(t(catDraft.id ? "Category updated" : "Category added"));
  };

  /* Drops a built-in's name/emoji override, restoring the translated
     default in every language. */
  const resetCategoryOverride = (id) => {
    const rest = { ...(state.categoryOverrides || {}) };
    delete rest[id];
    up({ categoryOverrides: rest });
    setCatDraft(null);
    notify(t("Reset to default"));
  };

  /* Non-destructive: nothing is erased. Past expenses and any recurring
     items in this category are reassigned to "other" so they keep showing
     up (instead of silently vanishing from the Budget tab because their
     category id no longer matches anything). */
  const deleteCategory = (c) => {
    if (!confirm(t("Delete {n}? Past expenses stay, shown under Other.", { n: t(c.label) }))) return;
    const reassign = (arr) => arr.map(x => x.category === c.id ? { ...x, category: "other" } : x);
    /* The budget allocation goes with the category. Expenses are historical
       facts and are kept (moved to Other); a budget is a forward-looking plan
       for something that no longer exists, and leaving it behind is what made
       "Living budget" disagree with the rows above it. It is NOT folded into
       Other — that would silently inflate a category the user didn't choose. */
    const budgets = { ...state.budgets };
    delete budgets[c.id];
    up({
      customCategories: (state.customCategories || []).filter(x => x.id !== c.id),
      budgets,
      expenses: reassign(state.expenses),
      recurring: reassign(state.recurring || []),
    });
    notify(t("Category deleted"));
  };

  return (
    <>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Label>{t("Income (after tax)")}</Label>
          <NumField value={state.income} onCommit={n => up({ income: n })}
            style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 8, color: T.gold, fontFamily: "'Geist Mono', monospace", fontSize: 16, fontWeight: 600, padding: "8px 10px", width: 110, textAlign: "right" }} />
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          {["weekly", "fortnightly", "monthly"].map(f => (
            <button key={f} onClick={() => {
              if (state.incomeFreq === f) return;
              // Convert the amount so the monthly equivalent (and the whole
              // plan) stays identical — switching units must never distort
              // Unassigned. The user then edits to their real pay if needed.
              const converted = Math.round((monthlyIncomeOf(state) / FREQ_FACTOR[f]) * 100) / 100;
              up({ incomeFreq: f, income: converted });
            }} style={{
              flex: 1, background: state.incomeFreq === f ? T.goldSoft : T.midnight,
              border: `1px solid ${state.incomeFreq === f ? T.gold : T.line}`,
              color: state.incomeFreq === f ? T.gold : T.dim,
              borderRadius: 99, padding: "6px 0", fontSize: 12, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
            }}>{t(f)}</button>
          ))}
        </div>
        {state.incomeFreq !== "monthly" && (
          <div style={{ fontSize: 11, color: T.faint, marginTop: 8 }}>
            {t("≈ {a} per month — all plan maths use this equivalent.", { a: fmt(monthlyIncome) })}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
          <Label>{t("Currency")}</Label>
          <select value={state.currency || "NZD"} aria-label={t("Currency")}
            onChange={e => { applyCurrency(e.target.value); up({ currency: e.target.value }); }}
            style={{ background: T.midnight, color: T.dim, border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
          </select>
        </div>
        <div style={{ marginTop: 14, fontSize: 12, color: T.dim, lineHeight: 1.9 }}>
          <Row k={t("Income (monthly)")} v={fmt(monthlyIncome)} />
          <Row k={t("Living budget")} v={`−${fmt(totalBudget)}`} />
          <Row k={t("Debt minimums")} v={`−${fmt(totalMins)}`} />
          <Row k={t("Extra to debt")} v={`−${fmt(state.extraPayment)}`} gold />
          <div style={{ borderTop: `1px solid ${T.line}`, marginTop: 6, paddingTop: 8 }}>
            <Row k={t("Unassigned")} v={fmt(leftover)} color={leftover < 0 ? T.garnet : T.greenHi} bold />
          </div>
        </div>
        {leftover < 0 && <div style={{ marginTop: 10, fontSize: 12, color: T.garnet }}>{t("⚠️ Your plan spends {a} more than you earn. Trim a category or lower the extra payment.", { a: fmt(-leftover) })}</div>}
      </Card>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "18px 2px 8px" }}>
        <Label>{t("Category budgets")}</Label>
        <div style={{ display: "flex", gap: 4 }}>
          {["weekly", "fortnightly", "monthly"].map(f => (
            <button key={f} onClick={() => setView(f)} style={{
              background: view === f ? T.panelHi : "transparent", border: `1px solid ${view === f ? T.gold + "66" : T.line}`,
              color: view === f ? T.gold : T.faint, borderRadius: 99, padding: "4px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer",
            }}>{{ weekly: "WK", fortnightly: "FN", monthly: "MO" }[f]}</button>
          ))}
        </div>
      </div>
      {view !== "monthly" && (
        <div style={{ fontSize: 11, color: T.faint, margin: "0 2px 8px" }}>
          {t("Viewing per {c}. Budgets are stored monthly — the input still edits the monthly amount.", { c: t(view === "weekly" ? "week" : "fortnight") })}
        </div>
      )}
      {cats.map(c => {
        const b = state.budgets[c.id] || 0;
        const s = spentBy(c.id);
        const p = b > 0 ? (s / b) * 100 : 0;
        const recs = recIn(c.id);
        const recTotal = recs.reduce((sum, r) => sum + recMonthly(r), 0);
        /* Money that's committed but not yet charged: recurring total minus
           what the auto-logger already booked this month. Shown as a dim
           "reserved" bar segment so a fully-committed budget never looks
           untouched before its bills fall due. */
        const recLogged = monthExpenses.filter(e => e.category === c.id && e.recurringId).reduce((sum, e) => sum + e.amount, 0);
        const reservedUpcoming = Math.max(0, recTotal - recLogged);
        const reservedShown = b > 0 ? Math.min(reservedUpcoming, Math.max(0, b - s)) : 0;
        const pr = b > 0 ? (reservedShown / b) * 100 : 0;
        const isOpen = open === c.id;
        return (
          <Card key={c.id} style={{ marginBottom: 8, padding: "12px 14px" }}>
            <div onClick={() => setOpen(isOpen ? null : c.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                <span style={{ color: T.faint, marginRight: 6, fontSize: 10 }}>{isOpen ? "▼" : "▶"}</span>
                {c.emoji} {t(c.label)}
                {recs.length > 0 && <span style={{ marginLeft: 6, fontSize: 10, color: T.faint }}>({recs.length})</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Mono style={{ fontSize: 12, color: p > 100 ? T.garnet : T.dim }}>
                  {view !== "monthly" && <span style={{ color: T.gold }}>{per(b)}/{cycleWord} · </span>}
                  {fmt(s)} /
                </Mono>
                <NumField value={b} onClick={e => e.stopPropagation()}
                  onCommit={n => up({ budgets: { ...state.budgets, [c.id]: n } })}
                  style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 8, color: T.white, fontFamily: "'Geist Mono', monospace", fontSize: 13, padding: "6px 8px", width: 76, textAlign: "right" }} />
                <button
                  onClick={e => { e.stopPropagation(); setCatDraft({ id: c.id, name: t(c.label), emoji: c.emoji, custom: Boolean(c.custom) }); }}
                  aria-label={t("Edit")} title={t("Edit")}
                  style={{ background: "none", border: "none", color: T.gold, cursor: "pointer", padding: 0, display: "flex" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" /><path d="M13.5 6.5l4 4" />
                  </svg>
                </button>
                {c.custom && (
                  <button onClick={e => { e.stopPropagation(); deleteCategory(c); }} aria-label={t("Delete")} title={t("Delete")}
                    style={{ background: "none", border: "none", color: T.faint, cursor: "pointer", fontSize: 15, padding: 0 }}>×</button>
                )}
              </div>
            </div>
            <div style={{ height: 5, background: T.line, borderRadius: 3, marginTop: 9, overflow: "hidden", display: "flex" }}
              role="img"
              aria-label={b > 0
                ? t("{c}: {s} spent of {b} budget", { c: t(c.label), s: fmt(s), b: fmt(b) })
                : t("{c}: {s} spent, no budget set", { c: t(c.label), s: fmt(s) })}>
              <div style={{ width: `${Math.min(100, p)}%`, height: "100%", background: p > 100 ? T.garnet : p > 80 ? T.gold : T.green, transition: "width 0.3s", flexShrink: 0 }} />
              {/* 2px of surface between the spent and reserved segments. */}
              {p > 0 && pr > 0 && <div style={{ width: 2, height: "100%", background: T.panel, flexShrink: 0 }} />}
              <div style={{ width: `${pr}%`, height: "100%", background: T.gold, opacity: 0.38, transition: "width 0.3s" }} />
            </div>
            {reservedUpcoming > 0 && (
              <div style={{ fontSize: 10.5, color: `${T.gold}AA`, marginTop: 6 }}>
                {t("{a} reserved for upcoming bills", { a: fmt(reservedUpcoming) })}
              </div>
            )}
            {isOpen && (
              <div style={{ marginTop: 12, borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
                {recs.length === 0 && (
                  <div style={{ fontSize: 12, color: T.faint, marginBottom: 8 }}>
                    {t("No recurring items yet. Add the bills that live here (e.g. {ex}) and they'll log themselves.", { ex: t(RECURRING_HINTS[c.id] || "regular payments") })}
                  </div>
                )}
                {recs.map(r => (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", fontSize: 13 }}>
                    <div>
                      {r.name}
                      <span style={{ color: T.faint, fontSize: 11, marginLeft: 6 }}>{fmt(r.amount, 2)}/{{ weekly: "wk", fortnightly: "fn", monthly: "mo" }[r.freq]} · {t("next")} {r.nextDue}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Mono style={{ fontSize: 12, color: T.dim }}>{fmt(recMonthly(r))}/mo</Mono>
                      <button onClick={() => setNewRec({ ...r, amount: String(r.amount) })} aria-label={t("Edit")} title={t("Edit")}
                        style={{ background: "none", border: "none", color: T.gold, cursor: "pointer", padding: 0, display: "flex" }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" /><path d="M13.5 6.5l4 4" />
                        </svg>
                      </button>
                      <button onClick={() => up({ recurring: state.recurring.filter(x => x.id !== r.id) })} aria-label={t("Delete")} style={{ background: "none", border: "none", color: T.faint, cursor: "pointer", fontSize: 15 }}>×</button>
                    </div>
                  </div>
                ))}
                {recs.length > 0 && (
                  <div style={{ fontSize: 11, color: recTotal > b ? T.garnet : T.faint, margin: "6px 0" }}>
                    {t("Recurring commits {r}/mo of this {b} budget", { r: fmt(recTotal), b: fmt(b) })}{recTotal > b ? t(" — budget is below your fixed bills") : ""}.
                  </div>
                )}
                <Btn small variant="ghost" style={{ marginTop: 4 }} onClick={() => setNewRec({ name: "", amount: "", freq: "monthly", category: c.id, nextDue: todayISO() })}>
                  {t("+ Add recurring item")}
                </Btn>
              </div>
            )}
          </Card>
        );
      })}

      <Btn variant="ghost" style={{ width: "100%", marginTop: 4, marginBottom: 12 }}
        onClick={() => { if (!requirePremium()) return; setCatDraft({ name: "", emoji: "🏷️" }); }}>
        {t("+ Add category")}{!premium && " 🔒"}
      </Btn>

      {catDraft && (
        <Sheet title={t(catDraft.id ? "Edit category" : "New category")} onClose={() => setCatDraft(null)}>
          <div style={{ display: "flex", gap: 8 }}>
            <Input value={catDraft.emoji} onChange={e => setCatDraft({ ...catDraft, emoji: e.target.value })}
              style={{ width: 56, textAlign: "center", fontSize: 18, flexShrink: 0 }} maxLength={4} />
            <Input placeholder={t("e.g. Pets, Kids school")} value={catDraft.name}
              onChange={e => setCatDraft({ ...catDraft, name: e.target.value })} autoFocus />
          </div>
          {!catDraft.id && (
            <div style={{ fontSize: 11, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
              {t("Deleting a category later won't erase its expenses — they move to Other.")}
            </div>
          )}
          <Btn style={{ width: "100%", marginTop: 16 }} onClick={saveCatDraft}>{t(catDraft.id ? "Save changes" : "Save category")}</Btn>
          {catDraft.id && !catDraft.custom && state.categoryOverrides?.[catDraft.id] && (
            <Btn variant="ghost" style={{ width: "100%", marginTop: 8 }} onClick={() => resetCategoryOverride(catDraft.id)}>
              {t("Reset to default")}
            </Btn>
          )}
        </Sheet>
      )}

      {newRec && (
        <Sheet title={`${t("Recurring")} · ${t(cats.find(c => c.id === newRec.category)?.label || "")}`} onClose={() => setNewRec(null)}>
          <Label style={{ marginBottom: 5 }}>{t("Name")}</Label>
          <Input placeholder={t("e.g. Netflix, Power, Transfer to parents")} value={newRec.name} onChange={e => setNewRec({ ...newRec, name: e.target.value })} />
          <Label style={{ margin: "12px 0 5px" }}>{t("Amount")}</Label>
          <Input type="number" inputMode="decimal" placeholder="0.00" value={newRec.amount} onChange={e => setNewRec({ ...newRec, amount: e.target.value })} />
          <Label style={{ margin: "12px 0 5px" }}>{t("Repeats")}</Label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {FREQS.map(f => (
              <button key={f} onClick={() => setNewRec({ ...newRec, freq: f })} style={{
                background: newRec.freq === f ? T.goldSoft : T.midnight,
                border: `1px solid ${newRec.freq === f ? T.gold : T.line}`,
                color: newRec.freq === f ? T.gold : T.dim,
                borderRadius: 99, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
              }}>{freqLabel(t, f)}</button>
            ))}
          </div>
          <Label style={{ margin: "12px 0 5px" }}>{t("Next due date")}</Label>
          <Input type="date" value={newRec.nextDue} onChange={e => setNewRec({ ...newRec, nextDue: e.target.value })} />
          <Btn style={{ width: "100%", marginTop: 16 }} onClick={saveRec}>{t(newRec.id ? "Save changes" : "Save recurring expense")}</Btn>
        </Sheet>
      )}
    </>
  );
}

/* ═══ ALLOCATION (the Grow tab's single source of truth for dates) ═══
   Every destination on this tab draws from ONE pot of spare money. Before
   v0.29.0 each was projected as if it received all of it — the deposit date
   and the savings-goal nudge both assumed 100%, and nothing reconciled them.
   Building the destination list here, once, and running it through allocate()
   means every date on the tab comes from the same walk of the same money. */
const DEST_INVEST = "invest";
const DEST_HOME = "home";
const DEST_EMERGENCY = "emergency";
const goalDest = (id) => `goal:${id}`;

/* State → ordered destinations for allocate(). Ids not present in
   state.allocationOrder append at the end in their natural order. */
function buildDestinations(state, country) {
  const home = state.home || DEFAULT_STATE.home;
  const list = [];

  /* Emergency fund is auto-computed and present whenever there's a real
     essential-spending base to buffer against — no new input for the TARGET,
     which is the differentiated part (see essentialMonthly/emergencyTarget in
     money.js). It defaults to first in the queue, matching standard advice
     to buffer before other goals, but reorders like anything else.
     Deliberate simplification: if a user already created a manual goal named
     "Emergency fund" before this shipped, it is not auto-merged into this —
     both appear, since matching free-text goal names across three languages
     reliably isn't possible without silently guessing at user intent. */
  const emergencyNeed = emergencyTarget(state, 3);
  if (emergencyNeed > 0) {
    const fund = state.emergencyFund || DEFAULT_STATE.emergencyFund;
    list.push({ id: DEST_EMERGENCY, kind: "emergency", need: emergencyNeed, already: fund.saved || 0 });
  }

  for (const g of state.savingsGoals || []) {
    list.push({ id: goalDest(g.id), kind: "goal", goal: g, need: g.target || 0, already: g.saved || 0, targetDate: g.targetDate || null });
  }
  if (home.active) {
    const plan = homePlan(home, country, 0); // amounts only; timing comes from allocate()
    list.push({ id: DEST_HOME, kind: "home", need: plan.depositNeeded, already: plan.available });
  }
  list.push({ id: DEST_INVEST, kind: "invest", ongoing: true, need: 0, already: 0 });

  const order = state.allocationOrder || [];
  const rank = (d) => {
    const i = order.indexOf(d.id);
    // Unranked items keep their natural order after the ranked ones, except
    // investing which always trails — it's ongoing, so anything "after" it
    // would never start.
    if (d.id === DEST_INVEST) return Number.MAX_SAFE_INTEGER;
    return i === -1 ? order.length + list.indexOf(d) : i;
  };
  return [...list].sort((a, b) => rank(a) - rank(b));
}

function AllocationCard({ state, up, t, lang, dests, alloc, avgSaveable }) {
  const order = dests.map(d => d.id);
  const move = (id, delta) => {
    const movable = order.filter(x => x !== DEST_INVEST);
    const i = movable.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= movable.length) return;
    [movable[i], movable[j]] = [movable[j], movable[i]];
    up({ allocationOrder: [...movable, DEST_INVEST] });
  };
  const label = (d) => d.kind === "goal" ? `${d.goal.emoji || "🎯"} ${d.goal.name}`
    : d.kind === "home" ? t("🏠 House deposit")
    : d.kind === "emergency" ? t("🛟 Emergency fund")
    : t("📈 Investing");

  return (
    <Card style={{ marginTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <Label style={{ ...ELLIPSIS }}>{t("Where your spare money goes")}</Label>
        <Mono style={{ color: T.gold, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{fmt(avgSaveable)}/{t("mo")}</Mono>
      </div>
      <div style={{ fontSize: 11.5, color: T.faint, lineHeight: 1.5, margin: "8px 0 12px" }}>
        {t("One at a time, in this order — each starts when the one above it is funded. Reorder to change what gets your money first.")}
      </div>
      {dests.map((d, idx) => {
        const a = alloc[d.id] || {};
        const isFirst = idx === 0;
        const canMove = d.id !== DEST_INVEST;
        return (
          <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: idx === 0 ? "none" : `1px solid ${T.line}` }}>
            <Mono style={{ color: T.faint, fontSize: 11, width: 14, flexShrink: 0 }}>{idx + 1}</Mono>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, ...ELLIPSIS }}>{label(d)}</div>
              <div style={{ fontSize: 11, color: a.late ? T.garnet : T.faint, marginTop: 2 }}>
                {a.ongoing
                  ? (a.startMonth === 0 ? t("gets what's left each month") : a.startMonth == null ? t("nothing spare yet") : t("from {d}", { d: monthName(addMonths(new Date(), a.startMonth), lang) }))
                  : a.months == null ? t("no date yet")
                  : a.remaining <= 0 ? t("funded")
                  : t("ready {d}", { d: monthName(a.readyDate, lang) })}
                {isFirst && !a.ongoing && a.remaining > 0 && ` · ${t("getting your money now")}`}
              </div>
            </div>
            {canMove && (
              <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                <button onClick={() => move(d.id, -1)} aria-label={t("Move up")} title={t("Move up")}
                  style={{ background: "none", border: `1px solid ${T.line}`, borderRadius: 6, color: T.dim, cursor: "pointer", fontSize: 11, padding: "2px 7px" }}>↑</button>
                <button onClick={() => move(d.id, 1)} aria-label={t("Move down")} title={t("Move down")}
                  style={{ background: "none", border: `1px solid ${T.line}`, borderRadius: 6, color: T.dim, cursor: "pointer", fontSize: 11, padding: "2px 7px" }}>↓</button>
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}

/* Emergency fund — the concept was previously absent entirely, telling that a
   test user invented "Emergency fund" as a manual goal. Its TARGET needs no
   input (3 months of essentials, computed from the Budget/Debts tabs already
   filled in); "saved so far" is the one number only the user can supply, kept
   in its own small piece of state rather than overloading savingsGoals. */
function EmergencyFundCard({ state, up, t, lang, alloc }) {
  const need = emergencyTarget(state, 3);
  if (need <= 0) return null; // nothing essential to buffer against yet
  const fund = state.emergencyFund || DEFAULT_STATE.emergencyFund;
  const a = alloc[DEST_EMERGENCY] || {};
  const pct = Math.min(100, (fund.saved / need) * 100);
  const statusLabel = a.remaining <= 0 ? t("funded")
    : a.months == null ? t("no date yet")
    : t("ready {d}", { d: monthName(a.readyDate, lang) });

  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <Label style={{ ...ELLIPSIS }}>{t("🛟 Emergency fund")}</Label>
        <span style={{ fontSize: 11, color: T.faint, flexShrink: 0 }}>{statusLabel}</span>
      </div>
      <div style={{ fontSize: 11.5, color: T.faint, lineHeight: 1.5, margin: "8px 0 10px" }}>
        {t("3 months of essentials — rent, groceries, utilities, transport, insurance, your recurring bills, and every debt minimum. Sized from your own numbers.")}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.dim, marginBottom: 5 }}>
        <Mono>{fmt(fund.saved)} / {fmt(need)}</Mono>
        <span>{pct.toFixed(0)}%</span>
      </div>
      <div style={{ height: 6, background: T.line, borderRadius: 3, overflow: "hidden" }}
        role="img" title={`${t("Emergency fund")} · ${fmt(fund.saved)} / ${fmt(need)}`}
        aria-label={`${t("Emergency fund")} · ${fmt(fund.saved)} / ${fmt(need)} · ${pct.toFixed(0)}%`}>
        <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? T.greenHi : T.gold, borderRadius: 3 }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, gap: 10 }}>
        <Label style={{ marginBottom: 0 }}>{t("Saved so far")}</Label>
        <NumField value={fund.saved} onCommit={v => up({ emergencyFund: { ...fund, saved: Math.max(0, v) } })}
          style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 8, color: T.white, fontFamily: "'Geist Mono', monospace", fontSize: 14, padding: "8px 9px", width: 108, textAlign: "right" }} />
      </div>
    </Card>
  );
}

/* ═══ SAVE (folds into the Grow tab, above the Invest section) ═══ */
function SaveSection({ state, up, t, lang, notify, sim, monthlyIncome, totalBudget, dests, alloc, avgSaveable, map, premium, requirePremium }) {
  const [goalDraft, setGoalDraft] = useState(null);
  const [contribFor, setContribFor] = useState(null);
  const [contribAmt, setContribAmt] = useState("");

  const goals = state.savingsGoals || [];

  /* `map`, `avgSaveable` and `alloc` are computed once in the Grow root and
     passed down — recomputing them here is what let this section and the
     first-home planner disagree about the same money. */
  /* Only months that actually leave something over count toward the year's
     savings — a short month contributes a hole, not negative savings. */
  const yearSaveable = map.reduce((s, m) => s + Math.max(0, m.saveable), 0);
  const savingsRate = monthlyIncome > 0 ? (avgSaveable / monthlyIncome) * 100 : 0;
  const barTotal = Math.max(1, ...map.map(m => m.committed + Math.max(0, m.saveable)));

  /* Where the map's "committed" line drops between consecutive months —
     either a specific dated debt's endDate just passed, or (matching
     sim.months) the whole plan just went debt-free and the extra payment
     freed up. Real transitions only; nothing fabricated. */
  const events = [];
  for (let i = 1; i < map.length; i++) {
    const delta = map[i - 1].committed - map[i].committed;
    if (delta <= 1) continue;
    const endedDebt = state.debts.find(d => d.balance > 0 && d.endDate &&
      new Date(d.endDate + "T12:00") >= map[i - 1].monthStart && new Date(d.endDate + "T12:00") < map[i].monthStart);
    const reason = endedDebt ? t("{n} ends", { n: endedDebt.name }) : sim.months === i ? t("you're debt-free") : null;
    if (reason) events.push({ monthStart: map[i].monthStart, delta, reason });
  }

  const saveGoal = () => {
    if (!goalDraft.name.trim() || !Number(goalDraft.target)) return;
    const g = {
      id: goalDraft.id || uid(), name: goalDraft.name.trim(), emoji: goalDraft.emoji || "🎯",
      target: Number(goalDraft.target) || 0, targetDate: goalDraft.targetDate || null,
      saved: goalDraft.id ? goalDraft.saved : 0,
    };
    up({ savingsGoals: goalDraft.id ? goals.map(x => x.id === g.id ? g : x) : [...goals, g] });
    setGoalDraft(null);
    notify(t(goalDraft.id ? "Goal updated" : "Goal added"));
  };

  const deleteGoal = (g) => {
    if (!confirm(t("Delete {n}? This can't be undone.", { n: g.name }))) return;
    up({ savingsGoals: goals.filter(x => x.id !== g.id) });
    notify(t("Goal deleted"));
  };

  const addToGoal = (goal, amount) => {
    if (!amount || amount <= 0) return;
    up({ savingsGoals: goals.map(x => x.id === goal.id ? { ...x, saved: x.saved + amount } : x) });
    notify(t("{a} added to {n} 🎉", { a: fmt(amount), n: goal.name }));
  };

  /* The destination actually receiving money right now — the first unfunded
     one in the waterfall. This used to be `goals[0]`, i.e. whichever goal was
     created first, while the gold nudge button ("Add it to X →") moved real
     money to it. If the user's real priority sat lower in the list, the button
     quietly paid the wrong thing. */
  const nextGoalDest = dests.find(d => d.kind === "goal" && (alloc[d.id]?.remaining || 0) > 0);
  const nextGoal = nextGoalDest?.goal || null;
  const thisMonthSaveable = map[0]?.saveable || 0;

  return (
    <>
      <div style={{ textAlign: "center", padding: "20px 8px 6px" }}>
        <Label style={{ color: T.gold }}>{t("You can save")}</Label>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 44, lineHeight: 1.05, margin: "10px 0 4px" }}>
          {fmt(avgSaveable)}<span style={{ fontSize: 18, color: T.dim }}>/{t("mo")}</span>
        </div>
        {monthlyIncome > 0 && (
          <div style={{ display: "inline-block", background: T.goldSoft, color: T.gold, borderRadius: 99, padding: "5px 14px", fontSize: 13, fontWeight: 600 }}>
            {t("Savings rate {r}%", { r: savingsRate.toFixed(0) })}
          </div>
        )}
      </div>

      <Card style={{ marginTop: 14 }}>
        <Label style={{ marginBottom: 12 }}>{t("This year's money map")}</Label>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 74 }}
          role="img"
          aria-label={t("Next 12 months, saveable versus committed. About {a} saveable per month on average.", { a: fmt(avgSaveable) })}>
          {map.map(m => {
            /* A short month has no saveable segment to draw — its committed bar
               goes garnet instead, which is the one thing the eye should catch
               scanning the year. */
            const short = m.shortfall > 0;
            const saveH = short ? 0 : Math.round((m.saveable / barTotal) * 70);
            const commitH = Math.round((m.committed / barTotal) * 70);
            return (
              <div key={m.key} title={`${monthName(m.monthStart, lang)} · ${short ? `${t("short by")} ${fmt(m.shortfall)}` : `${t("saveable")} ${fmt(m.saveable)}`} · ${t("committed")} ${fmt(m.committed)}`} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                <div style={{ width: "100%", height: saveH, background: T.gold, borderRadius: "2px 2px 0 0" }} />
                {/* 2px of surface separates the two stacked fills — a gap, not a
                    border, so no non-data ink is added to the mark. */}
                {saveH > 0 && commitH > 0 && <div style={{ width: "100%", height: 2, background: T.panel, flexShrink: 0 }} />}
                <div style={{ width: "100%", height: Math.max(2, commitH), background: short ? T.garnet : T.line, borderRadius: short ? "2px 2px 0 0" : 0 }} />
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 5 }}>
          {map.map(m => (
            <div key={m.key} style={{ flex: 1, textAlign: "center", fontSize: 9, color: T.faint }}>
              {m.monthStart.toLocaleDateString(lang, { month: "narrow" })}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 10.5, color: T.dim }}>
          <Swatch color={T.gold}>{t("saveable")}</Swatch>
          <Swatch color={T.line}>{t("committed")}</Swatch>
          {map.some(m => m.shortfall > 0) && <Swatch color={T.garnet}>{t("short")}</Swatch>}
        </div>
        <div style={{ fontSize: 12, color: T.dim, marginTop: 12, lineHeight: 1.6 }}>
          {t("≈ {a} you could save this year.", { a: fmt(yearSaveable) })}
        </div>
        {/* The differentiated half: everyone can draw the chart above, almost
            nobody closes the loop. A lumpy bill isn't a warning to sit with —
            it's a monthly number you can start setting aside today, and Aurio
            already has the savings goal to put it in. */}
        {(() => {
          const lumpy = [];
          for (let i = 0; i < map.length; i++) {
            for (const b of map[i].causes) {
              if (lumpy.some(x => x.id === b.id)) continue; // first occurrence only
              lumpy.push({ ...b, monthsAway: Math.max(1, i), monthStart: map[i].monthStart });
            }
          }
          if (!lumpy.length) return null;
          const startGoal = (b) => {
            if (!requirePremium()) return;
            setGoalDraft({ name: b.name, emoji: "🧾", target: String(b.amount), targetDate: b.date });
          };
          return (
            <div style={{ marginTop: 14, borderTop: `1px solid ${T.line}`, paddingTop: 12 }}>
              <Label style={{ marginBottom: 8 }}>{t("Big bills ahead")}</Label>
              {lumpy.slice(0, 4).map(b => (
                <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0", fontSize: 12.5 }}>
                  <div style={{ minWidth: 0, ...ELLIPSIS }}>
                    {b.name}
                    <span style={{ color: T.faint, fontSize: 11, marginLeft: 6 }}>
                      {fmt(b.amount)} · {monthName(b.monthStart, lang)}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <Mono style={{ fontSize: 12, color: T.gold }}>{fmt(b.amount / b.monthsAway)}{t("/mo")}</Mono>
                    <Btn small variant="ghost" onClick={() => startGoal(b)}>{t("Set aside")}{!premium && " 🔒"}</Btn>
                  </div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: T.faint, marginTop: 6, lineHeight: 1.5 }}>
                {t("Put that much away each month and the bill is already paid for when it lands.")}
              </div>
            </div>
          );
        })()}

        {events.map((e, i) => (
          <div key={i} style={{ fontSize: 12, color: T.gold, marginTop: 6, lineHeight: 1.5 }}>
            {t("→ From {d}: +{a}/mo ({r})", { d: monthName(e.monthStart, lang), a: fmt(e.delta), r: e.reason })}
          </div>
        ))}
      </Card>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "18px 2px 8px" }}>
        <Label>{t("Savings goals")}</Label>
      </div>
      {goals.map(g => {
        const pct = g.target > 0 ? Math.min(100, (g.saved / g.target) * 100) : 0;
        /* Goals used to show only a bar and a percentage while the home plan
           and the retirement projection each got a date. They now get the same
           treatment — including an honest verdict when the user's own target
           date can't be met at the current rate and priority. */
        const a = alloc[goalDest(g.id)] || {};
        const monthsLate = a.late && a.readyDate && g.targetDate
          ? Math.max(1, Math.round((a.readyDate - new Date(g.targetDate + "T12:00")) / 2629800000))
          : 0;
        return (
          <Card key={g.id} style={{ marginBottom: 8, padding: "12px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {g.emoji} {g.name}
                {g.targetDate && <span style={{ marginLeft: 6, fontSize: 10.5, color: T.faint, fontWeight: 400 }}>{t("by {d}", { d: monthName(new Date(g.targetDate + "T12:00"), lang) })}</span>}
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button onClick={() => setGoalDraft({ ...g })} aria-label={t("Edit")} title={t("Edit")} style={{ background: "none", border: "none", color: T.gold, cursor: "pointer", padding: 0, display: "flex" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" /><path d="M13.5 6.5l4 4" />
                  </svg>
                </button>
                <button onClick={() => deleteGoal(g)} aria-label={t("Delete")} style={{ background: "none", border: "none", color: T.faint, cursor: "pointer", fontSize: 15, padding: 0 }}>×</button>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.dim, marginTop: 8, marginBottom: 5 }}>
              <Mono>{fmt(g.saved)} / {fmt(g.target)}</Mono>
              <span>{pct.toFixed(0)}%</span>
            </div>
            <div style={{ height: 6, background: T.line, borderRadius: 3, overflow: "hidden" }}
              role="img" title={`${g.name} · ${fmt(g.saved)} / ${fmt(g.target)}`}
              aria-label={`${g.name} · ${fmt(g.saved)} / ${fmt(g.target)} · ${pct.toFixed(0)}%`}>
              <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: pct >= 100 ? T.greenHi : T.gold, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 11.5, marginTop: 7, lineHeight: 1.5, color: a.late ? T.garnet : T.faint }}>
              {a.remaining <= 0 ? t("✓ Funded")
                : a.months == null ? t("No date yet — nothing spare each month")
                : a.late
                  ? t("⚠️ On this plan you get there {d}, {n} after your target. Move it up the list, or lower the target.", {
                      d: monthName(a.readyDate, lang),
                      n: fmtTerm(monthsLate, t),
                    })
                  : t("On track for {d}", { d: monthName(a.readyDate, lang) })}
            </div>
            <Btn small variant="ghost" style={{ marginTop: 10 }} onClick={() => { setContribFor(g); setContribAmt(""); }}>{t("+ Add money")}</Btn>
          </Card>
        );
      })}
      <Btn variant="ghost" style={{ width: "100%", marginTop: 4, marginBottom: 12 }} onClick={() => setGoalDraft({ name: "", emoji: "🎯", target: "", targetDate: "" })}>
        {t("+ New goal")}
      </Btn>

      {thisMonthSaveable > 100 && nextGoal && (
        <Card style={{ marginBottom: 12, background: T.goldSoft, borderColor: `${T.gold}44` }}>
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            {t("💰 You have {a} saveable this month.", { a: fmt(thisMonthSaveable) })}
          </div>
          <Btn small style={{ width: "100%", marginTop: 10 }} onClick={() => addToGoal(nextGoal, thisMonthSaveable)}>
            {t("Add it to {n} →", { n: nextGoal.name })}
          </Btn>
        </Card>
      )}

      {contribFor && (
        <Sheet title={t("Add to {n}", { n: contribFor.name })} onClose={() => setContribFor(null)}>
          <Input type="number" inputMode="decimal" placeholder={t("Amount")} value={contribAmt} onChange={e => setContribAmt(e.target.value)} autoFocus />
          {thisMonthSaveable > 0 && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <Btn small variant="ghost" onClick={() => setContribAmt(String(Math.round(thisMonthSaveable)))}>{fmt(thisMonthSaveable)}</Btn>
            </div>
          )}
          <Btn style={{ width: "100%", marginTop: 14 }} onClick={() => { addToGoal(contribFor, Number(contribAmt)); setContribFor(null); }}>{t("Add money")}</Btn>
        </Sheet>
      )}

      {goalDraft && (
        <Sheet title={t(goalDraft.id ? "Edit goal" : "New goal")} onClose={() => setGoalDraft(null)}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <Input value={goalDraft.emoji} onChange={e => setGoalDraft({ ...goalDraft, emoji: e.target.value })}
              style={{ width: 56, textAlign: "center", fontSize: 18, flexShrink: 0 }} maxLength={4} />
            <Input placeholder={t("e.g. Emergency fund, House deposit")} value={goalDraft.name}
              onChange={e => setGoalDraft({ ...goalDraft, name: e.target.value })} autoFocus />
          </div>
          <Label style={{ marginBottom: 5 }}>{t("Target amount")}</Label>
          <Input type="number" inputMode="decimal" placeholder="0.00" value={goalDraft.target}
            onChange={e => setGoalDraft({ ...goalDraft, target: e.target.value })} style={{ marginBottom: 12 }} />
          <Label style={{ marginBottom: 5 }}>{t("Target date (optional)")}</Label>
          <Input type="date" value={goalDraft.targetDate || ""} onChange={e => setGoalDraft({ ...goalDraft, targetDate: e.target.value })} />
          <Btn style={{ width: "100%", marginTop: 16 }} onClick={saveGoal}>{t(goalDraft.id ? "Save changes" : "Save goal")}</Btn>
        </Sheet>
      )}
    </>
  );
}

/* ═══ FIRST HOME (folds into the Grow tab, between Save and Invest) ═══ */
const HOME_COUNTRY = { NZD: "NZ", AUD: "AU" };

/* Country-specific first-home guidance and a deposit-ready date, sitting
   between Save and Invest — the goal that funds a house, next to the goal
   that funds retirement. Opt-in: state.home.active stays false until the
   user turns it on, since not everyone is buying.

   The hero mirrors Home's debt-free-date reveal deliberately: this is the
   one comparison only a debt-first app can make. Lenders weigh existing debt
   when sizing a loan, so when the debt clears LATER than the deposit is
   ready, the debt date is really the one deciding when someone can move in —
   worth saying plainly rather than leaving two unrelated dates on the page. */
function FirstHomeSection({ state, up, t, lang, sim, totalDebt, avgSaveable, alloc, dests }) {
  const [showHelp, setShowHelp] = useState(false);
  const home = state.home || DEFAULT_STATE.home;
  const country = HOME_COUNTRY[state.currency] || null;

  /* Amounts still come from homePlan(); the TIMING now comes from the
     allocation waterfall. Passing avgSaveable straight in was the defect —
     it dated the deposit as if it received every spare dollar, while the
     savings-goal nudge assumed exactly the same thing about the same money. */
  const plan = useMemo(() => homePlan(home, country, avgSaveable),
    [home.price, home.depositPct, home.cash, home.scheme, country, avgSaveable]);
  const homeAlloc = alloc[DEST_HOME] || {};
  const homeRank = Math.max(0, dests.findIndex(d => d.id === DEST_HOME));
  const aheadOfHome = dests.slice(0, homeRank).filter(d => (alloc[d.id]?.remaining || 0) > 0);
  // The low-deposit comparison a scheme sheet references (e.g. AU's 5%
  // First Home Guarantee) — same engine, just a different depositPct.
  const plan5 = useMemo(() => homePlan({ ...home, depositPct: 5 }, country, avgSaveable),
    [home.price, home.cash, home.scheme, country, avgSaveable]);
  const pct = plan.depositNeeded > 0 ? Math.min(100, (plan.available / plan.depositNeeded) * 100) : 0;

  const set = (patch) => up({ home: { ...home, ...patch } });
  const numIn = (val, onChange, w = 108) => (
    <NumField value={val} onCommit={onChange}
      style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 8, color: T.white, fontFamily: "'Geist Mono', monospace", fontSize: 14, padding: "8px 9px", width: w, textAlign: "right" }} />
  );
  const field = (label, input) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
      <Label style={{ ...ELLIPSIS }}>{label}</Label>
      <div style={{ flexShrink: 0 }}>{input}</div>
    </div>
  );

  if (!home.active) {
    return (
      <>
        <Card>
          <Label style={{ marginBottom: 8 }}>{t("Saving for a property deposit?")}</Label>
          <div style={{ fontSize: 12.5, color: T.dim, lineHeight: 1.6, marginBottom: 12 }}>
            {t("Track your deposit alongside your debt payoff and see how the two dates compare — a debt clearing after your deposit is ready is usually what actually decides your move-in date.")}
          </div>
          <Btn small onClick={() => set({ active: true })}>{t("Set up my deposit plan")}</Btn>
        </Card>
      </>
    );
  }

  /* Timing from the waterfall, not from homePlan()'s "if it got everything". */
  const readyDate = plan.gap <= 0 ? new Date() : homeAlloc.readyDate || null;
  const noDate = plan.gap > 0 && readyDate == null;
  const debtFreeDate = totalDebt > 0 && !sim.neverPaysOff ? sim.freeDate : null;
  const comparison = readyDate && debtFreeDate
    ? (debtFreeDate > readyDate ? "debt-later" : "deposit-later")
    : null;
  const countryName = country === "NZ" ? t("New Zealand") : country === "AU" ? t("Australia") : null;
  const schemeLabel = country === "NZ" ? t("KiwiSaver (usable)") : country === "AU" ? t("Super (usable, FHSS)") : t("Other savings");

  return (
    <>
      <div style={{ textAlign: "center", padding: "20px 8px 6px" }}>
        <Label style={{ color: T.gold }}>{t("Deposit ready")}</Label>
        {/* months == null means "nothing to extrapolate from" (avgSaveable <= 0)
            — a missing input, not a bad outcome, so this stays T.faint like any
            other empty state rather than T.garnet, which everywhere else in the
            app means "something's actually wrong". The wording also has to name
            the specific number that's missing: "savings" is already the label on
            two lump-sum fields right below (Cash saved, KiwiSaver), so a vaguer
            "add savings" read as "you didn't fill those in", when the real gap
            is the monthly saveable RATE computed in the Save section above. */}
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: noDate ? 24 : 44, lineHeight: 1.25, margin: "10px 0 6px", color: noDate ? T.faint : T.white }}>
          {plan.gap <= 0 ? t("Ready now 🎉") : noDate ? t("No date yet — nothing saveable each month") : monthName(readyDate, lang)}
        </div>
        {noDate && (
          <div style={{ fontSize: 12, color: T.faint, lineHeight: 1.5, maxWidth: 320, margin: "0 auto" }}>
            {t("The \"You can save\" amount above is $0 — free up money in your budget or income to project a date here.")}
          </div>
        )}
        {/* The deposit isn't first in the queue, so say what it's waiting for
            rather than quietly showing a later date with no explanation. */}
        {!noDate && plan.gap > 0 && aheadOfHome.length > 0 && (
          <div style={{ fontSize: 12, color: T.faint, lineHeight: 1.5, maxWidth: 340, margin: "0 auto" }}>
            {t("Starts {d}, once {n} ahead of it are funded — reorder in \"Where your spare money goes\" to change that.", {
              d: homeAlloc.startMonth ? monthName(addMonths(new Date(), homeAlloc.startMonth), lang) : t("now"),
              n: aheadOfHome.length,
            })}
          </div>
        )}
      </div>

      <Card style={{ marginTop: 4 }}>
        <div
          role="img"
          title={t("{a} of {b} saved toward your deposit", { a: fmt(plan.available), b: fmt(plan.depositNeeded) })}
          aria-label={t("{a} of {b} saved toward your deposit — {p}%", { a: fmt(plan.available), b: fmt(plan.depositNeeded), p: Math.round(pct) })}
        >
          <div style={{ height: 6, background: T.line, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: T.gold, borderRadius: 3, transition: "width 0.4s" }} />
          </div>
        </div>
        <div style={{ fontSize: 12, color: T.dim, marginTop: 6 }}>
          {t("{a} of {b}", { a: fmt(plan.available), b: fmt(plan.depositNeeded) })}
        </div>

        {comparison && (
          <div style={{
            marginTop: 12, borderRadius: 10, padding: "10px 12px", fontSize: 12, lineHeight: 1.55,
            background: comparison === "debt-later" ? T.garnetSoft : T.greenSoft,
            border: `1px solid ${comparison === "debt-later" ? T.garnet : T.green}44`,
            color: comparison === "debt-later" ? T.garnet : T.greenHi,
          }}>
            {comparison === "debt-later"
              ? t("⚠️ Your debt clears {d} — after your deposit is ready. Lenders weigh your debts when sizing a loan, so on this plan the debt date is the one moving your move-in date.", { d: monthName(debtFreeDate, lang) })
              : t("Your deposit is ready {d}, before you're debt-free {b} — clearing the debt first is likely what decides when a lender says yes.", { d: monthName(readyDate, lang), b: monthName(debtFreeDate, lang) })}
          </div>
        )}

        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {field(t("Target price"), numIn(home.price, v => set({ price: v })))}
          {field(`${t("Deposit needed")} · ${home.depositPct}%`, numIn(home.depositPct, v => set({ depositPct: v }), 64))}
          {field(t("Cash saved"), numIn(home.cash, v => set({ cash: v })))}
          {field(schemeLabel, numIn(home.scheme, v => set({ scheme: v })))}
        </div>
        {country && (
          <div style={{ fontSize: 11, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
            {t("First-home schemes only — leave this at 0 for an investment property.")}
          </div>
        )}

        <div style={{ borderTop: `1px solid ${T.line}`, marginTop: 14, paddingTop: 10 }}>
          <Row k={t("Still to find")} v={fmt(plan.gap)} bold color={plan.gap <= 0 ? T.greenHi : undefined} />
          {/* avgSaveable <= 0 is already explained under the hero above —
              repeating it here would say the same thing twice on one screen.
              The term is measured from when this destination actually STARTS
              receiving money, not from today. */}
          {plan.gap > 0 && avgSaveable > 0 && homeAlloc.months != null && (
            <div style={{ fontSize: 12, color: T.dim, marginTop: 6 }}>
              {t("Saving {a}/mo · {n} of funding", { a: fmt(avgSaveable), n: fmtTerm(Math.max(1, homeAlloc.months - (homeAlloc.startMonth || 0)), t) })}
            </div>
          )}
        </div>

        {countryName && (
          <Btn small variant="ghost" style={{ width: "100%", marginTop: 14 }} onClick={() => setShowHelp(true)}>
            {t("See {c} first-home help", { c: countryName })}
          </Btn>
        )}
        <button onClick={() => set({ active: false })} style={{ background: "none", border: "none", color: T.faint, fontSize: 11, marginTop: 10, cursor: "pointer", textDecoration: "underline" }}>
          {t("Hide this section")}
        </button>
      </Card>

      {showHelp && country && (
        <HomeSchemeSheet t={t} lang={lang} country={country} countryName={countryName} home={home} plan={plan} plan5={plan5} onClose={() => setShowHelp(false)} />
      )}
    </>
  );
}

/* Country scheme sheet: factual guidance, no product recommendations — same
   standard as the investing GUIDES. `calc` entries get one personalised line
   computed from the user's own numbers, which is what turns generic
   guidance into an actual answer ("you already have enough at 5%"). */
function HomeSchemeSheet({ t, lang, country, countryName, home, plan, plan5, onClose }) {
  const entries = (HOME_SCHEMES[lang] || HOME_SCHEMES.en)[country] || [];
  const calcLine = (kind) => {
    if (kind === "kiwisaver") return t("Your KiwiSaver balance {a} → {b} usable toward the deposit.", { a: fmt(home.scheme), b: fmt(plan.schemeUsable) });
    if (kind === "fhss") return t("Your balance {a} → {b} usable under the $50,000 lifetime cap.", { a: fmt(home.scheme), b: fmt(plan.schemeUsable) });
    if (kind === "fhbg5") {
      return plan5.gap <= 0
        ? t("At a 5% deposit your target is {a} — you already have enough.", { a: fmt(plan5.depositNeeded) })
        : t("At a 5% deposit your target is {a} — {b} still to find.", { a: fmt(plan5.depositNeeded), b: fmt(plan5.gap) });
    }
    return null;
  };

  return (
    <Sheet title={t("{c} first-home help", { c: countryName })} onClose={onClose}>
      {entries.map((e) => (
        <div key={e.title} style={{
          marginBottom: 12, borderRadius: 10, padding: "12px 14px",
          background: e.warning ? T.garnetSoft : T.midnight,
          border: `1px solid ${e.warning ? T.garnet + "44" : T.line}`,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: e.warning ? T.garnet : T.white, marginBottom: 6 }}>
            {e.emoji} {e.title}
          </div>
          <div style={{ fontSize: 12.5, color: e.warning ? T.garnet : T.dim, lineHeight: 1.6 }}>{e.body}</div>
          {e.calc && (
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: T.gold }}>{calcLine(e.calc)}</div>
          )}
        </div>
      ))}
      <div style={{ fontSize: 11, color: T.faint, marginTop: 4, lineHeight: 1.6 }}>
        {t("General information, not financial advice, and not a recommendation of any product. Scheme rules change — verify current eligibility and figures with the relevant government agency or lender before relying on them. Checked {v}.", { v: HOME_VERIFIED })}
      </div>
    </Sheet>
  );
}

/* ═══ INVEST ═══ */
/* "What do I do with my next dollar?" — the question people actually arrive
   at this tab with, and the one no other budgeting app can answer this way:
   Aurio already knows every debt's APR AND the user's own investing
   assumption in one place. Standard, defensible sequencing (starter buffer →
   high-interest debt → full emergency fund → dated goals/deposit →
   investing), evaluated top-down against real state — see nextStep() in
   money.js. Only the first unmet step is ever presented as the
   recommendation; copy is written as reasoning the user can check ("it costs
   X, your assumption is Y"), never a bare instruction. */
function DoThisNextCard({ t, lang, step, expReturn, setTab, goGrow }) {
  const { next, steps } = step;
  /* Only the steps still genuinely ahead — a step that's already satisfied
     (no debt over the return assumption, no dated goal) has nothing useful to
     show in "Then:" and, for the debt step specifically, no debt to name at
     all (steps.debt is null once every debt clears the assumption). */
  const then = steps.slice(steps.indexOf(next) + 1).filter(s => !s.done);
  const stepLabel = (s) => s.id === "starter" ? t("🌱 Starter buffer")
    : s.id === "debt" ? `💳 ${s.debt ? s.debt.name : t("High-interest debt")}`
    : s.id === "fullBuffer" ? t("🛟 Full emergency fund")
    : s.id === "goals" ? t("🎯 Goals & deposit")
    : t("📈 Investing");

  /* Each step now names where to go, not just what to do. Before sub-tabs
     existed the only honest instruction was "scroll down". */
  let title, body, cta, go;
  if (next.id === "starter") {
    title = t("Build a starter buffer first");
    cta = t("Go to your buffer");
    go = () => goGrow("save");
    body = t("{a} covers one month of essentials — rent, groceries, utilities, transport, insurance, your bills, and every debt minimum. A small buffer first means the next surprise bill doesn't have to become a new debt.", { a: fmt(next.target) });
  } else if (next.id === "debt") {
    title = t("Clear {n} before investing", { n: next.debt.name });
    body = t("It costs {apr}%/yr. Your investing assumption is {r}%/yr — paying it off is a guaranteed {apr}% return, and you won't reliably beat that in the market.", { apr: next.debt.apr, r: expReturn });
    cta = t("Go to Debts");
    go = () => setTab("debts");
  } else if (next.id === "fullBuffer") {
    title = t("Grow your emergency fund");
    body = t("Your starter buffer is in place. The fully-funded target is {a} — three months of essentials.", { a: fmt(next.target) });
    cta = t("Go to your buffer");
    go = () => goGrow("save");
  } else if (next.id === "goals") {
    title = t("Fund your goals and deposit");
    body = t("Your buffer is solid and there's no high-interest debt in the way — \"Where your spare money goes\" is safe to follow in order.");
    cta = t("Where your money goes");
    go = () => goGrow("save");
  } else {
    title = t("Put spare money into investing");
    body = t("Buffer, debt, and goals are all covered. From here, every extra dollar compounds.");
    cta = t("See the projection");
    go = () => goGrow("retire");
  }

  return (
    <Card style={{ borderColor: `${T.gold}55`, background: T.goldSoft }}>
      <Label style={{ color: T.gold, marginBottom: 8 }}>{t("Do this next")}</Label>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.6, color: T.dim }}>{body}</div>
      {cta && <Btn small style={{ marginTop: 12 }} onClick={go}>{cta} →</Btn>}
      {then.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${T.gold}33`, fontSize: 11.5, color: T.faint, lineHeight: 1.8 }}>
          {t("Then:")} {then.map(stepLabel).join("  ·  ")}
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 10.5, color: T.faint }}>
        {t("Suggested order based on your own numbers — not financial advice.")}
      </div>
    </Card>
  );
}

/* Step sizes for the what-if levers. A monthly lever moves in small increments
   because $50 is the size of decision people actually make; a lump sum is a
   windfall, so it steps by a different order of magnitude. */
const WHATIF_STEP = { extra: 50, income: 50, spend: 50, lump: 500 };

/* What-if scenarios (Premium · Grow). The levers are DELTAS against the
   position the user already declared elsewhere in the app, and nothing here
   writes to state until "Make this the plan" — so the whole card can be poked
   at freely without damaging the real plan. That separation is the point: a
   forecast you are afraid to touch is a forecast nobody opens twice.

   Only `extra` and `strategy` can be committed. Income and spending are facts
   about the user's life rather than choices Aurio gets to make, so modelling
   them is useful but "applying" one would be nonsense.

   The verdict line is the part competitors don't ship. Any model will say that
   $2,000/month at debt clears it sooner; this one re-walks the calendar and
   says whether the money is actually there. */
function WhatIfCard({ state, up, t, lang, notify, monthlyIncome, totalBudget, premium, requirePremium }) {
  const [sc, setSc] = useState(EMPTY_SCENARIO);
  const r = useMemo(
    () => whatIf(state, sc, monthlyIncome, totalBudget),
    [state.debts, state.extraPayment, state.strategy, state.recurring, sc, monthlyIncome, totalBudget]
  );

  const edit = (patch) => { if (!requirePremium()) return; setSc(s => ({ ...s, ...patch })); };
  const canCommit = r.touched && (sc.extra !== 0 || (sc.strategy && sc.strategy !== state.strategy));
  const commit = () => {
    if (!requirePremium()) return;
    const patch = {};
    if (sc.extra !== 0) patch.extraPayment = Math.max(0, (state.extraPayment || 0) + sc.extra);
    if (sc.strategy && sc.strategy !== state.strategy) patch.strategy = sc.strategy;
    up(patch);
    setSc(EMPTY_SCENARIO);
    notify(t("Plan updated."));
  };

  const lever = (key, label, hint) => {
    const step = WHATIF_STEP[key];
    const val = sc[key];
    const nudge = (delta) => edit({ [key]: Math.round((val + delta) * 100) / 100 });
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, ...ELLIPSIS }}>{label}</div>
          <div style={{ fontSize: 11, color: T.faint, ...ELLIPSIS }}>{hint}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
          <Btn small variant="ghost" onClick={() => nudge(-step)} style={{ padding: "6px 10px" }}>−</Btn>
          <NumField value={val} onCommit={(n) => edit({ [key]: n })} step={step} aria-label={label}
            style={{ background: T.midnight, border: `1px solid ${val !== 0 ? T.gold + "66" : T.line}`, borderRadius: 8, color: val !== 0 ? T.gold : T.white, fontFamily: "'Geist Mono', monospace", fontSize: 13, padding: "7px 8px", width: 78, textAlign: "right" }} />
          <Btn small variant="ghost" onClick={() => nudge(step)} style={{ padding: "6px 10px" }}>+</Btn>
        </div>
      </div>
    );
  };

  const months = r.delta.months;
  const interest = r.delta.totalInterest;
  const better = months > 0 || (months === 0 && interest > 0);

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <Label>{t("What if…")}{!premium && " 🔒"}</Label>
        {r.touched && <Btn small variant="ghost" onClick={() => setSc(EMPTY_SCENARIO)}>{t("Reset")}</Btn>}
      </div>
      <div style={{ fontSize: 12, color: T.dim, margin: "8px 0 4px", lineHeight: 1.5 }}>
        {t("Try a change and see what it does to your debt-free date. Nothing is saved until you apply it.")}
      </div>

      {lever("extra", t("Extra at debt"), t("on top of {a}/mo", { a: fmt(state.extraPayment || 0) }))}
      {lever("lump", t("One-off lump sum"), t("a refund or bonus, paid today"))}
      {lever("income", t("Income change"), t("a raise, or lost hours"))}
      {lever("spend", t("Spending change"), t("use a minus to cut back"))}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0" }}>
        <div style={{ fontSize: 13, ...ELLIPSIS }}>{t("Strategy")}</div>
        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
          {STRATEGIES.map(k => {
            const on = (sc.strategy || state.strategy) === k;
            return (
              <button key={k} onClick={() => edit({ strategy: k === state.strategy ? null : k })}
                style={{ background: on ? T.gold : "transparent", color: on ? T.midnight : T.dim, border: `1px solid ${on ? T.gold : T.line}`, borderRadius: 8, padding: "6px 11px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                {t(k === "avalanche" ? "Avalanche" : "Snowball")}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 12, borderTop: `1px solid ${T.line}`, paddingTop: 12 }}>
        {!r.touched ? (
          <div style={{ fontSize: 12, color: T.faint, lineHeight: 1.5 }}>{t("Move a lever to see what changes.")}</div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, fontSize: 13, padding: "3px 0" }}>
              <span style={{ color: T.dim, ...ELLIPSIS }}>{t("Debt-free")}</span>
              <Mono style={{ flexShrink: 0 }}>
                <span style={{ color: T.faint }}>{r.base.neverPaysOff ? t("Never") : monthName(r.base.freeDate, lang)}</span>
                <span style={{ color: T.faint, margin: "0 6px" }}>→</span>
                <span style={{ color: r.scenario.neverPaysOff ? T.garnet : better ? T.greenHi : T.white, fontWeight: 600 }}>
                  {r.scenario.neverPaysOff ? t("Never") : monthName(r.scenario.freeDate, lang)}
                </span>
              </Mono>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, fontSize: 13, padding: "3px 0" }}>
              <span style={{ color: T.dim, ...ELLIPSIS }}>{t("Interest")}</span>
              <Mono style={{ flexShrink: 0 }}>
                <span style={{ color: T.faint }}>{fmt(r.base.totalInterest)}</span>
                <span style={{ color: T.faint, margin: "0 6px" }}>→</span>
                <span style={{ color: interest > 0 ? T.greenHi : interest < 0 ? T.garnet : T.white, fontWeight: 600 }}>{fmt(r.scenario.totalInterest)}</span>
              </Mono>
            </div>

            {(months !== 0 || Math.abs(interest) >= 1) && (
              <div style={{ fontSize: 12.5, color: better ? T.greenHi : T.garnet, marginTop: 8, lineHeight: 1.5 }}>
                {months > 0 ? t(months === 1 ? "{n} month sooner" : "{n} months sooner", { n: months })
                  : months < 0 ? t(-months === 1 ? "{n} month later" : "{n} months later", { n: -months })
                  : t("Same date")}
                {Math.abs(interest) >= 1 && " · " + (interest > 0
                  ? t("{a} less interest", { a: fmt(interest) })
                  : t("{a} more interest", { a: fmt(-interest) }))}
              </div>
            )}

            {/* The honesty line. A scenario that clears the debt in half the
                time is worthless if the user cannot eat that month. */}
            <div style={{ marginTop: 10, background: r.affordable ? T.greenSoft : T.garnetSoft, color: r.affordable ? T.greenHi : T.garnet, borderRadius: 10, padding: "9px 11px", fontSize: 12.5, lineHeight: 1.5 }}>
              {r.affordable
                ? t("✓ Your cash flow covers this every month for the next year.")
                : r.addedTightMonths > 0
                  ? t("⚠️ Short in {m} of the next {n} months (up from {b}), by as much as {a}.",
                      { m: r.scenario.tightMonths, n: r.scenario.map.length, b: r.base.tightMonths, a: fmt(r.scenario.worstShortfall) })
                  : t("⚠️ Short in {m} of the next {n} months, by as much as {a} — the same as your plan today.",
                      { m: r.scenario.tightMonths, n: r.scenario.map.length, a: fmt(r.scenario.worstShortfall) })}
            </div>

            {canCommit && (
              <Btn onClick={commit} style={{ width: "100%", marginTop: 10 }}>
                {t("Make this the plan")}{!premium && " 🔒"}
              </Btn>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function Invest({ state, up, t, lang, notify, premium, requirePremium, sim, debtBudget, totalDebt, monthlyIncome, totalBudget, setTab, map, growTab, setGrowTab }) {
  const inv = state.invest;
  const set = (patch) => up({ invest: { ...inv, ...patch } });
  const country = HOME_COUNTRY[state.currency] || null;
  const step = useMemo(() => nextStep(state, (state.emergencyFund || DEFAULT_STATE.emergencyFund).saved, inv.expReturn),
    [state.budgets, state.recurring, state.debts, state.savingsGoals, state.home, state.emergencyFund, inv.expReturn]);

  /* ONE walk of the money for the whole tab. Computing this here rather than
     inside each section is the fix for the tab's central defect: SaveSection
     and FirstHomeSection each used to run moneyMap() themselves and then
     project their own dates as if they had all of it. */
  /* `map` now arrives from the app so Home and Grow share one walk (see there).
     Allocation clamps at zero per month: a month you come up short frees no
     money to allocate, and letting a negative drag the average down would
     quietly under-fund every goal. The signed figure still drives the
     shortfall warnings — that's what it's for. */
  const avgSaveable = map.reduce((s, m) => s + Math.max(0, m.saveable), 0) / map.length;
  const dests = useMemo(() => buildDestinations(state, country),
    [state.savingsGoals, state.home, state.allocationOrder, country]);
  const alloc = useMemo(() => {
    const rows = allocate(dests, avgSaveable);
    return Object.fromEntries(rows.map(r => [r.id, r]));
  }, [dests, avgSaveable]);

  const yearsToRetire = Math.max(0, inv.retireAge - inv.age);
  const months = yearsToRetire * 12;
  const nestEgg = inv.withdrawRate > 0 ? (inv.targetMonthly * 12) / (inv.withdrawRate / 100) : 0;
  const inflation = inv.inflation ?? DEFAULT_STATE.invest.inflation;

  // Current plan: contribute inv.monthly the whole way.
  const base = useMemo(() => project(inv.current, inv.monthly, 0, null, months, inv.expReturn, inflation),
    [inv.current, inv.monthly, months, inv.expReturn, inflation]);
  // Debt-free boost: after payoff, freed-up debt payments become contributions —
  // but only the share the user says they'll realistically redirect, not all of
  // it (the rest reasonably goes to other bills, lifestyle creep, or a buffer).
  const redirectPct = inv.redirectPct ?? 60;
  const realisticBoost = debtBudget * (redirectPct / 100);
  const boostFrom = totalDebt > 0 ? Math.min(sim.months, months) : 0;
  const boosted = useMemo(() => project(inv.current, inv.monthly, realisticBoost, boostFrom, months, inv.expReturn, inflation),
    [inv.current, inv.monthly, realisticBoost, boostFrom, months, inv.expReturn, inflation]);

  const projectedIncome = (boosted.final * (inv.withdrawRate / 100)) / 12;
  const realIncome = (boosted.realFinal * (inv.withdrawRate / 100)) / 12;
  const hit = boosted.curve.findIndex(v => v >= nestEgg);
  const hitAge = hit > -1 ? inv.age + Math.ceil(hit / 12) : null;
  /* Country-gated like HOME_SCHEMES: the user's own market first, the rest
     behind a disclosure. Showing all four to everyone was the odd one out. */
  const allGuides = GUIDES[lang] || GUIDES.en;
  const homeGuideName = country === "NZ" ? "New Zealand" : country === "AU" ? "Australia" : null;
  const guideIdx = homeGuideName ? (GUIDES.en || []).findIndex(g => g.name === homeGuideName) : -1;
  const primaryGuide = guideIdx > -1 ? allGuides[guideIdx] : null;
  const otherGuides = primaryGuide ? allGuides.filter(g => g !== primaryGuide) : allGuides;
  const onTrack = projectedIncome >= inv.targetMonthly;

  const numIn = (val, onChange, w = 84) => (
    <NumField value={val} onCommit={onChange}
      style={{ background: T.midnight, border: `1px solid ${T.line}`, borderRadius: 8, color: T.white, fontFamily: "'Geist Mono', monospace", fontSize: 14, padding: "8px 9px", width: w, textAlign: "right" }} />
  );

  /* Only the open panel mounts. Everything expensive and shared — map, dests,
     alloc, avgSaveable — is computed above, once, whichever panel is showing;
     moving that work into the panels would re-walk the money map on every
     sub-tab switch. */
  const panel = (id, children) => growTab !== id ? null : (
    <div role="tabpanel" id={`growpanel-${id}`} aria-labelledby={`growtab-${id}`}>{children}</div>
  );

  /* A newly-opened panel starts at the top. Without this you inherit the
     previous panel's scroll and land halfway down the one you just chose. */
  useEffect(() => { window.scrollTo(0, 0); }, [growTab]);

  return (
    <>
      <SubTabs items={GROW_TABS.map(([id, icon, label]) => [id, icon, t(label)])}
        value={growTab} onChange={setGrowTab} label={t("Grow sections")} />

      {panel("next",
        <>
          <DoThisNextCard t={t} lang={lang} step={step} expReturn={inv.expReturn} setTab={setTab} goGrow={setGrowTab} />

          <WhatIfCard state={state} up={up} t={t} lang={lang} notify={notify}
            monthlyIncome={monthlyIncome} totalBudget={totalBudget}
            premium={premium} requirePremium={requirePremium} />
        </>
      )}

      {panel("save",
        <>
          <SaveSection state={state} up={up} t={t} lang={lang} notify={notify} sim={sim}
            monthlyIncome={monthlyIncome} totalBudget={totalBudget} premium={premium} requirePremium={requirePremium}
            dests={dests} alloc={alloc} avgSaveable={avgSaveable} map={map} />

          <AllocationCard state={state} up={up} t={t} lang={lang} dests={dests} alloc={alloc} avgSaveable={avgSaveable} />

          <EmergencyFundCard state={state} up={up} t={t} lang={lang} alloc={alloc} />
        </>
      )}

      {panel("deposit",
        <FirstHomeSection state={state} up={up} t={t} lang={lang} sim={sim} totalDebt={totalDebt}
          avgSaveable={avgSaveable} alloc={alloc} dests={dests} />
      )}

      {panel("retire",
        <>
          <div style={{ textAlign: "center", padding: "20px 8px 6px" }}>
            <Label style={{ color: T.gold }}>{t("Projected passive income at {age}", { age: inv.retireAge })}</Label>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 50, lineHeight: 1.05, margin: "10px 0 4px" }}>
              {fmt(projectedIncome)}
            </div>
            <div style={{ fontSize: 13, color: T.dim, marginBottom: 8 }}>{t("per month")}</div>
            {/* The honest number. A nominal figure 24 years out compared against a
                nominal target flatters itself badly — this says what it buys. */}
            {inflation > 0 && (
              <div style={{ fontSize: 12.5, color: T.faint, marginBottom: 10 }}>
                {t("≈ {a}/mo in today's money, at {r}% inflation", { a: fmt(realIncome), r: inflation })}
              </div>
            )}
            <div style={{ display: "inline-block", background: onTrack ? T.greenSoft : T.garnetSoft, color: onTrack ? T.greenHi : T.garnet, borderRadius: 99, padding: "5px 14px", fontSize: 13, fontWeight: 600 }}>
              {t("Goal: {a}/mo", { a: fmt(inv.targetMonthly) })} · {onTrack ? t("surplus") : t("short — see boosters below")}
            </div>
          </div>

          <Card style={{ marginTop: 14 }}>
            <Label style={{ marginBottom: 12 }}>{t("Your retirement inputs")}</Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12, color: T.dim }}>
              <div>{t("Age now")}<div style={{ marginTop: 5 }}>{numIn(inv.age, v => set({ age: v }), 70)}</div></div>
              <div>{t("Retire at")}<div style={{ marginTop: 5 }}>{numIn(inv.retireAge, v => set({ retireAge: v }), 70)}</div></div>
              <div>{t("Target passive income / month")}<div style={{ marginTop: 5 }}>{numIn(inv.targetMonthly, v => set({ targetMonthly: v }))}</div></div>
              <div>{t("Already invested")}<div style={{ marginTop: 5 }}>{numIn(inv.current, v => set({ current: v }))}</div></div>
              <div>{t("Investing / month now")}<div style={{ marginTop: 5 }}>{numIn(inv.monthly, v => set({ monthly: v }))}</div></div>
            </div>
            <div style={{ marginTop: 14, fontSize: 12, color: T.dim }}>
              {t("You need a pot of about")} <Mono style={{ color: T.gold, fontWeight: 600, fontSize: 14 }}>{fmt(nestEgg)}</Mono> {t("to draw {a}/mo at {r}%", { a: fmt(inv.targetMonthly), r: inv.withdrawRate })}
            </div>
          </Card>

          <Card style={{ marginTop: 12 }}>
            <Label style={{ marginBottom: 10 }}>{t("Assumptions (editable — the future is not predictable)")}</Label>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.dim, marginBottom: 6 }}>
              <span>{t("Expected return {r}% /yr", { r: inv.expReturn })}</span>
            </div>
            <input type="range" min="2" max="12" step="0.5" value={inv.expReturn} onChange={e => set({ expReturn: Number(e.target.value) })} />
            <div style={{ fontSize: 11, color: T.faint, margin: "6px 0 14px", lineHeight: 1.5 }}>
              {t("Long-run diversified share markets have averaged ~7–10% nominal before fees and tax; past performance does not predict the future.")}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.dim, marginBottom: 6 }}>
              <span>{t("Withdrawal / dividend rate {r}%", { r: inv.withdrawRate })}</span>
            </div>
            <input type="range" min="2.5" max="6" step="0.25" value={inv.withdrawRate} onChange={e => set({ withdrawRate: Number(e.target.value) })} />
            <div style={{ fontSize: 11, color: T.faint, margin: "6px 0 14px", lineHeight: 1.5 }}>
              {t("The classic '4% rule' sizes the pot you need; dividend portfolios often yield 3–6%.")}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.dim, marginBottom: 6 }}>
              <span>{t("Inflation {r}% /yr", { r: inflation })}</span>
            </div>
            <input type="range" min="0" max="8" step="0.25" value={inflation} onChange={e => set({ inflation: Number(e.target.value) })} />
            <div style={{ fontSize: 11, color: T.faint, marginTop: 6, lineHeight: 1.5 }}>
              {t("Used to show what your future income is worth in today's money. Set it to 0 to see the raw numbers instead.")}
            </div>
          </Card>

          {/* Stays visible after payoff. Unmounting it at totalDebt === 0 removed
              the explanation of the user's new investing power at exactly the
              moment they earned it — the app's whole point. */}
          {(totalDebt > 0 || debtBudget > 0) && (
            <Card style={{ marginTop: 12, background: T.goldSoft, borderColor: `${T.gold}44` }}>
              <Label style={{ color: T.gold, marginBottom: 8 }}>{t("The debt-free boost")}</Label>
              {totalDebt <= 0 ? (
                <>
                  <div style={{ fontSize: 13, lineHeight: 1.6, color: T.greenHi, fontWeight: 600 }}>
                    {t("🎉 You're debt-free — {a}/mo no longer goes to debt.", { a: fmt(debtBudget) })}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.dim, margin: "14px 0 6px" }}>
                    <span>{t("How much will you realistically redirect to investing?")}</span>
                    <Mono style={{ color: T.gold, fontWeight: 600 }}>{redirectPct}%</Mono>
                  </div>
                  <input type="range" min="0" max="100" step="5" value={redirectPct} onChange={e => set({ redirectPct: Number(e.target.value) })} />
                  <div style={{ fontSize: 13, lineHeight: 1.6, borderTop: `1px solid ${T.gold}33`, paddingTop: 10, marginTop: 10 }}>
                    {t("Redirecting {pct}% is {b}/mo into investing.", { pct: redirectPct, b: fmt(realisticBoost) })}
                  </div>
                </>
              ) : sim.neverPaysOff ? (
                <div style={{ fontSize: 13, lineHeight: 1.6, color: T.garnet }}>
                  {t("⚠️ Your current payments never clear this debt, so there's no freed-up amount to project yet — fix the plan in Debts first.")}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    {t("From {d}, {a}/mo frees up from debt payments. Realistically, some of that goes to other bills or lifestyle — not all of it becomes investing power.", {
                      d: monthName(sim.freeDate, lang), a: fmt(debtBudget),
                    })}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.dim, margin: "14px 0 6px" }}>
                    <span>{t("How much will you realistically redirect to investing?")}</span>
                    <Mono style={{ color: T.gold, fontWeight: 600 }}>{redirectPct}%</Mono>
                  </div>
                  <input type="range" min="0" max="100" step="5" value={redirectPct} onChange={e => set({ redirectPct: Number(e.target.value) })} />
                  <div style={{ fontSize: 11, color: T.faint, margin: "6px 0 12px", lineHeight: 1.5 }}>
                    {t("Defaults to 60% — a buffer for the bills that don't disappear just because the debt did.")}
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.6, borderTop: `1px solid ${T.gold}33`, paddingTop: 10 }}>
                    {t("Redirecting {pct}% ({b}/mo) changes your projection from {p1} to {p2} at age {age}.", {
                      pct: redirectPct, b: fmt(realisticBoost),
                      p1: fmt(base.final), p2: fmt(boosted.final), age: inv.retireAge,
                    })}
                  </div>
                </>
              )}
            </Card>
          )}

          <Card style={{ marginTop: 12 }}>
            {/* Identity rides a swatch, never the label text: a series colour used
                as text colour is illegible at 11px and puts meaning in the ink. */}
            <div style={{ display: "flex", gap: 14, fontSize: 11, marginBottom: 8, flexWrap: "wrap", color: T.dim }}>
              <Swatch color={T.gold}>{t("With debt-free boost")} · <Mono style={{ color: T.white }}>{fmt(boosted.final)}</Mono></Swatch>
              <Swatch color={T.greenHi}>{t("Current plan")} · <Mono style={{ color: T.white }}>{fmt(base.final)}</Mono></Swatch>
            </div>
            <Curve points={boosted.curve} points2={base.curve} h={80}
              label={t("Projected balance from age {a} to {b}: {x} with the debt-free boost, {y} on the current plan.", {
                a: inv.age, b: inv.retireAge, x: fmt(boosted.final), y: fmt(base.final),
              })} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.faint, marginTop: 4 }}>
              <span>{inv.age}</span><span>{inv.retireAge}</span>
            </div>
            <div style={{ fontSize: 12, marginTop: 10, color: hitAge ? T.greenHi : T.garnet }}>
              {hitAge ? t("Goal reached at age {age} 🎉", { age: hitAge }) : t("Goal not reached by {age} — raise contributions, retire later, or adjust the target.", { age: inv.retireAge })}
            </div>
          </Card>

          <Label style={{ margin: "18px 2px 8px" }}>{t("Where people invest for income")}</Label>
          {/* Country-gated to match HOME_SCHEMES directly above — the user's own
              market first, the rest behind a disclosure. Showing all four to
              everyone made the same tab behave two different ways. */}
          {primaryGuide && <GuideCard key={primaryGuide.name} g={primaryGuide} />}
          {primaryGuide ? (
            <OtherMarkets t={t} guides={otherGuides} />
          ) : (
            otherGuides.map(g => <GuideCard key={g.name} g={g} />)
          )}

          <div style={{ fontSize: 11, color: T.faint, margin: "14px 4px 4px", lineHeight: 1.6 }}>
            {t("Educational only — not financial advice, and not a recommendation of any product. Returns are not guaranteed and past performance does not predict future results. Consider licensed advice for personal decisions.")}
          </div>
        </>
      )}
    </>
  );
}

/* The markets that aren't the user's own — kept, but out of the way. */
function OtherMarkets({ t, guides }) {
  const [open, setOpen] = useState(false);
  if (!guides.length) return null;
  return (
    <>
      <button onClick={() => setOpen(!open)} style={{ background: "none", border: "none", color: T.dim, fontSize: 11.5, cursor: "pointer", padding: "4px 2px", textDecoration: "underline" }}>
        {open ? t("Hide other markets") : t("Other markets ({n})", { n: guides.length })}
      </button>
      {open && guides.map(g => <GuideCard key={g.name} g={g} />)}
    </>
  );
}

function GuideCard({ g }) {
  const [openG, setOpenG] = useState(false);
  return (
    <Card style={{ marginBottom: 8, padding: "12px 14px" }}>
      <div onClick={() => setOpenG(!openG)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{g.flag} {g.name}</div>
        <span style={{ color: T.faint, fontSize: 10 }}>{openG ? "▼" : "▶"}</span>
      </div>
      {openG && <div style={{ fontSize: 12.5, color: T.dim, lineHeight: 1.6, marginTop: 10 }}>{g.body}</div>}
    </Card>
  );
}

const Row = ({ k, v, gold, color, bold }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
    <span style={ELLIPSIS}>{k}</span>
    <Mono style={{ color: color || (gold ? "#C9A227" : "#F2ECDC"), fontWeight: bold ? 700 : 500, flexShrink: 0 }}>{v}</Mono>
  </div>
);

function Sheet({ title, children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.panelHi, borderRadius: "20px 20px 0 0", padding: "20px 20px 34px", width: "100%", maxWidth: 560, border: `1px solid ${T.line}`, borderBottom: "none", maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20 }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.faint, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
